import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, LayoutDashboard, Eye, AlertTriangle, ArrowLeft, RefreshCw, Loader2,
  CheckCircle2, XCircle, X, Flag, PlayCircle, ChevronDown, ChevronUp,
  TrendingUp, DollarSign, ChevronRight, ShieldCheck, Filter,
  FileText, User as UserIcon, Globe, Search, Crown, Ban, VolumeX, Volume2,
  Activity, Users, LogIn, KeyRound, Coins, Gift, MessageCircle, Wallet,
  Wrench, ArrowRight, Info, Hash, Network, Sparkles, Plus, Minus,
  ChevronsRight, Calendar, AtSign, Fingerprint, Droplet, RotateCcw,
  Package, Trophy, Check, ExternalLink, Bot, Server, Link2, Tag, ImageOff,
} from 'lucide-react';
import { WalletValue, useAuth } from '../context/AuthContext';
import axios from 'axios';
import { walletAmount, BB_NAME, BB_CHIP } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* ════════════════════════════════════════════════════════════
   FORMATTERS
   ════════════════════════════════════════════════════════════ */
const fmt = (n) => {
  if (!n && n !== 0) return '0';
  if (Math.abs(n) >= 1_000_000)     return n.toLocaleString();
  if (Math.abs(n) >= 1_000)         return parseFloat((n/1_000).toFixed(1))+'K';
  return n.toLocaleString();
};
const fmtD = (n) => (n || n === 0) ? `$${fmt(n)}` : '$0';
const relTime = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now()-new Date(iso).getTime())/1000);
  if (s<60) return `${s}s ago`; if (s<3600) return `${Math.floor(s/60)}m ago`;
  if (s<86400) return `${Math.floor(s/3600)}h ago`; return `${Math.floor(s/86400)}d ago`;
};
const fullTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};
const formatFlag = (f='') => f.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());
const formatAction = formatFlag;
const fmtDelta = (n) => {
  if (n === null || n === undefined) return '—';
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${fmtD(Math.abs(n))}`;
};
const fmtClock = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString(undefined, { hour12: false }); }
  catch { return ''; }
};

/* ════════════════════════════════════════════════════════════
   RANK / SEVERITY / CATEGORY / SOURCE CONFIG
   ════════════════════════════════════════════════════════════ */
const RANK_LEVELS = { anonymous: 0, user: 1, moderator: 2, admin: 3, owner: 4 };

const RANK_CFG = {
  owner:     { label:'OWNER', color:'text-amber-300',  bg:'bg-amber-500/15 border-amber-500/30',  dot:'bg-amber-400',  icon:Crown },
  admin:     { label:'ADMIN', color:'text-red-300',    bg:'bg-red-500/15 border-red-500/30',      dot:'bg-red-400',    icon:Shield },
  moderator: { label:'MOD',   color:'text-blue-300',   bg:'bg-blue-500/15 border-blue-500/30',    dot:'bg-blue-400',   icon:ShieldCheck },
  user:      { label:'USER',  color:'text-zinc-400',   bg:'bg-white/5 border-white/10',           dot:'bg-zinc-500',   icon:UserIcon },
  anonymous: { label:'ANON',  color:'text-zinc-600',   bg:'bg-white/5 border-white/10',           dot:'bg-zinc-700',   icon:UserIcon },
};

const SEVERITY_CFG = {
  info:     { color:'text-blue-400',   bg:'bg-blue-500/10 border-blue-500/20',    dot:'bg-blue-400',   label:'INFO' },
  warning:  { color:'text-yellow-400', bg:'bg-yellow-500/10 border-yellow-500/30', dot:'bg-yellow-400', label:'WARN' },
  critical: { color:'text-red-400',    bg:'bg-red-500/15 border-red-500/30',       dot:'bg-red-400',    label:'CRIT' },
};

const CATEGORY_CFG = {
  auth:         { icon:LogIn,         color:'text-blue-400',    label:'Auth' },
  security:     { icon:ShieldCheck,   color:'text-red-400',     label:'Security' },
  admin_action: { icon:Shield,        color:'text-red-300',     label:'Admin' },
  permission:   { icon:KeyRound,      color:'text-purple-400',  label:'Permission' },
  balance:      { icon:Coins,         color:'text-emerald-400', label:'Balance' },
  transaction:  { icon:Wallet,        color:'text-emerald-400', label:'Transaction' },
  game:         { icon:Activity,      color:'text-zinc-400',    label:'Game' },
  chat:         { icon:MessageCircle, color:'text-blue-400',    label:'Chat' },
  system:       { icon:Wrench,        color:'text-zinc-400',    label:'System' },
  affiliate:    { icon:Users,         color:'text-purple-400',  label:'Affiliate' },
  tip:          { icon:Gift,          color:'text-pink-400',    label:'Tip' },
};

const SOURCE_CFG = {
  audit_log:    { icon:Shield,        color:'text-red-400',     label:'Audit',   bg:'bg-red-500/10' },
  balance_log:  { icon:Coins,         color:'text-emerald-400', label:'Balance', bg:'bg-emerald-500/10' },
  game_history: { icon:Activity,      color:'text-zinc-400',    label:'Game',    bg:'bg-white/5' },
  transaction:  { icon:Wallet,        color:'text-blue-400',    label:'TX',      bg:'bg-blue-500/10' },
  chat_message: { icon:MessageCircle, color:'text-purple-400',  label:'Chat',    bg:'bg-purple-500/10' },
};

/* ════════════════════════════════════════════════════════════
   FILTER TAXONOMIES — all games, balance semantic groups, tx config
   ════════════════════════════════════════════════════════════ */
// Hard-coded full game list — always selectable even if user hasn't played.
// `matchers` is a list of lowercase substrings; a stored game string matches the
// pill if it contains ANY matcher. This handles DB inconsistencies like
// "case_battle" vs "case_battles", "chicken-road" vs "chicken_road", etc.
const GAME_TYPES = [
  { key:'roulette',     label:'Roulette',     icon:Activity,   matchers:['roulette'] },
  { key:'crash',        label:'Crash',        icon:TrendingUp, matchers:['crash'] },
  { key:'tower',        label:'Tower',        icon:Trophy,     matchers:['tower'] },
  { key:'plinko',       label:'Plinko',       icon:Activity,   matchers:['plinko'] },
  { key:'dice',         label:'Dice',         icon:Activity,   matchers:['dice'] },
  { key:'mines',        label:'Mines',        icon:Activity,   matchers:['mines','mine'] },
  { key:'blackjack',    label:'Blackjack',    icon:Activity,   matchers:['blackjack','black_jack','bj'] },
  { key:'jackpot',      label:'Jackpot',      icon:Coins,      matchers:['jackpot'] },
  { key:'coinflip',     label:'Coinflip',     icon:Activity,   matchers:['coinflip','coin_flip','coin-flip'] },
  { key:'chicken_road', label:'Chicken Road', icon:Activity,   matchers:['chicken_road','chicken-road','chickenroad','chicken'] },
  { key:'case_opening', label:'Case Opening', icon:Package,    matchers:['case_opening','case_open','caseopening','caseopen','open_case'] },
  { key:'case_battle',  label:'Case Battles', icon:Package,    matchers:['case_battle','case_battles','casebattle','casebattles','battle'] },
  { key:'daily_race',   label:'Daily Race',   icon:Trophy,     matchers:['daily_race','dailyrace','daily-race','race'] },
];

// Match a stored game name against a GAME_TYPES key using its matchers.
const matchGameType = (storedGame, gameKey) => {
  if (!storedGame || !gameKey) return false;
  const cfg = GAME_TYPES.find(g => g.key === gameKey);
  if (!cfg) return false;
  const s = String(storedGame).toLowerCase();
  return cfg.matchers.some(m => s.includes(m));
};

// Balance log action → high-level semantic groups (substring match, lowercase)
const BALANCE_CATEGORIES = [
  { key:'deposit',   label:'Deposits',    icon:Plus,      color:'emerald',
    matchers:['deposit','p2p_credit','legacy_credit'] },
  { key:'withdraw',  label:'Withdrawals', icon:Minus,     color:'blue',
    matchers:['withdraw','payout'] },
  { key:'game_bet',  label:'Game Bets',   icon:Activity,  color:'red',
    matchers:['game_bet','bet_placed','bet_debit'] },
  { key:'game_win',  label:'Game Wins',   icon:TrendingUp,color:'emerald',
    matchers:['game_win','win_payout','win_credit'] },
  { key:'tip',       label:'Tips',        icon:Gift,      color:'pink',
    matchers:['tip_sent','tip_received','tip_'] },
  { key:'rain',      label:'Rain',        icon:Droplet,   color:'cyan',
    matchers:['rain_','rain_tip','rain_join','rain_receive'] },
  { key:'promo',     label:'Promo Codes', icon:Sparkles,  color:'purple',
    matchers:['promo'] },
  { key:'rakeback',  label:'Rakeback',    icon:RotateCcw, color:'amber',
    matchers:['rakeback'] },
  { key:'affiliate', label:'Affiliate',   icon:Users,     color:'purple',
    matchers:['affiliate','referral','vault'] },
  { key:'admin',     label:'Admin Adj.',  icon:Crown,     color:'amber',
    matchers:['balance_adjust','manual_adjust','admin_credit','admin_debit'] },
  { key:'refund',    label:'Refunds',     icon:RotateCcw, color:'zinc',
    matchers:['refund'] },
  { key:'case',      label:'Cases',       icon:Package,   color:'orange',
    matchers:['case_open','case_battle','case_reward','case_item'] },
];

const TX_TYPES = [
  { key:'deposit',  label:'Deposit',  color:'emerald' },
  { key:'withdraw', label:'Withdraw', color:'blue'    },
];

const TX_STATUSES = [
  { key:'pending',    label:'Pending',    color:'amber'   },
  { key:'processing', label:'Processing', color:'blue'    },
  { key:'completed',  label:'Completed',  color:'emerald' },
  { key:'failed',     label:'Failed',     color:'red'     },
  { key:'cancelled',  label:'Cancelled',  color:'zinc'    },
  { key:'expired',    label:'Expired',    color:'zinc'    },
  { key:'refunded',   label:'Refunded',   color:'purple'  },
];

// Match a balance action string against a category key
const matchBalanceCategory = (action, catKey) => {
  if (!action) return false;
  const cat = BALANCE_CATEGORIES.find(c => c.key === catKey);
  if (!cat) return false;
  const a = action.toLowerCase();
  return cat.matchers.some(m => a.includes(m));
};

// Tailwind class map for filter pill colors (static so JIT picks them up)
const PILL_COLORS = {
  red:     { active:'bg-red-500/15 text-red-300 border-red-500/30',         dot:'bg-red-400'     },
  blue:    { active:'bg-blue-500/15 text-blue-300 border-blue-500/30',      dot:'bg-blue-400'    },
  emerald: { active:'bg-emerald-500/15 text-emerald-300 border-emerald-500/30', dot:'bg-emerald-400' },
  amber:   { active:'bg-amber-500/15 text-amber-300 border-amber-500/30',   dot:'bg-amber-400'   },
  purple:  { active:'bg-purple-500/15 text-purple-300 border-purple-500/30',dot:'bg-purple-400'  },
  cyan:    { active:'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',      dot:'bg-cyan-400'    },
  pink:    { active:'bg-pink-500/15 text-pink-300 border-pink-500/30',      dot:'bg-pink-400'    },
  orange:  { active:'bg-orange-500/15 text-orange-300 border-orange-500/30',dot:'bg-orange-400'  },
  zinc:    { active:'bg-white/10 text-zinc-200 border-white/20',            dot:'bg-zinc-400'    },
  yellow:  { active:'bg-yellow-500/15 text-yellow-300 border-yellow-500/30',dot:'bg-yellow-400'  },
};

/* ════════════════════════════════════════════════════════════
   FIELD NORMALIZATION — DB has inconsistent field names across
   collections. These helpers read with multiple fallbacks so
   filters & UI work regardless of which name the DB used.
   ════════════════════════════════════════════════════════════ */

// Game name: game_history may use `game_type` (current schema) or `game` (older)
const getGameName = (d) => d?.game_type || d?.game || d?.type || null;

// Game win outcome: may be `result: "win"|"loss"`, `win: bool`, `outcome: "win"`, or computed
const getGameWin = (d) => {
  if (d == null) return null;
  if (typeof d.win === 'boolean') return d.win;
  if (d.result)  return String(d.result).toLowerCase() === 'win';
  if (d.outcome) return String(d.outcome).toLowerCase() === 'win';
  // Fallback: derive from win_amount > bet_amount or net > 0
  if (typeof d.net === 'number') return d.net > 0;
  if (typeof d.win_amount === 'number' && typeof d.bet_amount === 'number') {
    return d.win_amount > d.bet_amount;
  }
  return null;
};

// Net P&L for a game row (won - wagered)
const getGameNet = (d) => {
  if (d == null) return 0;
  if (typeof d.net === 'number') return d.net;
  const won = d.win_amount || 0;
  const bet = d.bet_amount || 0;
  return won - bet;
};

// Bet amount
const getBetAmount = (d) => d?.bet_amount ?? d?.bet ?? d?.amount ?? 0;

// Balance log: action / category — may be `reason`, `action`, `type`, `kind`
const getBalanceAction = (d) =>
  d?.reason || d?.action || d?.type || d?.kind || null;

// Balance log: amount (always positive — direction tells sign)
const getBalanceAmount = (d) => Math.abs(d?.amount ?? d?.delta ?? 0);

// Balance log: signed delta — uses `direction: 'credit'|'debit'` OR explicit sign in delta
const getBalanceSignedDelta = (d) => {
  if (d == null) return 0;
  if (d.direction === 'credit') return  Math.abs(d.amount ?? d.delta ?? 0);
  if (d.direction === 'debit')  return -Math.abs(d.amount ?? d.delta ?? 0);
  // No direction field — assume `delta` is already signed, fall back to amount
  if (typeof d.delta === 'number') return d.delta;
  return d.amount ?? 0;
};

// Transaction: type/status (already standardized, but be safe)
const getTxType   = (d) => d?.type   || d?.tx_type   || null;
const getTxStatus = (d) => d?.status || d?.tx_status || null;
const getTxAmount = (d) => d?.amount ?? 0;

// Chat: message text
const getChatMessage = (d) => d?.message ?? d?.text ?? d?.content ?? '';

// Audit log: stored fields
const getAuditAction   = (d) => d?.action   || null;
const getAuditCategory = (d) => d?.category || null;
const getAuditSeverity = (d) => d?.severity || null;

// Timestamp resolution (each collection uses a different field)
const getItemTimestamp = (item) => {
  if (!item) return null;
  if (item.timestamp) return item.timestamp;
  const d = item.data || {};
  return d.timestamp || d.created_at || d.updated_at || null;
};

// IP / device / fingerprint hints inside data (for click-thru and display)
const getItemIp = (d) => d?.ip || null;

const getUserRank = (u) => {
  if (!u) return 0;
  if (u.is_owner) return 4;
  if (u.is_admin) return 3;
  if (u.is_moderator) return 2;
  return 1;
};
const getRankLabel = (u) => {
  const r = getUserRank(u);
  if (r === 4) return 'owner';
  if (r === 3) return 'admin';
  if (r === 2) return 'moderator';
  if (r === 1) return 'user';
  return 'anonymous';
};

/* ════════════════════════════════════════════════════════════
   NAV — rank-aware
   ════════════════════════════════════════════════════════════ */
const NAV = [
  { id:'dashboard',         icon:LayoutDashboard, label:'Dashboard',         minRank:2 },
  { id:'audit-logs',        icon:FileText,        label:'Audit Logs',        minRank:2 },
  { id:'user-lookup',       icon:UserIcon,        label:'User Lookup',       minRank:2 },
  { id:'identity-investigation', icon:Globe,           label:'Identity Lookup',   minRank:2 },
  { id:'manual-review',     icon:Eye,             label:'Manual Review',     minRank:3 },
  { id:'suspicious-events', icon:AlertTriangle,   label:'Suspicious Events', minRank:3 },
  { id:'mm2-bots',          icon:Bot,             label:'MM2 Bots',          minRank:3 },
  { id:'mm2-identity',      icon:Tag,             label:'MM2 Item Identity', minRank:3 },
  { id:'mm2-prices',        icon:DollarSign,     label:'MM2 Price Issues',  minRank:3 },
];

/* ════════════════════════════════════════════════════════════
   REUSABLE BITS
   ════════════════════════════════════════════════════════════ */
const RankBadge = ({ rank='user', size='sm' }) => {
  const cfg = RANK_CFG[rank] || RANK_CFG.user;
  const Icon = cfg.icon;
  const sz = size === 'xs' ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-0.5 gap-1.5';
  const iconSz = size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  return (
    <span className={`inline-flex items-center font-bold border rounded-full ${cfg.bg} ${cfg.color} ${sz}`}>
      <Icon className={iconSz}/>{cfg.label}
    </span>
  );
};

const SeverityBadge = ({ severity='info' }) => {
  const cfg = SEVERITY_CFG[severity] || SEVERITY_CFG.info;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 border rounded-full ${cfg.bg} ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}/>
      {cfg.label}
    </span>
  );
};

const CategoryChip = ({ category }) => {
  const cfg = CATEGORY_CFG[category] || { icon:Hash, color:'text-zinc-500', label:category||'?' };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${cfg.color}`}>
      <Icon className="w-3 h-3"/>{cfg.label}
    </span>
  );
};

const UserChip = ({ snapshot, label }) => {
  if (!snapshot) return (
    <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
      {label && <span className="text-zinc-700">{label}:</span>}
      <span className="italic">none</span>
    </span>
  );
  const rank = snapshot.rank || 'user';
  const cfg = RANK_CFG[rank] || RANK_CFG.user;
  const display = snapshot.username || snapshot.roblox_username || snapshot.email || snapshot.user_id?.slice(0,8) || 'unknown';
  return (
    <span className="inline-flex items-center gap-1.5 max-w-full">
      {label && <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{label}</span>}
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-xs ${cfg.bg}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}/>
        <span className={`font-bold truncate max-w-[140px] ${cfg.color}`}>{display}</span>
      </span>
    </span>
  );
};

const EmptyState = ({ icon:Icon=Info, title='Nothing here', sub='' }) => (
  <div className="text-center py-16">
    <Icon className="w-10 h-10 text-zinc-700 mx-auto mb-3"/>
    <p className="text-zinc-400 font-bold">{title}</p>
    {sub && <p className="text-zinc-600 text-sm mt-1">{sub}</p>}
  </div>
);

const LoadingBlock = () => (
  <div className="flex justify-center py-20">
    <Loader2 className="w-8 h-8 animate-spin text-zinc-600"/>
  </div>
);

/* ════════════════════════════════════════════════════════════
   CONFIRM DIALOG  (existing — P2P approve/reject with note)
   ════════════════════════════════════════════════════════════ */
const ConfirmDialog = ({ type, amount, onConfirm, onClose }) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  // 3 aksiyon: approve (depositor+withdrawer kredi) / reject (withdrawer serbest, depositor yok)
  //            fund_both (depositor approve gibi kredi + withdrawer reject gibi serbest/iade)
  const CFG = {
    approve: {
      title: 'Approve Match',
      body: <>This will credit <span className="text-emerald-400 font-bold">{fmtD(amount)}</span> to the withdrawer's progress. Cannot be undone.</>,
      placeholder: 'e.g. Manual balance adjustment, reason confirmed',
      focus: 'focus:border-emerald-500',
      btn: 'bg-emerald-600 hover:bg-emerald-500',
      cta: 'Approve',
    },
    reject: {
      title: 'Reject Match',
      body: <>This will release <span className="text-red-400 font-bold">{fmtD(amount)}</span> back to available capacity. Cannot be undone.</>,
      placeholder: 'e.g. No matching record found',
      focus: 'focus:border-red-500',
      btn: 'bg-red-600 hover:bg-red-500',
      cta: 'Reject',
    },
    fund_both: {
      title: 'Fund Both Sides',
      body: <>Credits <span className="text-emerald-400 font-bold">{fmtD(amount)}</span> to the <span className="text-emerald-400 font-bold">depositor</span> (like approve), and releases the <span className="text-blue-400 font-bold">withdrawer's</span> {fmtD(amount)} back to them (like reject &mdash; their funds are <span className="text-blue-400 font-bold">not</span> deducted). Use when the withdrawer cancelled during review but the depositor paid legitimately. Cannot be undone.</>,
      placeholder: 'e.g. Withdrawer cancelled during review; depositor paid legitimately',
      focus: 'focus:border-blue-500',
      btn: 'bg-blue-600 hover:bg-blue-500',
      cta: 'Fund Both',
    },
  };
  const cfg = CFG[type] || CFG.reject;
  const submit = async () => {
    if (note.trim().length < 10) return;
    setLoading(true);
    try { await onConfirm(note.trim()); } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{scale:0.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.95,opacity:0}}
        onClick={e=>e.stopPropagation()}
        className="w-full max-w-md bg-[#121417] rounded-2xl border border-[#2A2D31] shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-lg">{cfg.title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
        </div>
        <p className="text-zinc-400 text-sm mb-4 leading-relaxed">{cfg.body}</p>
        <div className="mb-4 space-y-2">
          <label className="text-sm text-zinc-400">Note (required, min 10 chars)</label>
          <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
            placeholder={cfg.placeholder}
            className={`w-full p-3 bg-[#0B0C0E] border rounded-xl text-white text-sm outline-none resize-none ${
              note.length>0&&note.length<10 ? 'border-red-500' : `border-[#2A2D31] ${cfg.focus}`
            } transition-colors`} />
          {note.length>0&&note.length<10 && <p className="text-xs text-red-400">{10-note.length} more characters required</p>}
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
          <button onClick={submit} disabled={loading||note.trim().length<10}
            className={`flex-1 py-3 rounded-xl text-white font-bold transition-colors disabled:opacity-40 ${cfg.btn}`}>
            {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : cfg.cta}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   P2P EVENT ROW + AUDIT MODAL (existing)
   ════════════════════════════════════════════════════════════ */
const EVENT_CFG = {
  created:                     { icon:PlayCircle,    color:'text-blue-400',    bg:'border-blue-500/20 bg-blue-500/[0.07]',       label:'Match Created'           },
  match_selected_and_reserved: { icon:ShieldCheck,   color:'text-blue-300',    bg:'border-blue-500/20 bg-blue-500/[0.07]',       label:'Withdraw Reserved'       },
  match_selected_by_bot:       { icon:ShieldCheck,   color:'text-cyan-300',    bg:'border-cyan-500/20 bg-cyan-500/[0.07]',       label:'Bot Selected Withdrawer' },
  selected:                    { icon:PlayCircle,    color:'text-blue-400',    bg:'border-blue-500/20 bg-blue-500/[0.07]',       label:'Selected'                },
  precheck_passed:             { icon:CheckCircle2,  color:'text-emerald-300', bg:'border-emerald-500/20 bg-emerald-500/[0.07]', label:'Pre-Check OK'            },
  precheck_failed:             { icon:XCircle,       color:'text-red-400',     bg:'border-red-500/20 bg-red-500/[0.07]',         label:'Pre-Check Failed'        },
  select_failed:               { icon:XCircle,       color:'text-red-400',     bg:'border-red-500/20 bg-red-500/[0.07]',         label:'Selection Failed'        },
  no_match_found:              { icon:AlertTriangle, color:'text-yellow-400',  bg:'border-yellow-500/20 bg-yellow-500/[0.07]',   label:'No Visible Withdrawer'   },
  tick:                        { icon:RefreshCw,     color:'text-zinc-600',    bg:'border-[#2A2D31] bg-[#0B0C0E]',               label:'Tick'                    },
  manual_flag:                 { icon:Flag,          color:'text-orange-400',  bg:'border-orange-500/30 bg-orange-500/[0.10]',   label:'Anomaly → Manual Review' },
  completed:                   { icon:CheckCircle2,  color:'text-emerald-400', bg:'border-emerald-500/20 bg-emerald-500/[0.07]', label:'Match Completed'         },
  rolled_back:                 { icon:XCircle,       color:'text-red-400',     bg:'border-red-500/20 bg-red-500/[0.07]',         label:'Rolled Back'             },
  rollback:                    { icon:XCircle,       color:'text-red-400',     bg:'border-red-500/20 bg-red-500/[0.07]',         label:'Rolled Back'             },
  cancelled_by_user:           { icon:XCircle,       color:'text-zinc-400',    bg:'border-[#2A2D31] bg-[#0B0C0E]',               label:'Cancelled by User'       },
  expired:                     { icon:XCircle,       color:'text-zinc-400',    bg:'border-[#2A2D31] bg-[#0B0C0E]',               label:'Expired'                 },
  manual_credit:               { icon:CheckCircle2,  color:'text-emerald-400', bg:'border-emerald-500/20 bg-emerald-500/[0.07]', label:'Admin Approved'          },
  manual_reject:               { icon:XCircle,       color:'text-red-400',     bg:'border-red-500/20 bg-red-500/[0.07]',         label:'Admin Rejected'          },
  manual_fund_both:            { icon:Coins,         color:'text-blue-400',    bg:'border-blue-500/20 bg-blue-500/[0.07]',       label:'Admin Funded Both'       },
};

/* Modalın tepesindeki BAŞLANGIÇ BAKİYELERİ paneli */
const BaselinePanel = ({ baseline, depIgn, wIgn, expected }) => {
  if (!baseline) {
    return (
      <div className="bg-[#0B0C0E] border border-[#2A2D31] rounded-xl px-4 py-3 text-center">
        <span className="mm-num text-xs text-zinc-600">No baseline recorded for this match.</span>
      </div>
    );
  }
  return (
    <div className="bg-[#121417] border border-[#2A2D31] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">Starting Balances</span>
        <span className="mm-num text-xs text-zinc-600">{fmtClock(baseline.observed_at)}</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-xs font-bold uppercase text-zinc-600 mb-1">Depositor</div>
          <div className="mm-num text-xs font-bold text-zinc-300 truncate">{depIgn||'—'}</div>
          <div className="mm-num text-base font-bold text-emerald-400 mt-1">{fmtD(baseline.depositor_bal)}</div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase text-zinc-600 mb-1">Withdrawer</div>
          <div className="mm-num text-xs font-bold text-zinc-300 truncate">{wIgn||'—'}</div>
          <div className="mm-num text-base font-bold text-blue-400 mt-1">{fmtD(baseline.withdrawer_bal)}</div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase text-zinc-600 mb-1">Expected</div>
          <div className="mm-num text-xs font-bold text-zinc-500">D ➜ W</div>
          <div className="mm-num text-base font-bold text-amber-400 mt-1">{fmtD(expected)}</div>
        </div>
      </div>
    </div>
  );
};

/* Manual review'a NEDEN düştüğünü öne çıkaran panel */
const AnomalyPanel = ({ flagEvent, baseline, expected }) => {
  if (!flagEvent) return null;
  const obs    = flagEvent.data?.observed || {};
  const flags  = flagEvent.data?.flags || [];
  const reason = flagEvent.data?.reason || '';
  const baselineW = obs.baseline_w ?? baseline?.withdrawer_bal;
  const baselineD = obs.baseline_d ?? baseline?.depositor_bal;
  const currentW  = obs.current_w;
  const currentD  = obs.current_d;
  const wDelta    = obs.w_delta;
  const dDelta    = obs.d_delta;
  return (
    <div className="bg-orange-500/[0.06] border border-orange-500/30 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Flag className="w-3.5 h-3.5 text-orange-400" strokeWidth={2.5}/>
        <span className="text-xs font-bold uppercase tracking-wider text-orange-300">Why this was flagged</span>
        <span className="ml-auto mm-num text-xs text-zinc-600">{fmtClock(flagEvent.at)}</span>
      </div>
      {flags.length>0&&(
        <div className="flex flex-wrap gap-1.5 mb-2">
          {flags.map((f,i)=>(
            <span key={i} className="px-2 py-0.5 rounded-md border border-orange-500/30 bg-orange-500/10 mm-num text-xs font-bold text-orange-200">{formatFlag(f)}</span>
          ))}
        </div>
      )}
      {reason&&<div className="mm-num text-xs text-zinc-400 mb-3">{String(reason).replace(/_/g,' ')}</div>}
      {(currentW!==undefined||currentD!==undefined)&&(
        <div className="grid grid-cols-2 gap-3 border-t border-orange-500/15 pt-3">
          <div>
            <div className="text-xs font-bold uppercase text-zinc-500 mb-1">Withdrawer</div>
            <div className="mm-num text-xs text-zinc-300">{fmtD(baselineW)} <span className="text-zinc-600">→</span> {fmtD(currentW)}</div>
            <div className="mm-num text-xs font-bold mt-0.5">
              <span className={(wDelta??0)>=0?'text-emerald-400':'text-red-400'}>{fmtDelta(wDelta)}</span>
              <span className="ml-2 text-zinc-600">expected {fmtD(expected)}</span>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase text-zinc-500 mb-1">Depositor</div>
            <div className="mm-num text-xs text-zinc-300">{fmtD(baselineD)} <span className="text-zinc-600">→</span> {fmtD(currentD)}</div>
            <div className="mm-num text-xs font-bold mt-0.5">
              <span className={(dDelta??0)>=0?'text-emerald-400':'text-red-400'}>{fmtDelta(-(dDelta??0))}</span>
              <span className="ml-2 text-zinc-600">expected −{fmtD(expected)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Tek event satırı — tipe göre özet, expand'da raw JSON */
const EventRow = ({ event }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg  = EVENT_CFG[event.type] || { icon:RefreshCw, color:'text-zinc-400', bg:'border-[#2A2D31] bg-[#0B0C0E]', label:event.type };
  const Icon = cfg.icon;
  const data = event.data || {};

  const renderInline = () => {
    switch (event.type) {
      case 'tick': {
        const wDelta = data.w_delta ?? 0;
        const dDelta = data.d_delta ?? 0;
        return (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 mm-num text-xs text-zinc-500">
            <span>W: <span className="text-blue-300">{fmtD(data.withdrawer_bal)}</span> <span className={wDelta>=0?'text-emerald-400':'text-red-400'}>({fmtDelta(wDelta)})</span></span>
            <span>D: <span className="text-emerald-300">{fmtD(data.depositor_bal)}</span> <span className={dDelta<=0?'text-emerald-400':'text-red-400'}>({fmtDelta(-dDelta)})</span></span>
            {data.status&&data.status!=='ok'&&<span className="text-yellow-400">[{data.status}]</span>}
          </div>
        );
      }
      case 'precheck_passed':
      case 'match_selected_by_bot': {
        const w = data.withdrawer_bal ?? data.baseline_w;
        const d = data.depositor_bal  ?? data.baseline_d;
        return (
          <div className="flex flex-wrap gap-x-3 mt-0.5 mm-num text-xs text-zinc-500">
            {w!==undefined&&<span>W bal: <span className="text-blue-300">{fmtD(w)}</span></span>}
            {d!==undefined&&<span>D bal: <span className="text-emerald-300">{fmtD(d)}</span></span>}
            {data.selected_ign&&<span>→ <span className="text-zinc-300">{data.selected_ign}</span></span>}
          </div>
        );
      }
      case 'match_selected_and_reserved':
        return data.withdrawer_ign
          ? <div className="mt-0.5 mm-num text-xs text-zinc-500">withdrawer: <span className="text-zinc-300">{data.withdrawer_ign}</span></div>
          : null;
      case 'precheck_failed':
      case 'select_failed':
      case 'no_match_found':
      case 'rollback':
      case 'rolled_back':
      case 'expired':
        return data.reason
          ? <div className="mt-0.5 mm-num text-xs text-red-300/70">{String(data.reason).replace(/_/g,' ')}</div>
          : null;
      case 'manual_flag': {
        const flags = data.flags || [];
        return (
          <div className="flex flex-wrap gap-x-2 mt-0.5 mm-num text-xs">
            {flags.map((f,i)=>(<span key={i} className="text-orange-300">{formatFlag(f)}</span>))}
            {data.reason&&<span className="text-zinc-500">— {String(data.reason).replace(/_/g,' ')}</span>}
          </div>
        );
      }
      case 'completed': {
        const wD = data.w_delta;
        const dD = data.d_delta;
        return (
          <div className="flex flex-wrap gap-x-3 mt-0.5 mm-num text-xs text-zinc-500">
            {data.withdrawer_bal_after!==undefined&&(
              <span>W: {fmtD(data.withdrawer_bal_before)} → <span className="text-blue-300">{fmtD(data.withdrawer_bal_after)}</span> <span className="text-emerald-400">({fmtDelta(wD)})</span></span>
            )}
            {data.depositor_bal_after!==undefined&&(
              <span>D: {fmtD(data.depositor_bal_before)} → <span className="text-emerald-300">{fmtD(data.depositor_bal_after)}</span> <span className="text-emerald-400">({fmtDelta(-(dD||0))})</span></span>
            )}
          </div>
        );
      }
      case 'manual_credit':
      case 'manual_reject':
      case 'manual_fund_both':
        return data.note
          ? <div className="mt-0.5 mm-num text-xs italic text-zinc-400">"{data.note}"</div>
          : null;
      default:
        return null;
    }
  };

  const hasDetails = data && Object.keys(data).length > 0;

  return (
    <div className={`rounded-xl border p-3 overflow-hidden ${cfg.bg}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-black/20">
          <Icon className={`w-3.5 h-3.5 ${cfg.color}`} strokeWidth={2}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2">
            <span className={`text-xs font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
            <span className="mm-num text-xs text-zinc-600">{fmtClock(event.at)}</span>
            <span className="mm-num text-xs text-zinc-700">· {relTime(event.at)}</span>
          </div>
          {renderInline()}
        </div>
        {hasDetails&&(
          <button onClick={()=>setExpanded(v=>!v)} className="text-zinc-700 hover:text-zinc-400">
            {expanded?<ChevronUp className="w-4 h-4"/>:<ChevronDown className="w-4 h-4"/>}
          </button>
        )}
      </div>
      <AnimatePresence>
        {expanded&&hasDetails&&(
          <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
            <pre className="mt-2 p-2 bg-black/30 rounded-lg mm-num text-xs text-zinc-500 overflow-x-auto">{JSON.stringify(data,null,2)}</pre>
            <div className="mt-1 mm-num text-xs text-zinc-700">{event.at}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


/* ════════════════════════════════════════════════════════════
   SUSPICIOUS EVENT TYPES (existing)
   ════════════════════════════════════════════════════════════ */
const STYPE = {
  rapid_balance_gain:             { label:'Rapid Balance Gain', icon:TrendingUp,   color:'text-orange-400' },
  balance_cap_exceeded:           { label:'Absurd Balance',     icon:DollarSign,   color:'text-red-400'    },
  reconciliation_mismatch_excess: { label:'Balance Mismatch',   icon:AlertTriangle,color:'text-yellow-400' },
};

/* ════════════════════════════════════════════════════════════
   AUDIT EVENT CARD (new — for AuditLogsView & IdentityInvestigationView)
   ════════════════════════════════════════════════════════════ */
const AuditEventCard = ({ event, delay=0, onUserClick, onIdentityClick }) => {
  const [expanded, setExpanded] = useState(false);
  const catCfg = CATEGORY_CFG[event.category] || { icon:Hash, color:'text-zinc-500', label:event.category };
  const CatIcon = catCfg.icon;
  const hasDetails = event.details && Object.keys(event.details).length > 0;
  const hasMeta = event.user_agent || event.device_id || event.fingerprint;

  return (
    <motion.div
      initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} transition={{delay}}
      className="bg-[#121417] border border-[#2A2D31] rounded-xl overflow-hidden hover:border-white/15 transition-colors">
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center ${catCfg.color}`}>
            <CatIcon className="w-4 h-4"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1.5">
              <span className="font-bold text-white text-sm truncate">{formatAction(event.action)}</span>
              <SeverityBadge severity={event.severity}/>
              <CategoryChip category={event.category}/>
              <span className="text-xs text-zinc-600 ml-auto whitespace-nowrap" title={fullTime(event.timestamp)}>{relTime(event.timestamp)}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {event.actor ? (
                <button onClick={()=>onUserClick?.(event.actor.user_id)} className="hover:scale-105 transition-transform">
                  <UserChip snapshot={event.actor} label="actor"/>
                </button>
              ) : <UserChip snapshot={null} label="actor"/>}
              {event.target && <>
                <ArrowRight className="w-3 h-3 text-zinc-700"/>
                <button onClick={()=>onUserClick?.(event.target.user_id)} className="hover:scale-105 transition-transform">
                  <UserChip snapshot={event.target} label="target"/>
                </button>
              </>}
              {event.ip && (
                <button onClick={()=>onIdentityClick?.('ip', event.ip)} className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/5 text-zinc-500 hover:text-blue-400 hover:bg-blue-500/10 mm-num text-[11px] transition-colors">
                  <Globe className="w-3 h-3"/>{event.ip}
                </button>
              )}
            </div>
          </div>
          {(hasDetails||hasMeta) && (
            <button onClick={()=>setExpanded(v=>!v)} className="text-zinc-700 hover:text-zinc-300 shrink-0">
              {expanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
            </button>
          )}
        </div>
        <AnimatePresence>
          {expanded && (hasDetails||hasMeta) && (
            <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
              <div className="mt-3 pt-3 border-t border-[#2A2D31] space-y-2">
                {hasMeta && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {event.user_agent && <div className="bg-[#0B0C0E] rounded-lg p-2"><div className="text-zinc-600">User-Agent</div><div className="text-zinc-400 truncate mm-num text-[10px]" title={event.user_agent}>{event.user_agent}</div></div>}
                    {event.device_id && (
                      <button onClick={()=>onIdentityClick?.('device', event.device_id)} className="bg-[#0B0C0E] hover:bg-blue-500/10 rounded-lg p-2 text-left transition-colors group">
                        <div className="text-zinc-600 group-hover:text-blue-400">Device ID</div>
                        <div className="text-zinc-400 group-hover:text-blue-400 truncate mm-num text-[10px]">{event.device_id}</div>
                      </button>
                    )}
                    {event.fingerprint && (
                      <button onClick={()=>onIdentityClick?.('fingerprint', event.fingerprint)} className="bg-[#0B0C0E] hover:bg-purple-500/10 rounded-lg p-2 text-left transition-colors group">
                        <div className="text-zinc-600 group-hover:text-purple-400">Fingerprint</div>
                        <div className="text-zinc-400 group-hover:text-purple-400 truncate mm-num text-[10px]">{event.fingerprint}</div>
                      </button>
                    )}
                    {event.id && <div className="bg-[#0B0C0E] rounded-lg p-2"><div className="text-zinc-600">Event ID</div><div className="text-zinc-400 truncate mm-num text-[10px]">{event.id}</div></div>}
                  </div>
                )}
                {hasDetails && (
                  <div className="bg-black/40 rounded-lg p-2 max-h-64 overflow-auto">
                    <pre className="text-[11px] text-zinc-400 mm-num whitespace-pre-wrap break-all">{JSON.stringify(event.details,null,2)}</pre>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════
   UNIFIED ACTIVITY ROW (new — for UserLookupView)
   ════════════════════════════════════════════════════════════ */
const UnifiedActivityRow = ({ item, delay=0, onIdentityClick }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg = SOURCE_CFG[item.source] || { icon:Info, color:'text-zinc-500', label:item.source, bg:'bg-white/5' };
  const Icon = cfg.icon;
  const data = item.data || {};

  let title = '';
  let subtitle = '';
  let amount = null;

  switch (item.source) {
    case 'audit_log':
      title = formatAction(getAuditAction(data));
      subtitle = getAuditCategory(data) || '';
      break;
    case 'balance_log': {
      const action = getBalanceAction(data);
      title = formatAction(action || 'Balance change');
      amount = getBalanceSignedDelta(data);
      // Prefer descriptive subtitle: roblox_username in details, or before→after, else action label
      const bBefore = data.balance_before;
      const bAfter  = data.balance_after;
      if (data.details?.roblox_username) {
        subtitle = `Roblox: ${data.details.roblox_username}`;
      } else if (bBefore != null && bAfter != null) {
        subtitle = `${fmtD(bBefore)} → ${fmtD(bAfter)}`;
      } else {
        subtitle = data.direction ? `direction: ${data.direction}` : '';
      }
      break;
    }
    case 'game_history': {
      const gameName = getGameName(data);
      const cfgMatch = GAME_TYPES.find(t => matchGameType(gameName, t.key));
      title = cfgMatch?.label || formatAction(gameName || 'Game');
      const bet = getBetAmount(data);
      const won = data.win_amount || 0;
      amount = getGameNet(data) || (won - bet);
      const winState = getGameWin(data);
      const outcomeLabel = winState === true ? 'WIN' : winState === false ? 'LOSS' : '—';
      // Extra detail: multiplier if present
      const mult = data.details?.multiplier || data.multiplier;
      subtitle = `bet ${fmtD(bet)} · ${outcomeLabel}${mult ? ` · ${mult}x` : ''}`;
      break;
    }
    case 'transaction':
      title = formatAction(getTxType(data) || 'Transaction');
      amount = getTxAmount(data);
      subtitle = getTxStatus(data) || '';
      break;
    case 'chat_message':
      title = 'Chat message';
      subtitle = getChatMessage(data).slice(0,80);
      break;
    default:
      title = item.source;
  }

  return (
    <motion.div initial={{opacity:0,x:-6}} animate={{opacity:1,x:0}} transition={{delay}}
      className="bg-[#121417] border border-[#2A2D31] rounded-xl overflow-hidden hover:border-white/15 transition-colors">
      <div className="flex items-center gap-3 p-3">
        <div className={`w-8 h-8 shrink-0 rounded-lg ${cfg.bg} border border-white/5 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${cfg.color}`}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
            <span className="font-bold text-white text-sm truncate">{title}</span>
            {item.source === 'audit_log' && data.severity && <SeverityBadge severity={data.severity}/>}
          </div>
          {subtitle && <div className="text-xs text-zinc-500 truncate mt-0.5">{subtitle}</div>}
        </div>
        {amount !== null && amount !== undefined && (
          <div className={`shrink-0 font-bold text-sm ${amount > 0 ? 'text-emerald-400' : amount < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
            {amount > 0 ? '+' : ''}{fmtD(amount)}
          </div>
        )}
        <div className="shrink-0 text-xs text-zinc-600 whitespace-nowrap ml-2" title={fullTime(item.timestamp)}>{relTime(item.timestamp)}</div>
        <button onClick={()=>setExpanded(v=>!v)} className="text-zinc-700 hover:text-zinc-300">
          {expanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
        </button>
      </div>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
            <div className="px-3 pb-3 pt-1 border-t border-[#2A2D31]/50">
              {data.ip && onIdentityClick && (
                <button onClick={()=>onIdentityClick('ip', data.ip)} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 text-zinc-400 hover:text-blue-400 hover:bg-blue-500/10 mm-num text-[10px] mb-2">
                  <Globe className="w-3 h-3"/>{data.ip}
                </button>
              )}
              <div className="bg-black/40 rounded-lg p-2 max-h-56 overflow-auto">
                <pre className="text-[11px] text-zinc-400 mm-num whitespace-pre-wrap break-all">{JSON.stringify(data,null,2)}</pre>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════
   ACTION MODALS (new) — Ban / Unban / Mute / Unmute / SetRank / SetFlag / BalanceAdjust
   ════════════════════════════════════════════════════════════ */
const ModalShell = ({ title, icon:Icon, accent='red', children, onClose }) => {
  const accentMap = {
    red:     'text-red-400',
    amber:   'text-amber-400',
    blue:    'text-blue-400',
    emerald: 'text-emerald-400',
    purple:  'text-purple-400',
  };
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{scale:0.95,opacity:0,y:8}} animate={{scale:1,opacity:1,y:0}} exit={{scale:0.95,opacity:0}}
        onClick={e=>e.stopPropagation()}
        className="w-full max-w-md bg-[#121417] rounded-2xl border border-[#2A2D31] shadow-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {Icon && <Icon className={`w-5 h-5 ${accentMap[accent]}`}/>}
            <h3 className="text-white font-bold text-lg">{title}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
};

const BanUserModal = ({ targetUser, onClose, onDone }) => {
  const { token } = useAuth();
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (reason.trim().length < 5) return toast.error('Reason must be at least 5 characters.');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/ban`,
        { identifier: targetUser.id, reason: reason.trim() },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(`${targetUser.site_username||targetUser.roblox_username||'User'} banned.`);
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Ban failed.');
    } finally { setLoading(false); }
  };
  return (
    <ModalShell title="Ban User" icon={Ban} accent="red" onClose={onClose}>
      <div className="mb-4 p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl">
        <div className="text-xs text-zinc-500">Target</div>
        <div className="font-bold text-white">{targetUser.site_username||targetUser.roblox_username||targetUser.id}</div>
        <div className="text-xs text-zinc-500 mt-0.5">Cascade IP/device/fingerprint ban will apply.</div>
      </div>
      <div className="mb-4 space-y-2">
        <label className="text-sm text-zinc-400">Reason (min 5 chars)</label>
        <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}
          placeholder="e.g. Multi-accounting evidence in chat logs"
          className="w-full p-3 bg-[#0B0C0E] border border-[#2A2D31] focus:border-red-500 rounded-xl text-white text-sm outline-none resize-none transition-colors"/>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading||reason.trim().length<5}
          className="flex-1 py-3 bg-red-600 hover:bg-red-500 disabled:opacity-40 rounded-xl text-white font-bold transition-colors">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Ban'}
        </button>
      </div>
    </ModalShell>
  );
};

const UnbanUserModal = ({ targetUser, onClose, onDone }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/unban`,
        { identifier: targetUser.id },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(`${targetUser.site_username||'User'} unbanned.`);
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Unban failed.');
    } finally { setLoading(false); }
  };
  return (
    <ModalShell title="Unban User" icon={CheckCircle2} accent="emerald" onClose={onClose}>
      <p className="text-zinc-400 text-sm mb-4">Remove ban from <span className="font-bold text-white">{targetUser.site_username||targetUser.id}</span> and clear cascaded IP/device/fingerprint blocks.</p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading}
          className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 rounded-xl text-white font-bold">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Unban'}
        </button>
      </div>
    </ModalShell>
  );
};

const LiftWithdrawBanModal = ({ targetUser, onClose, onDone }) => {
  /* Fiyat ihlali nedeniyle konan çekim yasağını / dondurmayı kaldırır.
     ⚠️ İhlal GEÇMİŞİNİ silmez, yalnızca yasağı kaldırır: geçmişi silmek,
     kullanıcı tekrar ihlal ettiğinde kademeli cezayı en baştan başlatırdı.
     Bu ayrımı admine de açıkça söylüyoruz ki "temiz sayfa" sandığı şeyin
     ne olduğunu bilsin. */
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');

  const blocked = !!targetUser.withdraw_blocked;
  const frozen = !!targetUser.is_frozen;

  const submit = async () => {
    setLoading(true);
    try {
      /* ⚠️ `identifier` — diğer admin uçlarıyla AYNI alan adı. Sunucu
         `resolve_user_identifier` ile ad/e-posta/id hepsini çözüyor. */
      const res = await axios.post(`${API}/admin/users/lift-withdraw-ban`,
        { identifier: targetUser.id, note: note.trim() || undefined },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(res.data?.message || 'Withdraw ban lifted.');
      onDone?.(); onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not lift the ban.');
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Lift Withdraw Ban" icon={ShieldCheck} accent="emerald" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-sm text-zinc-300">
          Restore withdrawals for <span className="font-bold text-white">
          {targetUser.site_username || targetUser.id}</span>?
        </p>
        <div className="text-[11px] text-zinc-400 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg p-3 space-y-1">
          <p>Current status:
            {blocked && <span className="text-amber-300 font-bold"> withdrawals blocked</span>}
            {frozen && <span className="text-red-300 font-bold"> · account frozen</span>}
            {!blocked && !frozen && <span> none</span>}
          </p>
          {/* ⚠️ Fiyat DIŞI dondurma varsa admin'i uyar: bu buton onu
              kaldırmaz, sunucu da 409 döndürür. */}
          {frozen && targetUser.frozen_reason && targetUser.frozen_reason !== 'p2p_price_manipulation' && (
            <p className="text-red-300 font-bold">
              ⚠ This freeze is "{targetUser.frozen_reason}", not a price
              violation. This action will not lift it — use the freeze controls.
            </p>
          )}
          <p>Price violations on record:
            <span className="font-bold text-white"> {targetUser.p2p_price_violations || 0}</span>
          </p>
          {/* ⚠️ Yanlış anlamayı önle: bu buton geçmişi silmiyor. */}
          <p className="text-zinc-500 pt-1">
            This lifts the ban only. The violation history is kept, so repeat
            offences still escalate.
          </p>
        </div>
        <textarea
          value={note} onChange={e=>setNote(e.target.value)}
          maxLength={500} rows={2}
          placeholder="Reason (e.g. support ticket #123) — saved to the audit log"
          className="w-full bg-[#0B0C0E] border border-[#2A2D31] focus:border-emerald-500 rounded-lg px-3 py-2 text-sm text-white outline-none resize-none"/>
        <button onClick={submit} disabled={loading}
          className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="animate-spin w-5 h-5"/> : 'Lift withdraw ban'}
        </button>
      </div>
    </ModalShell>
  );
};

const MuteUserModal = ({ targetUser, onClose, onDone }) => {
  const { token } = useAuth();
  const [duration, setDuration] = useState(60);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const PRESETS = [
    { v:5,    l:'5m' },{ v:15,   l:'15m' },{ v:60,   l:'1h' },
    { v:360,  l:'6h' },{ v:1440, l:'1d' },{ v:10080,l:'7d' },
  ];
  const submit = async () => {
    if (duration < 1 || duration > 10080) return toast.error('Duration must be 1-10080 minutes.');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/mute`,
        { identifier: targetUser.id, duration_minutes: parseInt(duration,10), reason: reason.trim() || undefined },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(`Muted for ${duration}m.`);
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Mute failed.');
    } finally { setLoading(false); }
  };
  return (
    <ModalShell title="Mute User" icon={VolumeX} accent="amber" onClose={onClose}>
      <div className="mb-4 p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl">
        <div className="text-xs text-zinc-500">Target</div>
        <div className="font-bold text-white">{targetUser.site_username||targetUser.roblox_username||targetUser.id}</div>
        <div className="text-xs text-zinc-500 mt-0.5">Chat history will be deleted.</div>
      </div>
      <div className="mb-3 space-y-2">
        <label className="text-sm text-zinc-400">Duration (1–10080 min)</label>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map(p=>(
            <button key={p.v} onClick={()=>setDuration(p.v)}
              className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-colors ${duration===p.v?'bg-amber-500/20 text-amber-300 border border-amber-500/30':'bg-white/5 text-zinc-400 hover:text-white border border-transparent'}`}>{p.l}</button>
          ))}
        </div>
        <input type="number" min="1" max="10080" value={duration} onChange={e=>setDuration(e.target.value)}
          className="w-full p-2.5 bg-[#0B0C0E] border border-[#2A2D31] focus:border-amber-500 rounded-xl text-white text-sm outline-none transition-colors"/>
      </div>
      <div className="mb-4 space-y-2">
        <label className="text-sm text-zinc-400">Reason (optional)</label>
        <input value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. spam"
          className="w-full p-2.5 bg-[#0B0C0E] border border-[#2A2D31] focus:border-amber-500 rounded-xl text-white text-sm outline-none transition-colors"/>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading} className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 rounded-xl text-white font-bold">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Mute'}
        </button>
      </div>
    </ModalShell>
  );
};

const UnmuteUserModal = ({ targetUser, onClose, onDone }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/unmute`,
        { identifier: targetUser.id },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success('Unmuted.');
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Unmute failed.');
    } finally { setLoading(false); }
  };
  return (
    <ModalShell title="Unmute User" icon={Volume2} accent="emerald" onClose={onClose}>
      <p className="text-zinc-400 text-sm mb-4">Remove mute from <span className="font-bold text-white">{targetUser.site_username||targetUser.id}</span>.</p>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading} className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 rounded-xl text-white font-bold">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Unmute'}
        </button>
      </div>
    </ModalShell>
  );
};

const SetRankModal = ({ targetUser, viewer, onClose, onDone }) => {
  const { token } = useAuth();
  const viewerRank = getUserRank(viewer);
  const targetRank = getUserRank(targetUser);
  const [newRank, setNewRank] = useState(getRankLabel(targetUser));
  const [loading, setLoading] = useState(false);

  const RANK_OPTIONS = [
    { v:'user',      l:'User',      r:1 },
    { v:'moderator', l:'Moderator', r:2 },
    { v:'admin',     l:'Admin',     r:3 },
    { v:'owner',     l:'Owner',     r:4 },
  ];
  // Strict hierarchy: actor rank must be > target current AND > selected
  const canPick = (r) => viewerRank > r;

  const submit = async () => {
    if (newRank === getRankLabel(targetUser)) return toast.error('Rank unchanged.');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/set-rank`,
        { identifier: targetUser.id, rank: newRank },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(`Rank set to ${newRank}.`);
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Rank change failed.');
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Set Rank" icon={Crown} accent="amber" onClose={onClose}>
      <div className="mb-4 p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-zinc-500">Target</div>
            <div className="font-bold text-white">{targetUser.site_username||targetUser.id}</div>
          </div>
          <RankBadge rank={getRankLabel(targetUser)}/>
        </div>
      </div>
      <div className="mb-4 space-y-2">
        <label className="text-sm text-zinc-400">New rank</label>
        <div className="grid grid-cols-2 gap-2">
          {RANK_OPTIONS.map(opt => {
            const allowed = canPick(opt.r);
            const cfg = RANK_CFG[opt.v];
            const Icon = cfg.icon;
            return (
              <button key={opt.v} onClick={()=>allowed && setNewRank(opt.v)} disabled={!allowed}
                className={`flex items-center gap-2 p-3 rounded-xl border transition-colors ${
                  newRank===opt.v ? `${cfg.bg} ${cfg.color} border-current`
                  : allowed ? 'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10'
                  : 'bg-white/5 text-zinc-700 border-white/5 opacity-40 cursor-not-allowed'
                }`}>
                <Icon className="w-4 h-4"/>
                <span className="font-bold text-sm">{opt.l}</span>
                {!allowed && <span className="ml-auto text-[9px]">LOCKED</span>}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading||newRank===getRankLabel(targetUser)} className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 rounded-xl text-white font-bold">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Apply'}
        </button>
      </div>
    </ModalShell>
  );
};

const SetFlagModal = ({ targetUser, onClose, onDone }) => {
  const { token } = useAuth();
  const FLAGS = [
    { key:'is_vip',       label:'VIP' },
    { key:'is_youtuber',  label:'YouTuber' },
    { key:'is_streamer',  label:'Streamer' },
    { key:'is_tiktoker',  label:'TikToker' },
  ];
  const [flag, setFlag] = useState('is_vip');
  const [value, setValue] = useState(!targetUser[flag]);
  const [loading, setLoading] = useState(false);

  useEffect(()=>{ setValue(!targetUser[flag]); }, [flag, targetUser]);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/set-flag`,
        { identifier: targetUser.id, flag, value },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(`${FLAGS.find(f=>f.key===flag)?.label} ${value?'enabled':'disabled'}.`);
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Flag set failed.');
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Set Badge Flag" icon={Sparkles} accent="purple" onClose={onClose}>
      <div className="mb-4 p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl">
        <div className="text-xs text-zinc-500">Target</div>
        <div className="font-bold text-white">{targetUser.site_username||targetUser.id}</div>
      </div>
      <div className="mb-4 space-y-2">
        <label className="text-sm text-zinc-400">Flag</label>
        <div className="grid grid-cols-2 gap-2">
          {FLAGS.map(f => (
            <button key={f.key} onClick={()=>setFlag(f.key)}
              className={`p-2.5 rounded-xl border text-sm font-bold transition-colors flex items-center justify-between ${flag===f.key?'bg-purple-500/15 text-purple-300 border-purple-500/30':'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10'}`}>
              <span>{f.label}</span>
              {targetUser[f.key] && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400"/>}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-4 flex items-center justify-between p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl">
        <div>
          <div className="text-sm font-bold text-white">{FLAGS.find(f=>f.key===flag)?.label}</div>
          <div className="text-xs text-zinc-500">Currently: {targetUser[flag] ? 'enabled' : 'disabled'}</div>
        </div>
        <button onClick={()=>setValue(v=>!v)}
          className={`relative w-12 h-6 rounded-full transition-colors ${value?'bg-purple-500':'bg-white/10'}`}>
          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${value?'left-6':'left-0.5'}`}/>
        </button>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading} className="flex-1 py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 rounded-xl text-white font-bold">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Apply'}
        </button>
      </div>
    </ModalShell>
  );
};

const BalanceAdjustModal = ({ targetUser, onClose, onDone }) => {
  const { token } = useAuth();
  const [delta, setDelta] = useState('');
  const [direction, setDirection] = useState('add'); // 'add' or 'subtract'
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const numericDelta = parseFloat(delta) || 0;
  const signedAmount = direction === 'add' ? numericDelta : -numericDelta;
  // 🍀 HANGİ CÜZDAN? Varsayılan 'main' → mevcut davranış birebir korunur.
  // ⚠️ Bu seçim olmadan operatör BB Coin bakiyesine HİÇ dokunamaz ve
  // gerçek para düzeltmesi için MongoDB'yi elle düzenlemek zorunda kalır.
  const [adjWallet, setAdjWallet] = useState('main');
  const isLcAdj = adjWallet === 'lc';
  const newBalance = (isLcAdj ? (targetUser.lc_balance_milli || 0)
                              : (targetUser.site_balance ?? targetUser.balance ?? 0)) + signedAmount;

  const submit = async () => {
    if (numericDelta === 0) return toast.error('Enter a non-zero amount.');
    if (reason.trim().length < 5) return toast.error('Reason required (min 5 chars).');
    // ⚠️ TAVAN CÜZDANA GÖRE. 1.000.000 site birimi makul bir operatör sınırı;
    // AYNI sayı mili-LC olarak yalnızca 1.000 LC'dir. Backend'in kendi tavanı
    // da cüzdana özel (ADMIN_ADJUST_CAP_LC).
    const cap = isLcAdj ? 1_000_000_000 : 1_000_000;
    if (Math.abs(numericDelta) > cap)
      return toast.error(`Max ±${cap.toLocaleString()} per adjustment for this wallet.`);
    setLoading(true);
    try {
      await axios.post(`${API}/admin/users/balance-adjust`,
        { identifier: targetUser.id, amount: signedAmount, reason: reason.trim(), wallet: adjWallet },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(`Balance ${direction==='add'?'credited':'debited'}: ${fmtD(numericDelta)}`);
      onDone?.(); onClose();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Balance adjust failed.');
    } finally { setLoading(false); }
  };

  return (
    <ModalShell title="Adjust Balance" icon={Coins} accent="amber" onClose={onClose}>
      <div className="mb-4 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl">
        <div className="flex items-center gap-2 text-amber-300 text-xs font-bold mb-1">
          <Crown className="w-3.5 h-3.5"/>OWNER ACTION
        </div>
        <p className="text-zinc-400 text-xs">This action is logged as <code className="text-amber-300">CRITICAL</code> and cannot be undone. Both balance_logs and audit_logs receive entries.</p>
      </div>
      <div className="mb-4 p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl">
        <div className="text-xs text-zinc-500">Target</div>
        <div className="font-bold text-white">{targetUser.site_username||targetUser.id}</div>
        {/* İKİ BAKİYE DE GÖRÜNMELİ: operatör hangi cüzdanı düzelttiğini ve
            mevcut durumu aynı ekranda görmeden karar vermemeli. */}
        <div className="text-xs text-zinc-500 mt-1">Robux: <span className="text-emerald-400 font-bold">{fmtD(targetUser.site_balance ?? targetUser.balance)}</span></div>
        <div className="text-xs text-zinc-500 mt-0.5">{BB_NAME}: <span className="text-[#F5B700] font-bold"><WalletValue amount={targetUser.lc_balance_milli || 0} wallet={'lc'} /></span></div>
      </div>

      {/* 🍀 CÜZDAN SEÇİMİ — hangi bakiye düzeltilecek? */}
      <div className="mb-3">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold mb-1.5">Wallet</div>
        <div className="flex gap-2">
          {[['main','Robux'],['lc',BB_NAME]].map(([k,lbl])=>(
            <button key={k} onClick={()=>setAdjWallet(k)}
              className={`flex-1 py-2 rounded-xl border font-bold text-xs transition-colors ${
                adjWallet===k ? (k==='lc' ? 'bg-[#F5B700]/15 text-[#F5B700] border-[#F5B700]/30'
                                          : 'bg-[#B059E8]/15 text-[#c98bf0] border-[#B059E8]/30')
                              : 'bg-white/5 text-zinc-400 border-white/10'}`}>
              {lbl}
            </button>
          ))}
        </div>
        {isLcAdj && (
          <p className="mt-1.5 text-[10px] text-amber-300/70 leading-snug">
            ⚠️ {BB_NAME} is REAL money. Amount is in milli-units (1 {BB_CHIP} = 1,000).
          </p>
        )}
      </div>
      <div className="mb-3 flex gap-2">
        <button onClick={()=>setDirection('add')}
          className={`flex-1 py-2.5 rounded-xl border font-bold text-sm transition-colors flex items-center justify-center gap-1.5 ${direction==='add'?'bg-emerald-500/15 text-emerald-300 border-emerald-500/30':'bg-white/5 text-zinc-400 border-white/10'}`}>
          <Plus className="w-4 h-4"/>Credit
        </button>
        <button onClick={()=>setDirection('subtract')}
          className={`flex-1 py-2.5 rounded-xl border font-bold text-sm transition-colors flex items-center justify-center gap-1.5 ${direction==='subtract'?'bg-red-500/15 text-red-300 border-red-500/30':'bg-white/5 text-zinc-400 border-white/10'}`}>
          <Minus className="w-4 h-4"/>Debit
        </button>
      </div>
      <div className="mb-3 space-y-1.5">
        <label className="text-sm text-zinc-400">Amount (max 1,000,000)</label>
        <input type="number" min="0" step="0.01" value={delta} onChange={e=>setDelta(e.target.value)}
          placeholder="0.00"
          className="w-full p-3 bg-[#0B0C0E] border border-[#2A2D31] focus:border-amber-500 rounded-xl text-white text-lg font-bold outline-none transition-colors"/>
        {numericDelta > 0 && (
          <div className="text-xs text-zinc-500">
            New balance: <span className={`font-bold ${newBalance>=0?'text-emerald-400':'text-red-400'}`}>{fmtD(newBalance)}</span>
          </div>
        )}
      </div>
      <div className="mb-4 space-y-1.5">
        <label className="text-sm text-zinc-400">Reason (min 5 chars)</label>
        <textarea value={reason} onChange={e=>setReason(e.target.value)} rows={2}
          placeholder="e.g. Refund for failed deposit tx ID xyz"
          className="w-full p-2.5 bg-[#0B0C0E] border border-[#2A2D31] focus:border-amber-500 rounded-xl text-white text-sm outline-none resize-none transition-colors"/>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading||numericDelta===0||reason.trim().length<5}
          className="flex-1 py-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 rounded-xl text-white font-bold">
          {loading ? <Loader2 className="animate-spin mx-auto w-5 h-5"/> : 'Apply'}
        </button>
      </div>
    </ModalShell>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN EXPORT — AdminPanel
   ════════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════════
   MM2 BOTS — bagli botlar, envanterleri ve ozel sunucu linkleri
   ════════════════════════════════════════════════════════════
   Botlar BURADAN EKLENMEZ. Her bot kendi rolesi uzerinden kendini tanitir;
   bu ekran onlari izlemek ve ozel sunucu linkini vermek icindir.

   ⚠️ Link burada girilirse rolenin bildirdigini EZER. Iki kaynak sunucuda
      ayri alanlarda tutuluyor; aksi halde role her 20 saniyede bir tanitim
      yaptigi icin buradan girilen link surekli silinirdi.
   ════════════════════════════════════════════════════════════ */
const BotStatus = ({ bot }) => {
  const h = bot.health || {};
  // ⚠️ Durum ELLE AYARLANMAZ — tamamen bağlantıdan türetilir. Sunucu
  //    `ready` alanını hesaplayıp gönderiyor; burada onu tekrar hesaplamıyoruz
  //    ki iki mantık ayrışmasın.
  let t='Online', c='text-emerald-400 bg-emerald-500/10 border-emerald-500/30', d='bg-emerald-400';
  if (h.error)                 { t='Relay down'; c='text-red-400 bg-red-500/10 border-red-500/30'; d='bg-red-400'; }
  else if (!bot.ready && !bot.join_link) { t='No link'; c='text-amber-400 bg-amber-500/10 border-amber-500/30'; d='bg-amber-400'; }
  else if (!bot.ready)         { t='Offline'; c='text-zinc-400 bg-zinc-500/10 border-zinc-500/30'; d='bg-zinc-500'; }
  else if (bot.active_trades)  { t='Busy'; c='text-sky-400 bg-sky-500/10 border-sky-500/30'; d='bg-sky-400'; }
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full border ${c}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${d}`}/>{t}
    </span>
  );
};

const BotCard = ({ bot, canEdit, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [link, setLink] = useState(bot.private_server_link || '');
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ setLink(bot.private_server_link || ''); }, [bot.private_server_link]);

  const loadItems = async () => {
    if (items || loadingItems) return;
    setLoadingItems(true);
    try {
      const r = await axios.get(`${API}/admin/mm2/bots/items?bot_id=${encodeURIComponent(bot.id)}`);
      setItems(r.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load items'); }
    finally { setLoadingItems(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mm2/bots/link`,
        { bot_id: bot.id, private_server_link: link.trim() });
      toast.success(link.trim() ? 'Private server link saved.' : 'Link cleared.');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  const h = bot.health || {};
  return (
    <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#0d1f38] border border-[#2A2D31] flex items-center justify-center shrink-0">
            <Bot className="w-5 h-5 text-sky-400"/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white truncate">{bot.name}</span>
              <BotStatus bot={bot}/>
            </div>
            <p className="text-[11px] text-zinc-500 mt-0.5 truncate">
              @{bot.roblox_username || '—'} · {bot.relay_url} · seen {relTime(bot.health_at)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-4">
          {[
            { l:'Items',  v: fmt(bot.items_held||0) },
            { l:'Value',  v: fmt(bot.items_value||0) },
            { l:'Active', v: fmt(bot.active_trades||0) },
            { l:'Queue',  v: fmt(h.queue_depth||0) },
          ].map(m=>(
            <div key={m.l} className="bg-[#0B0C0E] border border-[#2A2D31] rounded-lg px-2 py-2">
              <p className="text-[9px] font-bold uppercase tracking-wider text-zinc-600">{m.l}</p>
              <p className="text-sm font-bold text-white truncate">{m.v}</p>
            </div>
          ))}
        </div>

        {/* ozel sunucu linki */}
        <div className="mt-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Server className="w-3.5 h-3.5 text-zinc-500"/>
            <span className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">
              Private server link
            </span>
            {bot.link_source && (
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                bot.link_source==='admin' ? 'text-emerald-400 bg-emerald-500/10'
                                          : 'text-sky-400 bg-sky-500/10'}`}>
                {bot.link_source === 'admin' ? 'set here' : 'from relay'}
              </span>
            )}
          </div>
          {canEdit ? (
            <div className="flex gap-2">
              <input value={link} onChange={e=>setLink(e.target.value)}
                placeholder="https://www.roblox.com/share?code=...&type=Server"
                className="flex-1 min-w-0 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-sky-500/50"/>
              <button onClick={save} disabled={saving || link.trim()===(bot.private_server_link||'')}
                className="px-3 py-2 rounded-lg bg-sky-500/15 border border-sky-500/40 text-sky-300 text-xs font-bold hover:bg-sky-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : 'Save'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-zinc-400 truncate">{bot.join_link || '— not set —'}</p>
          )}
          {bot.join_link && (
            <a href={bot.join_link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-sky-400 hover:text-sky-300">
              <Link2 className="w-3 h-3"/> open link <ExternalLink className="w-2.5 h-2.5"/>
            </a>
          )}
          {!bot.join_link && (
            <p className="text-[10px] text-amber-400/80 mt-1.5">
              No link — users can't be sent to this bot, so it won't be used.
            </p>
          )}
        </div>

        {h.error && (
          <p className="mt-3 text-[11px] text-red-400/90 bg-red-500/10 border border-red-500/25 rounded-lg px-2.5 py-2">
            {String(h.error)}
          </p>
        )}
      </div>

      {/* envanter */}
      <button onClick={()=>{ setOpen(o=>!o); loadItems(); }}
        className="w-full px-4 py-2.5 border-t border-[#2A2D31] flex items-center gap-2 text-xs font-bold text-zinc-400 hover:text-white hover:bg-white/5 transition-colors">
        <Package className="w-3.5 h-3.5"/> Inventory
        <span className="text-zinc-600">({fmt(bot.items_held||0)})</span>
        {open ? <ChevronUp className="w-3.5 h-3.5 ml-auto"/> : <ChevronDown className="w-3.5 h-3.5 ml-auto"/>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}}
            className="overflow-hidden border-t border-[#2A2D31]">
            <div className="p-3 max-h-64 overflow-y-auto">
              {loadingItems ? (
                <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-600"/></div>
              ) : !items || items.length===0 ? (
                <p className="py-5 text-center text-xs text-zinc-600">No items on this bot.</p>
              ) : (
                <div className="space-y-1">
                  {items.map(it=>(
                    <div key={it.item_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-[#0B0C0E]">
                      <span className="text-xs text-white truncate flex-1">{it.name}</span>
                      <span className="text-[11px] text-zinc-500 shrink-0">x{it.count}</span>
                      <span className="text-[11px] font-bold text-emerald-400 shrink-0 w-16 text-right">
                        {fmt(it.total_value)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Mm2BotsView = ({ viewerRank }) => {
  const [bots, setBots] = useState([]);
  const [unlocated, setUnlocated] = useState(0);
  const [pending, setPending] = useState([]);
  /* 👻 Botun envanterinde BULUNAMAYAN eşyalar. Bu liste dolu olduğunda site
     defteri ile botun gerçek Roblox hesabı AYRIŞMIŞ demektir — bekleyen
     eşyalardan (fiyatlanamamış yatırma) tamamen farklı ve daha ciddi bir
     durum, o yüzden ayrı bir state ve ayrı bir uyarı bloğu. */
  const [missing, setMissing] = useState([]);
  const [missingInfo, setMissingInfo] = useState(null);
  /* Kapatma akışı: hangi kayıt açık, not ne, istek uçuyor mu.
     ⚠️ Not ZORUNLU (sunucu min 3 karakter istiyor). Notsuz kapatma,
        kaydı çözmek değil GİZLEMEK olurdu — bir sonraki operatör aynı
        açığı sıfırdan araştırmak zorunda kalırdı. */
  const [resolving, setResolving] = useState(null);   // {id} | null
  const [resolveNote, setResolveNote] = useState('');
  const [resolveBusy, setResolveBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const canEdit = viewerRank >= 4;   // OWNER

  const load = useCallback(async () => {
    try {
      /* ⚠️ Hayalet kayıt isteği de `.catch(()=>null)` ile yutuluyor — aynı
         desen, aynı gerekçe: bu uç 403/404 dönse bile bot listesi
         GÖRÜNMEYE devam etmeli. Bot listesini kaybetmek, operatörü
         tamamen kör bırakırdı. */
      const [b, p, m] = await Promise.all([
        axios.get(`${API}/admin/mm2/bots`),
        axios.get(`${API}/admin/mm2/pending-items`).catch(()=>null),
        axios.get(`${API}/admin/mm2/missing-items`).catch(()=>null),
      ]);
      setBots(b.data?.bots || []);
      setUnlocated(b.data?.unlocated_items || 0);
      setPending(p?.data?.pending || []);
      setMissing(m?.data?.missing || []);
      setMissingInfo(m?.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load bots');
    } finally { setLoading(false); }
  }, []);

  useEffect(()=>{ load(); }, [load]);
  useEffect(()=>{ const id=setInterval(load, 15000); return ()=>clearInterval(id); }, [load]);

  /* ⚠️ SADECE KAYDI KAPATIR — eşya basmaz, para vermez.
     Sunucu tarafı da bilerek böyle: telafi gerekiyorsa ayrı ve bilinçli bir
     işlemle yapılır. Tek tuşta "çöz + telafi et" birleştirmek, kaydı
     kapatırken yanlışlıkla değer basmayı kolaylaştırırdı. */
  const resolveMissing = async (id) => {
    const note = resolveNote.trim();
    if (note.length < 3) { toast.error('Write a short note (min 3 chars).'); return; }
    setResolveBusy(true);
    try {
      await axios.post(`${API}/admin/mm2/missing-items/resolve`,
        { missing_id: id, note });
      toast.success('Record closed.');
      setResolving(null); setResolveNote('');
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not close the record.');
    } finally { setResolveBusy(false); }
  };

  const ready = bots.filter(b=>b.ready).length;

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}} className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Bot className="w-5 h-5 text-sky-400"/> MM2 Bots
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            {ready}/{bots.length} online · status is automatic — bots register and
            report themselves; you only set their server link
          </p>
        </div>
        <button onClick={load}
          className="px-3 py-2 rounded-lg bg-[#121417] border border-[#2A2D31] text-xs font-bold text-zinc-400 hover:text-white flex items-center gap-2">
          <RefreshCw className="w-3.5 h-3.5"/> Refresh
        </button>
      </div>

      {!canEdit && (
        <p className="text-[11px] text-zinc-500 bg-[#121417] border border-[#2A2D31] rounded-lg px-3 py-2">
          View only — owner rank is required to set server links.
        </p>
      )}

      {unlocated > 0 && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl border border-red-500/30 bg-red-500/10">
          <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5"/>
          <p className="text-xs text-red-300 leading-relaxed">
            <span className="font-bold">{fmt(unlocated)} items have no known bot.</span>{' '}
            They can't be withdrawn until their location is fixed — usually caused by a
            deleted bot or items minted by a bot coinflip.
          </p>
        </div>
      )}

      {pending.length > 0 && (
        <div className="px-4 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10">
          <p className="text-xs font-bold text-amber-300 mb-1.5">
            {pending.length} deposited item(s) not credited — no price could be determined
          </p>
          {pending.slice(0,5).map(p=>(
            <p key={p.id} className="text-[11px] text-zinc-400">
              {p.username || p.user_id} → {(p.names||[]).join(', ')}
            </p>
          ))}
        </div>
      )}

      {/* 👻 SİTEDE VAR, BOTTA YOK — EN CİDDİ ENVANTER UYARISI.
          Bekleyen eşyalardan (amber) ayrı ve daha yüksek şiddette gösteriliyor:
          orada bir kullanıcıya BORÇ vardır, burada envanterin kendisi
          gerçeklikten kopmuştur. Sebebi bot hesabının ele geçirilmesi
          olabilir; operatörün bunu bir fiyatlama sorunuyla karıştırmaması
          için görsel olarak da ayrılıyor. */}
      {missing.length > 0 && (
        <div className="px-4 py-3 rounded-xl border border-red-500/40 bg-red-500/10 space-y-2">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5"/>
            <div className="min-w-0">
              <p className="text-xs font-bold text-red-300">
                {fmt(missingInfo?.items_total ?? missing.length)} item(s) were missing
                from a bot's Roblox inventory
              </p>
              <p className="text-[11px] text-red-200/80 leading-relaxed mt-0.5">
                The site's ledger and the bot's real account have drifted apart. Each
                withdrawal was cancelled and the buyer was refunded automatically.
                {missingInfo?.auto_remove_enabled === false
                  ? ' Ledger auto-correction is OFF — these rows are still listed as in stock.'
                  : ' Affected rows were dropped from the ledger unless marked otherwise below.'}
                {' '}Check the bot's in-game trade history and account security.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pl-6 text-[11px] text-zinc-400">
            <span>Item value: <span className="text-zinc-200 mm-num">{fmt(missingInfo?.value_total ?? 0)}</span></span>
            <span>User paid (auto-refunded): <span className="text-zinc-200 mm-num">{fmt(missingInfo?.charged_total ?? 0)}</span></span>
            {(missingInfo?.compensation_due_total ?? 0) > 0 && (
              <span className="text-amber-300">
                Owed to users: <span className="mm-num">{fmt(missingInfo.compensation_due_total)}</span>
              </span>
            )}
          </div>
          <div className="pl-6 space-y-0.5">
            {missing.slice(0,6).map(m=>(
              <div key={m.id}>
                {/* ⚠️ `truncate` YALNIZCA METİN SPAN'INDA. Önceden tüm satır
                    kırpılıyordu ve uzun eşya adlarında "close" butonu
                    görünmez oluyordu — operatör kaydı kapatamıyordu. Buton
                    artık flex düzeninde ayrı bir öge ve asla kırpılmaz. */}
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-[11px] text-zinc-400 truncate min-w-0">
                    <span className="text-zinc-500">{m.bot_name || m.bot_id || '—'}</span>
                    {' · '}{(m.names||[]).join(', ')}
                    {' · '}
                    <span className={m.owner === 'house' ? 'text-zinc-500' : 'text-amber-300'}>
                      {m.owner === 'house' ? 'house stock' : `user: ${m.username || m.user_id || '?'}`}
                    </span>
                    {m.removed_from_ledger === false && (
                      <span className="text-orange-300"> · still in stock</span>
                    )}
                  </span>
                  {canEdit && resolving?.id !== m.id && (
                    <button
                      onClick={()=>{ setResolving({id:m.id}); setResolveNote(''); }}
                      className="shrink-0 text-[10px] font-bold text-zinc-500 hover:text-white underline underline-offset-2">
                      close
                    </button>
                  )}
                </div>
                {/* ⚠️ Otomatik düzeltme atlandıysa SEBEBİ gösterilir.
                    "Neden hâlâ stokta görünüyor?" sorusunu operatörün
                    tahmin etmesi, yanlış müdahalenin en kısa yoludur. */}
                {m.removed_from_ledger === false && m.skip_reason && (
                  <p className="text-[10px] text-orange-300/80 leading-snug">
                    ↳ {m.skip_reason}
                  </p>
                )}
                {canEdit && resolving?.id === m.id && (
                  <div className="flex items-center gap-2 mt-1 mb-1.5">
                    <input
                      value={resolveNote}
                      onChange={(e)=>setResolveNote(e.target.value)}
                      maxLength={200}
                      placeholder="What did you find? (required)"
                      className="flex-1 min-w-0 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg px-2 py-1 text-[11px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-red-500/50"/>
                    <button
                      disabled={resolveBusy}
                      onClick={()=>resolveMissing(m.id)}
                      className="px-2 py-1 rounded-lg bg-red-500/20 border border-red-500/40 text-[10px] font-bold text-red-200 hover:bg-red-500/30 disabled:opacity-50">
                      {resolveBusy ? '…' : 'Close'}
                    </button>
                    <button
                      disabled={resolveBusy}
                      onClick={()=>{ setResolving(null); setResolveNote(''); }}
                      className="px-2 py-1 rounded-lg border border-[#2A2D31] text-[10px] font-bold text-zinc-500 hover:text-white disabled:opacity-50">
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
            {missing.length > 6 && (
              <p className="text-[11px] text-zinc-500">+{missing.length - 6} more…</p>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid gap-3 md:grid-cols-2">
          {[0,1,2,3].map(i=>(
            <div key={i} className="h-56 rounded-2xl bg-[#121417] border border-[#2A2D31] animate-pulse"/>
          ))}
        </div>
      ) : bots.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2A2D31] bg-[#121417] py-14 px-6 text-center">
          <Bot className="w-10 h-10 mx-auto mb-3 text-zinc-700"/>
          <p className="font-bold text-white mb-1">No bots connected</p>
          <p className="text-xs text-zinc-500 max-w-md mx-auto leading-relaxed">
            Run one mm2_server.py per bot (each with its own port and state file) and start
            the Lua script in that bot's Roblox client. Bots appear here automatically once
            they send their first heartbeat.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {bots.map(b=><BotCard key={b.id} bot={b} canEdit={canEdit} onSaved={load}/>)}
        </div>
      )}
    </motion.div>
  );
};

export const AdminPanel = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState('dashboard');

  // Cross-section navigation state (from clicking a user/identity chip elsewhere)
  const [prefilledIdentifier, setPrefilledIdentifier] = useState('');
  const [prefilledIdentity, setPrefilledIdentity] = useState(null); // {type:'ip'|'fingerprint'|'device', value:string}

  const viewerRank = getUserRank(user);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); window.dispatchEvent(new CustomEvent('donut:auth', { detail: { tab: 'login' } })); return; }
    if (viewerRank < 2) {
      toast.error('Staff access required.');
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate, viewerRank]);

  if (authLoading || viewerRank < 2) return null;

  const visibleNav = NAV.filter(n => viewerRank >= n.minRank);

  const goToUser = (identifier) => {
    if (!identifier) return;
    setPrefilledIdentifier(identifier);
    setPage('user-lookup');
  };
  const goToIdentity = (type, value) => {
    if (!value || !type) return;
    setPrefilledIdentity({ type, value });
    setPage('identity-investigation');
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E] text-white pt-16 md:pt-20">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">

          {/* SIDEBAR */}
          <aside className="hidden md:flex flex-col w-56 shrink-0">
            <div className="sticky top-6 space-y-3">
              <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center border border-red-500/30">
                  <Shield className="w-5 h-5 text-red-400"/>
                </div>
                <div>
                  <div className="text-xs text-zinc-500">DonutLuck</div>
                  <div className="font-bold text-white text-sm">Admin Panel</div>
                </div>
              </div>
              <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl px-4 py-3">
                <div className="text-xs text-zinc-500 mb-1.5">Signed in</div>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-bold text-white text-sm truncate">{user.site_username||user.username}</div>
                  <RankBadge rank={getRankLabel(user)} size="xs"/>
                </div>
              </div>
              <nav className="space-y-1">
                {visibleNav.map(({id,icon:Icon,label,minRank})=>(
                  <button key={id} onClick={()=>setPage(id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold transition-all ${
                      page===id ? 'bg-red-500/20 text-red-300 border border-red-500/20' : 'text-zinc-500 hover:bg-white/5 hover:text-white'
                    }`}>
                    <Icon className="w-4 h-4"/>
                    <span className="flex-1 text-left">{label}</span>
                    {minRank === 3 && page!==id && <Shield className="w-3 h-3 opacity-40"/>}
                  </button>
                ))}
              </nav>
              <button onClick={()=>navigate('/')}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm text-zinc-600 hover:text-white hover:bg-white/5 transition-colors">
                <ArrowLeft className="w-4 h-4"/> Back to Site
              </button>
            </div>
          </aside>

          {/* MOBILE NAV */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#121417] border-t border-[#2A2D31] flex overflow-x-auto">
            {visibleNav.map(({id,icon:Icon,label})=>(
              <button key={id} onClick={()=>setPage(id)}
                className={`flex-1 min-w-[68px] flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold transition-all ${page===id?'text-red-400':'text-zinc-600'}`}>
                <Icon className="w-4 h-4"/>{label.split(' ')[0]}
              </button>
            ))}
          </div>

          {/* CONTENT */}
          <main className="flex-1 min-w-0 pb-20 md:pb-0">
            <AnimatePresence mode="wait">
              {page==='dashboard' &&
                <DashboardView key="d" setPage={setPage}/>}
              {page==='audit-logs' &&
                <AuditLogsView key="a" goToUser={goToUser} goToIdentity={goToIdentity}/>}
              {page==='user-lookup' &&
                <UserLookupView key="u" viewer={user}
                  prefilled={prefilledIdentifier}
                  onConsumed={()=>setPrefilledIdentifier('')}
                  goToIdentity={goToIdentity}/>}
              {page==='identity-investigation' &&
                <IdentityInvestigationView key="i"
                  prefilled={prefilledIdentity}
                  onConsumed={()=>setPrefilledIdentity(null)}
                  goToUser={goToUser}
                  goToIdentity={goToIdentity}/>}
              {page==='manual-review' && viewerRank>=3 &&
                <ManualReviewView key="m"/>}
              {page==='mm2-bots' && viewerRank>=3 &&
                <Mm2BotsView key="mb" viewerRank={viewerRank}/>}
              {page==='mm2-identity' && viewerRank>=3 &&
                <Mm2IdentityView key="mi" viewerRank={viewerRank}/>}
              {page==='mm2-prices' && viewerRank>=3 &&
                <Mm2PriceIssuesView key="mp" viewerRank={viewerRank}/>}
              {page==='suspicious-events' && viewerRank>=3 &&
                <SuspiciousView key="s"/>}
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
};



/* ════════════════════════════════════════════════════════════
   MM2 PRICE ISSUES — fiyatı güncellenemeyen eşyalar

   NEDEN VAR: kaynak bir eşyayı listelemeyi bıraktığında fiyatı sessizce
   donuyor. Eşya bir süre mağazada kalıyor, sonra tazelik kapısından
   düşüyor ve sebebi hiçbir yerde görünmüyordu.

   ⚠️ BU EKRAN FİYAT YAZAR — para kararı verir. Yazma owner ister ve
      girilen fiyat `price_manual` damgasıyla ayrışır: otomatik senkronun
      yazdığıyla karıştırılmaz, denetimde ayırt edilir.
   ════════════════════════════════════════════════════════════ */
/* ⚠️⚠️ SUNUCU YANITININ ŞEKLİNE GÜVENİLMEZ.
   🔴 Kapatılan çökme sınıfı: panel `data.items`, `row.family`, `it.icon_ids`
   gibi alanların doğru tipte olduğunu varsayıyordu. Yanıt bir vekilden
   geçerse, sunucu eski sürümse ya da yanıt kısmi gelirse bu alanlar
   metin/sözlük/null olabiliyor ve `.forEach` / `.find` çağrısı TypeError
   fırlatıyor. React'te bir bileşen fırlatınca TÜM AĞAÇ sökülür — operatör
   beyaz ekran görür ve sebebini bilemez.

   ⚠️ Sessizce boş dönmek doğru: amaç doğrulama değil, ekranın ayakta
      kalması. Boş liste zaten "sonuç yok" olarak gösteriliyor. */
/* ⚠️⚠️ HATA DETAYINI HER TİPTE METNE ÇEVİR.
   🔴 Kapatılan çökme: hata dalları `e?.response?.data?.detail || 'Kaydedilemedi'`
   yazıyordu. FastAPI 422'de (doğrulama hatası) `detail` bir SÖZLÜK LİSTESİDİR;
   React onu child olarak render EDEMEZ ("Objects are not valid as a React
   child") ve fırlatan bileşen TÜM AĞACI söker — operatör beyaz ekran görür ve
   sebebini bilemez.

   ⚠️ Ulaşılabilir bir durum, teorik değil: slug alanları serbest metin ve
      istemci tarafı doğrulama yok. Operatör "evil.com/foo" ya da "foo!bar"
      yazarsa `_mm2_parse_price_slug` sunucuda ValueError fırlatır → 422.

   ⚠️ Amaç doğrulama değil DAYANIKLILIK: ne gelirse gelsin okunabilir bir
      satır üretilir, asla fırlatılmaz. */
const detailText = (detail, fallback = 'İşlem başarısız') => {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail.trim() || fallback;
  if (Array.isArray(detail)) {
    const parts = detail.map(d => {
      if (typeof d === 'string') return d;
      /* pydantic hata nesnesi: {loc:[...], msg:"...", type:"..."} */
      const loc = Array.isArray(d?.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
      const msg = d?.msg || d?.message || '';
      if (msg) return loc ? `${loc}: ${msg}` : msg;
      try { return JSON.stringify(d); } catch { return String(d); }
    }).filter(Boolean);
    return parts.length ? parts.join('\n') : fallback;
  }
  if (typeof detail === 'object') {
    const msg = detail?.msg || detail?.message || detail?.detail;
    if (typeof msg === 'string' && msg.trim()) return msg;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail) || fallback;
};

/* ⚠️⚠️ SUNUCU MESAJI ÇOK SATIRLI OLABİLİR.
   🔴 Kapatılan bilgi kaybı: kısmi uygulama 409'u
   "…\n\nNOTE: 1 item(s) were already saved…" gönderiyor. CSS
   varsayılanında satır sonu BOŞLUĞA dönüşür ve bu uyarı tek paragrafın
   ortasında kaybolur. Oysa "bazı kayıtlar YAZILDI" bilgisi kritik:
   operatör "geri alındı" sanıp aynı işlemi tekrar dener.

   ⚠️ Toast'a JSX veriliyor: satırlar ayrı ayrı, NOTE satırı vurgulu. */
const toastDetail = (detail, fallback) => {
  const raw = detailText(detail, fallback);
  const parts = raw.split('\n').map(x => x.trim()).filter(Boolean);
  if (parts.length < 2) return raw;
  return (
    <span className="block">
      {parts.map((p, i) => (
        <span key={i}
          className={`block ${i > 0 ? 'mt-1 font-semibold text-amber-300' : ''}`}>
          {p}
        </span>
      ))}
    </span>
  );
};

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) =>
  (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
const asRecords = (v) => asArray(v).filter(x => x && typeof x === 'object');

/* ⚠️⚠️ Sunucudaki `_mm2_safe_int` ile BİREBİR AYNI davranmalı: bozuk/eksik
   değer 0 olur, ASLA uydurulmuş bir sayıya dönüşmez. `|| 1` kalıbı 0'ı 1
   gösteriyordu ve yarış korumasının karşılaştırdığı iki değeri kalıcı olarak
   ayırıyordu.

   ⚠️ DİZİLER AÇIKÇA REDDEDİLİR. JS'te `Number([5])` === 5, oysa Python'da
      `float([5])` TypeError verir ve `_mm2_safe_int` 0 döner. Bu tek fark,
      `value: [5]` taşıyan bir kayıtta panelin `value_seen: 5`, sunucunun
      `_db_val: 0` görmesine ve koşullu yazmanın SONSUZA KADAR 409 dönmesine
      yol açardı — düzeltmeye çalıştığımız hatanın ta kendisi, başka kılıkta. */
/* ⚠️⚠️ SUNUCU MODELLERİYLE HİZALI SINIRLAR.
   🔴 Kapatılan bilgi boşluğu (köprü testi buldu): panel yalnızca ALT sınırı
   (value >= 1) kontrol ediyordu. Üst sınır ve ad uzunluğu hiç bakılmıyordu,
   dolayısıyla operatör "1e20" ya da 70 karakterlik eski bir adı kaydetmeye
   çalıştığında istek sunucuya gidiyor ve ham bir 422 dönüyordu. Ölçüldü:
   panelin geçirdiği 1600 gönderimin 882'si modelde reddediliyordu.
   Para riski yok (sunucu doğru reddediyor) ama operatör NEYİ düzelteceğini
   ekranda göremiyordu — §6'daki "operatör bilgisi" sınıfı.

   ⚠️ Bu sabitler `MM2AdminItem` / `MM2IdentityEntry` / `MM2PriceFixEntry`
      alanlarının AYNISI olmalı. Sunucuda değişirse burası da değişmeli;
      ayrışırsa panel ya boşuna engeller ya da yine ham 422'ye düşer.
      Sunucu yine de tek yetkili doğrulayıcıdır — bu kontrol onun YERİNE
      değil, ONDAN ÖNCE operatöre söylemek için var. */
const MM2_VALUE_MIN = 1;
const MM2_VALUE_MAX = 100000000;      // le=100_000_000
const MM2_NAME_MAX  = 64;             // max_length=64
const MM2_ID_RE     = /^[a-z0-9_]{2,48}$/;   // _MM2_SLUG_RE

/* ⚠️ SLUG ÖN KONTROLÜ — SUNUCUNUN YERİNE DEĞİL, ONDAN ÖNCE.
   Sunucudaki `_mm2_parse_price_slug` tek yetkili doğrulayıcıdır (yabancı
   alan adı, fail-closed kaynak listesi, `..` — hepsi orada). Burada YALNIZCA
   kesin olarak geçersiz olanı yakalıyoruz ki operatör kaydet'e basmadan
   görsün. Şüpheli olanı GEÇİRİYORUZ: burada reddetmek, sunucunun kabul
   edeceği bir girdiyi engellemek olurdu (iki doğrulayıcının ayrışması).
   ⚠️ Boş dize GEÇERLİDİR: slug'ı kasıtlı boşaltmanın yolu. */
const mm2SlugProblem = (v) => {
  const t = String(v ?? '').trim();
  if (!t) return null;
  if (t.includes('..')) return "'..' içeremez";
  if (t.length > 96) return 'çok uzun (en fazla 96 karakter)';
  /* URL yapıştırmak SERBEST: sunucu alan adını doğrulayıp son parçayı alır. */
  if (t.includes('/')) return null;
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(t))
    return 'yalnızca harf, rakam, - ve _ (harf/rakamla başlamalı)';
  return null;
};

const asInt = (v, dflt = 0) => {
  if (v === null || typeof v === 'object') return dflt;   // dizi + nesne
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : dflt;
};


const Mm2PriceIssuesView = ({ viewerRank }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState({});      // id → {price_slug?, value?}
  const [reason, setReason] = useState('');
  const [maxAge, setMaxAge] = useState(null);
  const [thumbs, setThumbs] = useState({});
  const [filter, setFilter] = useState('all');
  const [truncated, setTruncated] = useState(false);
  /* ⚠️ `id` alanı olmayan katalog kayıtları bu ekrandan düzeltilemez
     (düzeltme ucu `id` ile yazıyor). Sunucu onları atlıyor ama SESSİZ
     kalmıyor; sayıyı burada gösteriyoruz ki operatör veritabanında
     elle bakabilsin. */
  const [skippedNoId, setSkippedNoId] = useState(0);

  const auth = useMemo(()=>({ headers:{ Authorization:`Bearer ${token}` } }), [token]);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/mm2/price-issues`, auth);
      /* ⚠️ `asRecords` hem listeyi hem İÇİNDEKİ öğeleri süzer. */
      const list = asRecords(r.data?.items);
      setItems(list);
      setMaxAge(r.data?.manual_price_max_age_days ?? null);
      setTruncated(!!r.data?.truncated);
      setSkippedNoId(asInt(r.data?.skipped_no_id));
      setEdits({});
      /* Görseller: kimlik ekranıyla aynı vekil. */
      const ids = new Set();
      list.forEach(i => asArray(i.icon_ids).forEach(a => ids.add(String(a))));
      if (ids.size) {
        const t = await fetchThumbsChunked(ids, auth);
        setThumbs(t.urls);
      } else setThumbs({});
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail, 'Could not load price issues'));
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(()=>{ fetchIssues(); }, [fetchIssues]);

  const setEdit = (id, k, v) =>
    setEdits(p => ({ ...p, [id]: { ...(p[id]||{}), [k]: v } }));

  /* ⚠️ YALNIZCA DEĞİŞTİRİLENLER gönderilir. Tüm listeyi göndermek,
     dokunulmamış eşyalara da `price_manual` damgası basmak olurdu —
     yani operatörün onaylamadığı fiyatları "onaylandı" saymak. */
  const dirty = useMemo(() => Object.entries(edits).filter(([id, e]) => {
    /* ⚠️ `items` her zaman dizi olmayabilir (başlangıç/kısmi yanıt). */
    const it = asRecords(items).find(x => x.id === id);
    if (!it) return false;
    const slugChanged = e.price_slug !== undefined &&
                        (e.price_slug || '') !== (it.price_slug || '');
    /* ⚠️ `Number("abc")` NaN'dır ve `NaN !== x` HER ZAMAN doğrudur — yani
       geçersiz metin "değiştirildi" sayılıp gönderime giriyordu. Geçerli
       bir sayı değilse değişiklik SAYILMAZ; ayrı bir kontrol operatörü
       uyarır (aşağıya bakın). */
    const valChanged  = e.value !== undefined && String(e.value).trim() !== '' &&
                        Number.isFinite(Number(e.value)) &&
                        asInt(e.value) !== asInt(it.value);
    return slugChanged || valChanged;
  }), [edits, items]);

  const save = async () => {
    if (!dirty.length) { toast.error('Değişiklik yok'); return; }
    if (reason.trim().length < 3) { toast.error('Kısa bir gerekçe yazın (denetim)'); return; }
    const entries = dirty.map(([id, e]) => {
      const it = asRecords(items).find(x => x.id === id);
      const out = { id };
      if (e.price_slug !== undefined && (e.price_slug||'') !== (it.price_slug||''))
        out.price_slug = e.price_slug || '';
      if (e.value !== undefined && String(e.value).trim() !== '' &&
          Number.isFinite(Number(e.value)) && asInt(e.value) !== asInt(it.value)) {
        /* ⚠️⚠️ `Math.max(1, parseInt(v) || 0)` KULLANILAMAZ — PARA HATASI.
           🔴 Kapatılan açık: geçersiz her girdi SESSİZCE 1'e dönüyordu:
             "abc" → 1 · "1e5" → 1 · "-50" → 1 · "0" → 1
           Ardından `!(en.value >= 1)` kontrolünden de geçiyordu (1 ≥ 1).
           Yani operatör 100.000 yazmak için "1e5" yazdığında eşyanın fiyatı
           1 oluyor, üstüne `price_manual` damgasıyla 30 GÜN sabitleniyordu.
           Sonuç: eşya mağazada ~1 bakiyeye satılıyor (gerçek değeri binlerce)
           ve yatırmada kullanıcı eşyasını 1 karşılığında veriyor.
           Artık geçersiz girdi hiç gönderilmez; aşağıdaki kontrol uyarır. */
        out.value = asInt(e.value);
        /* ⚠️ SAYFAYI AÇARKEN GÖRÜLEN FİYAT. Sunucu bununla "operatör bilerek
           değiştirdi" ile "ekran açıkken senkron değiştirdi"yi ayırıyor.
           Gönderilmezse yarış koruması devreye GİRMEZ ve eski bir değer
           30 günlük damgayla geri yazılabilir. */
        /* ⚠️ Ekranda GÖRÜLEN değerle birebir aynı olmalı; `asInt`
           sunucudaki `_mm2_safe_int` ile aynı davranır. */
        out.value_seen = asInt(it.value);
      }
      return out;
    });
    /* ⚠️ Geçersiz metin artık `entries`e HİÇ girmiyor; operatör yine de
       uyarılmalı, yoksa "yazdım ama kaydolmadı" durumu doğar. */
    for (const [id, e] of Object.entries(edits)) {
      const raw = e.value;
      if (raw === undefined || String(raw).trim() === '') continue;
      if (!Number.isFinite(Number(raw)) || asInt(raw) < MM2_VALUE_MIN) {
        toast.error(`${id}: "${raw}" geçerli bir fiyat değil. `
                    + `Sadece rakam yazın (ör. 100000).`);
        return; }
      /* ⚠️ ÜST SINIR — sunucu modeliyle hizalı. Olmadan "1e20" gibi bir
         girdi sunucuya gidip ham 422 üretiyordu. */
      if (asInt(raw) > MM2_VALUE_MAX) {
        toast.error(`${id}: fiyat en fazla ${MM2_VALUE_MAX.toLocaleString('tr-TR')} `
                    + `olabilir (girilen: ${asInt(raw).toLocaleString('tr-TR')}).`);
        return; }
    }
    for (const [id, e] of Object.entries(edits)) {
      if (e.price_slug === undefined) continue;
      const sp = mm2SlugProblem(e.price_slug);
      if (sp) { toast.error(`${id}: kaynak slug'ı geçersiz — ${sp}.`); return; }
    }
    for (const en of entries) {
      if (en.value !== undefined && !(en.value >= 1)) {
        toast.error(`${en.id}: fiyat en az 1 olmalı`); return; }
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/mm2/price-issues/fix`,
                       { entries, reason: reason.trim() }, auth);
      toast.success(`${entries.length} eşya güncellendi`);
      setReason('');
      fetchIssues();
    } catch (e) {
      /* ⚠️ 409 = sayfa açıkken fiyat değişti. Listeyi yenileyip güncel
         değeri göstermek, operatörün eski değeri geri yazmasını önler. */
      if (e?.response?.status === 409) {
        toast.error(toastDetail(e?.response?.data?.detail,
                                'Fiyat değişti — liste yenileniyor'),
                    { duration: 8000 });
        fetchIssues();
      } else {
        toast.error(detailText(e?.response?.data?.detail, 'Kaydedilemedi'));
      }
    } finally { setSaving(false); }
  };

  const shown = items.filter(i =>
    filter === 'all' ? true :
    filter === 'missing' ? i.sync_missing :
    filter === 'held' ? i.held > 0 : true);

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-amber-400"/> MM2 Price Issues
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Fiyatı otomatik güncellenemeyen eşyalar. Kaynak slug'ını düzeltin ya da
            fiyatı elle girin — elle girilen fiyat{maxAge ? ` ${maxAge} gün` : ''} boyunca
            markette geçerli sayılır.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* ⚠️ Sayılar düğmede görünür: operatör boş bir filtreye basmadan
              önce kaç eşya olduğunu bilir. */}
          {['all','missing','held'].map(f => {
            const n = f === 'all' ? items.length
                    : f === 'missing' ? items.filter(i => i.sync_missing).length
                    : items.filter(i => i.held > 0).length;
            return (
              <button key={f} onClick={()=>setFilter(f)}
                className={`px-2.5 py-1 rounded-lg text-xs ${filter===f
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-600/50'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                {f==='all'?'Hepsi':f==='missing'?'Kaynakta yok':'Elde tutulan'}
                <span className="ml-1 opacity-60">{n}</span>
              </button>
            );
          })}
          <button onClick={fetchIssues} disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-white">
            <RefreshCw className={`w-4 h-4 ${loading && 'animate-spin'}`}/> Yenile
          </button>
        </div>
      </div>

      {loading && items.length===0 && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin"/>
        </div>
      )}

      {/* ⚠️ Aynı gerekçe: kırpılmış fiyat listesi tam sanılmamalı. */}
      {truncated && (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <span>Katalog tarama sınırını aştı — bu liste <b>eksik</b>.</span>
        </div>
      )}

      {/* ⚠️ Atlanan bozuk kayıtlar da sessiz kalmamalı: eskiden bu kayıtlar
          ekranı komple 500'e düşürüyordu, şimdi atlanıyorlar — ama atlandığı
          söylenmezse "sorun yok" sanılır. */}
      {skippedNoId > 0 && (
        <div className="rounded-lg border border-rose-700/60 bg-rose-950/20 px-3 py-2 text-xs text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <span><b>{skippedNoId}</b> katalog kaydında <code>id</code> alanı yok
            (ya da metin değil). Bu ekrandan düzeltilemezler — veritabanında
            elle bakın.</span>
        </div>
      )}

      {!loading && items.length===0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-10 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2"/>
          <p className="text-slate-300 font-medium">Fiyat sorunu yok</p>
          <p className="text-xs text-slate-500 mt-1">Tüm aktif eşyaların fiyatı güncel.</p>
        </div>
      )}

      {/* ⚠️ FİLTRE HİÇBİR ŞEY DÖNDÜRMEZSE DE BİR ŞEY YAZ.
          🔴 Kapatılan hata: `items` dolu ama `shown` boşken HİÇBİR blok
          render edilmiyordu — operatör filtreye basıyor, ekran bomboş
          kalıyor ve sayfa bozulmuş gibi görünüyordu. */}
      {!loading && items.length > 0 && shown.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 text-center">
          <Info className="w-6 h-6 text-slate-500 mx-auto mb-2"/>
          <p className="text-slate-300 text-sm">
            Bu filtreye uyan eşya yok
            <span className="text-slate-500"> ({items.length} eşya diğer filtrelerde)</span>
          </p>
          <button onClick={()=>setFilter('all')}
            className="mt-2 text-xs text-sky-400 hover:text-sky-300">
            Hepsini göster
          </button>
        </div>
      )}

      {shown.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
          {shown.map(it => {
            const e = edits[it.id] || {};
            const slugVal = e.price_slug !== undefined ? e.price_slug : (it.price_slug || '');
            const valVal  = e.value !== undefined ? e.value : it.value;
            return (
              <div key={it.id} className="flex items-start gap-3 p-3 border-b border-slate-800 last:border-0">
                <AssetThumb assetId={asArray(it.icon_ids)[0]}
                            url={thumbs[asArray(it.icon_ids)[0]]} size={44}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-medium">{it.name}</span>
                    <span className="text-[10px] font-mono text-slate-500">{it.id}</span>
                    {it.sync_missing && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-700/50">
                        kaynakta yok
                      </span>
                    )}
                    {it.price_manual && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-700/50">
                        elle girilmiş
                      </span>
                    )}
                    {it.held > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-700/50">
                        {it.held} adet elde
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">{it.reason}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    <div>
                      <label className="text-[10px] uppercase tracking-wide text-slate-500">
                        Price source slug
                      </label>
                      <input value={slugVal}
                        onChange={ev=>setEdit(it.id,'price_slug',ev.target.value)}
                        placeholder={`/${it.id.replace(/_/g,'-')}`}
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white font-mono"/>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wide text-slate-500">
                        Value {it.price_updated_at && (
                          <span className="normal-case text-slate-600">
                            · son: {String(it.price_updated_at).slice(0,10)}
                          </span>)}
                      </label>
                      <input type="number" min={1} value={valVal}
                        onChange={ev=>setEdit(it.id,'value',ev.target.value)}
                        className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"/>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dirty.length > 0 && (
        <div className="rounded-xl border border-emerald-800/50 bg-emerald-950/20 p-3 space-y-2">
          <div className="text-sm text-emerald-200">
            {dirty.length} eşyada değişiklik var.
          </div>
          {/* ⚠️ Fiyat girildiğinde eşya MARKETE GİRER — operatör bunu bilerek yapmalı. */}
          <p className="text-xs text-emerald-200/70">
            Fiyat girdiğiniz eşyalar “elle girilmiş” damgasıyla
            {maxAge ? ` ${maxAge} gün` : ''} boyunca markette geçerli sayılacak.
            Yalnızca slug düzelttiyseniz fiyata dokunulmaz; bir sonraki senkron bulur.
          </p>
          <input value={reason} onChange={e=>setReason(e.target.value)}
            placeholder="Gerekçe (denetim kaydı)"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"/>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving || viewerRank < 4}
              title={viewerRank < 4 ? 'Owner rütbesi gerekiyor' : ''}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm text-white font-medium">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Check className="w-4 h-4"/>}
              Kaydet
            </button>
            <button onClick={()=>{setEdits({}); setReason('');}}
              className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-slate-300">
              Vazgeç
            </button>
            {viewerRank < 4 && (
              <span className="text-xs text-amber-400">Fiyat yazmak için owner gerekiyor</span>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════
   MM2 ITEM IDENTITY — aynı isimli farklı eşyaları ayırma ekranı

   NEDEN VAR: MM2'de aynı ad iki ayrı eşyaya ait olabiliyor ("Starfish"
   hem gun hem knife). Bot her eşyanın assetId'sini bildiriyor; katalogda
   o assetId yoksa deposit KARANTİNAYA düşüyor ve kredilenmiyor. Bu ekran
   o boşluğu kapatır.

   ⚠️ BU EKRAN EŞYA VERMEZ, KREDİ YAZMAZ. Yalnızca katalogu düzenler.
      Kullanıcıya eşya verme (inventory/grant) ve kaydı kapatma
      (pending-items/resolve) AYRI ve bilinçli adımlardır. Birleştirmek,
      katalog düzenlerken yanlışlıkla eşya basmayı kolaylaştırırdı.
   ════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ GÖRSEL ADRESİ SUNUCUDAN GELİR — burada URL KURULMAZ.

   MM2'nin ikonları oyuna ait özel varlıklar. Doğrudan görsel döndüren eski
   uç noktaların (Thumbs/Asset.ashx, asset-thumbnail/image) hepsi kapandı;
   `library/{id}` de 404 veriyor — bu ID'nin bozuk olduğu ANLAMINA GELMEZ.
   Çalışan tek yol `thumbnails.roblox.com/v1/assets` ve o JSON döndürüyor,
   ayrıca tarayıcıdan çağrılınca CORS'a takılıyor. Bu yüzden adresi sunucu
   çözüyor (/admin/mm2/asset-thumbs), tarayıcı resmi CDN'den çekiyor. */
/* ⚠️⚠️ GÖRSELLER 100'ERLİK PARÇALAR HÂLİNDE İSTENİR.

   🔴 Kapatılan iki hata:
     · Kimlik ekranı listeyi OLDUĞU GİBİ gönderiyordu. Sunucu modeli
       `max_length=100` ile 422 dönüyor, panel yakalayıp `setThumbs({})`
       yapıyordu — 100'den fazla assetId olduğunda TEK BİR görsel bile
       gelmiyordu ve sebebi hiçbir yerde görünmüyordu.
     · Fiyat ekranı `.slice(0,100)` ile sessizce kırpıyordu: 100'den
       sonrası kalıcı olarak görselsiz, uyarı yok.

   ⚠️ Parça boyutu SUNUCU TAVANIYLA aynı olmalı (100). Büyütmek 422'ye,
      küçültmek gereksiz isteğe yol açar.
   ⚠️ Bir parça düşerse diğerleri KORUNUR: kısmi görsel, hiç görselden
      iyidir. */
const THUMB_CHUNK = 100;

async function fetchThumbsChunked(ids, auth) {
  const list = [...ids];
  const out = {};
  const failed = [];
  for (let i = 0; i < list.length; i += THUMB_CHUNK) {
    const part = list.slice(i, i + THUMB_CHUNK);
    try {
      const r = await axios.post(`${API}/admin/mm2/asset-thumbs`,
                                 { asset_ids: part }, auth);
      Object.assign(out, asObject(r.data?.urls));
      asArray(r.data?.failed).forEach(x => failed.push(x));
    } catch {
      part.forEach(x => failed.push(x));
    }
  }
  return { urls: out, failed, requested: list.length };
}

const AssetThumb = ({ assetId, url, size=64 }) => {
  const [broken, setBroken] = useState(false);
  const show = url && !broken;
  if (!show) {
    /* ⚠️ assetId EKRANDA KALIR: görsel gelmese de operatör numarayı
       görebilsin, kopyalayabilsin, karşılaştırabilsin. */
    return (
      <div className="flex flex-col items-center justify-center bg-slate-800 border border-slate-700 rounded-lg text-slate-500 overflow-hidden"
           style={{ width:size, height:size }}
           title={assetId ? `asset ${assetId}` : 'assetId bildirilmedi'}>
        <ImageOff className="w-4 h-4 mb-0.5"/>
        {assetId && (
          <span className="text-[8px] font-mono leading-none px-0.5 text-center break-all select-all">
            {String(assetId).slice(-7)}
          </span>
        )}
      </div>
    );
  }
  return (
    <img src={url} alt={`asset ${assetId}`}
         onError={()=>setBroken(true)}
         referrerPolicy="no-referrer" loading="lazy"
         className="rounded-lg border border-slate-700 bg-slate-800 object-contain"
         style={{ width:size, height:size }}/>
  );
};

/* Aile üyesi düzenleme satırı. Kayıtlı üyeler DOLU gelir. */
const IdentityRow = ({ entry, onChange, onRemove, isNew, thumbs = {} }) => {
  const set = (k, v) => onChange({ ...entry, [k]: v });
  return (
    <div className={`rounded-xl border p-3 space-y-3 ${isNew ? 'border-emerald-700/50 bg-emerald-950/20' : 'border-slate-700 bg-slate-900/40'}`}>
      <div className="flex items-start gap-3">
        <AssetThumb assetId={asArray(entry.icon_ids)[0]} url={thumbs[asArray(entry.icon_ids)[0]]} size={56}/>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Item ID (slug)</label>
            <input value={entry.id} disabled={!isNew}
              onChange={e=>set('id', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g,'_'))}
              placeholder="starfish_gun"
              className={`w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white ${!isNew && 'opacity-60 cursor-not-allowed'}`}/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Site name</label>
            <input value={entry.name} onChange={e=>set('name', e.target.value)}
              placeholder="Starfish (Gun)"
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"/>
          </div>
          <div>
            {/* ⚠️ Oyundaki ad. Aile üyeleri bunu PAYLAŞIR — kilitli. */}
            <label className="text-[10px] uppercase tracking-wide text-slate-500">In-game name (shared)</label>
            <input value={entry.game_name} disabled
              className="w-full bg-slate-950/60 border border-slate-800 rounded-lg px-2 py-1.5 text-sm text-slate-400 cursor-not-allowed"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-slate-500">Value</label>
            <input type="number" min={1} max={100000000} value={entry.value}
              /* ⚠️ `Math.max(0, parseInt(x))` NaN ÜRETİR: parseInt("abc") NaN,
                 Math.max(0,NaN) da NaN. NaN taslakta kalıyor, JSON'da `null`
                 olarak gidiyor ve sunucu 422 dönüyordu; panel kendi kontrolüyle
                 blokluyordu ama mesaj "katalogdaki fiyat geçersiz (null)" diyerek
                 operatörü yanlış yere bakmaya yönlendiriyordu. `asInt` NaN'ı 0'a
                 indirir, alan boş/geçersizken 0 kalır ve mesaj doğru olur. */
              onChange={e=>set('value', Math.max(0, asInt(e.target.value)))}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"/>
          </div>
        </div>
        {isNew && (
          <button onClick={onRemove} title="Remove"
            className="text-slate-500 hover:text-red-400 p-1"><X className="w-4 h-4"/></button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Category</label>
          <select value={entry.category} onChange={e=>set('category', e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white">
            <option value="weapons">weapons</option><option value="pets">pets</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Kind</label>
          <select value={entry.kind} onChange={e=>set('kind', e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white">
            <option value="gun">gun</option><option value="knife">knife</option>
            <option value="pet">pet</option><option value="other">other</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className="text-[10px] uppercase tracking-wide text-slate-500">Asset IDs (comma separated)</label>
          <input value={asArray(entry.icon_ids).join(', ')}
            onChange={e=>set('icon_ids', e.target.value.split(',').map(x=>x.trim()).filter(Boolean))}
            placeholder="18322189584"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white font-mono"/>
        </div>
      </div>

      {/* ⚠️ FİYAT KAYNAĞINDAKİ SLUG. Senkron kataloğu id/name/price_slug
          üzerinden kaynakla eşleştiriyor. Çoğu durumda GEREKMEZ: Item ID
          zaten aynı anahtarı üretir (clownfish_gun ↔ /clownfish-gun).
          Kaynağın adresi id'den farklıysa buraya yapıştırın. */}
      <div>
        <label className="text-[10px] uppercase tracking-wide text-slate-500">
          Price source slug (mm2.rocks)
        </label>
        <input value={entry.price_slug || ''}
          onChange={e=>set('price_slug', e.target.value)}
          placeholder="/clownfish-gun  (boş bırakılabilir)"
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white font-mono"/>
        <p className="text-[10px] text-slate-500 mt-0.5">
          Adres çubuğundan yapıştırabilirsiniz; başındaki <span className="font-mono">/</span> ve
          tam URL temizlenir. <b>Yalnızca tanınan fiyat kaynağının</b> adresi
          kabul edilir — başka bir siteden yapıştırırsanız kayıt reddedilir.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs">
        <label className="flex items-center gap-1.5 text-slate-400 cursor-pointer">
          <input type="checkbox" checked={!!entry.active}
            onChange={e=>set('active', e.target.checked)} className="accent-emerald-500"/> active
        </label>
        <label className="flex items-center gap-1.5 text-slate-400 cursor-pointer" title="Only for items that genuinely have no icon in-game">
          <input type="checkbox" checked={!!entry.no_icon}
            onChange={e=>set('no_icon', e.target.checked)} className="accent-amber-500"/> no_icon
        </label>
        {entry.no_icon && (entry.icon_ids||[]).length>0 && (
          <span className="text-red-400">⚠️ no_icon and asset IDs are mutually exclusive</span>
        )}
      </div>
    </div>
  );
};

const Mm2IdentityView = ({ viewerRank }) => {
  const { token } = useAuth();
  const [queue, setQueue] = useState([]);
  const [collisions, setCollisions] = useState({});
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [draft, setDraft] = useState([]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const auth = useMemo(()=>({ headers:{ Authorization:`Bearer ${token}` } }), [token]);

  const [thumbs, setThumbs] = useState({});
  const [resolvedPending, setResolvedPending] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [queueTruncated, setQueueTruncated] = useState(false);
  /* ⚠️ TEŞHİS DURUMU. Görsel gelmediğinde SEBEBİ ekranda görünmeli;
     aksi halde "çalışmıyor" deyip nereye bakacağını bilemiyorsun.
     null = henüz denenmedi · {ok,msg} = son denemenin sonucu */
  const [thumbDiag, setThumbDiag] = useState(null);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/mm2/item-identity/queue`, auth);
      const q = asRecords(r.data?.queue);
      setQueue(q);
      /* ⚠️ Kimliği çözülmüş ama HÂLÂ KREDİLENMEMİŞ deposit'ler. Kuyruktan
         kalktılar ama kullanıcı eşyasını almadı — ekran söylemezse sessizce
         asılı kalırlar. */
      setResolvedPending(asArray(r.data?.resolved_pending));
      /* ⚠️ Sunucu listeyi 100'de kırpıyor; GERÇEK sayı ayrı geliyor.
         Liste uzunluğunu saymak eksik bildirirdi. */
      setPendingTotal(Number(r.data?.resolved_pending_total) ||
                      asArray(r.data?.resolved_pending).length);
      setQueueTruncated(!!r.data?.truncated);
      setCollisions(asObject(r.data?.icon_collisions));

      /* ⚠️ Kuyruktaki VE ailelerdeki tüm assetId'ler tek istekte çözülür.
         Satır başına ayrı istek, 40 satırlık bir kuyrukta 40 çağrı demekti. */
      const ids = new Set();
      q.forEach(row => {
        if (row.asset) ids.add(String(row.asset));
        asRecords(row.family).forEach(d =>
          asArray(d.icon_ids).forEach(a => ids.add(String(a))));
      });
      Object.keys(asObject(r.data?.icon_collisions)).forEach(a => ids.add(String(a)));
      if (ids.size) {
        {
          /* ⚠️ `fetchThumbsChunked` kendi içinde hata yakalıyor: bir parça
             düşse bile diğerleri korunuyor ve düşenler `failed`e giriyor.
             Bu yüzden dış try/catch GEREKSİZ — üstelik yanıltıcıydı:
             "İstek başarısız" mesajı artık hiç görünemezdi çünkü çekici
             asla fırlatmıyor. Teşhis mesajı sonuca göre kuruluyor. */
          const t = await fetchThumbsChunked(ids, auth);
          setThumbs(t.urls);
          //  ⚠️ Çözülemeyenler AYRI bir durumda tutulmuyor: bilgi zaten
          //     aşağıdaki teşhis mesajında gösteriliyor. Okunmayan bir
          //     durum tutmak, ileride "bu neden var" sorusunu doğurur.
          const n = Object.keys(t.urls).length;
          setThumbDiag({
            ok: n > 0,
            msg: n > 0
              ? `${n}/${t.requested} görsel çözüldü` +
                (t.failed.length
                  ? ` · ${t.failed.length} çözülemedi: ${t.failed.slice(0,3).join(', ')}`
                  : '')
              : `Sunucu 0 görsel çözdü (${t.requested} istendi). Sunucu ` +
                `logunda "MM2 thumb" satırlarına bakın — sunucunun ` +
                `thumbnails.roblox.com'a erişimi olmayabilir, ya da ` +
                `/admin/mm2/asset-thumbs ucu yok (eski sunucu).`,
          });
        }
      } else { setThumbs({}); setThumbDiag(null); }
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail, 'Could not load the identity queue'));
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(()=>{ fetchQueue(); }, [fetchQueue]);

  /* Bir kuyruk satırı açıldığında: KAYITLI aile üyeleri dolu gelir,
     üstüne bu deposit'in assetId'sini taşıyan YENİ bir taslak eklenir.
     ⚠️ Mevcut üyelerden biri zaten bu asset'i taşıyorsa yeni taslak
        EKLENMEZ — aksi halde operatör farkında olmadan aynı asset'i iki
        kayda yazmaya çalışır ve sunucu reddeder. */
  const openRow = (row) => {
    if (openKey === row.key) { setOpenKey(null); setDraft([]); return; }
    /* ⚠️⚠️ `|| varsayılan` KALIBI TİP GÜVENLİ DEĞİL.
       🔴 Kapatılan çökme (panel fuzz'ı buldu): `icon_ids: d.icon_ids || []`
       yazılıydı. Alan sözlük/sayı ise `||` onu OLDUĞU GİBİ geçiriyor ve
       hemen aşağıdaki `(e.icon_ids||[]).includes(...)` TypeError fırlatıyor:
       "includes is not a function". React'te fırlatan bileşen TÜM AĞACI
       söker — operatör BEYAZ EKRAN görür ve sebebini bilemez (§5.7).
       ⚠️ Panelin `asArray`/`asRecords` yardımcıları tam bu iş için var ve
          bu dosyanın HER YERİNDE kullanılıyor; yalnızca burası atlanmıştı.
       ⚠️ Sunucu `icon_ids`i artık normalize gönderiyor (mm2_identity_queue),
          ama panel BAĞIMSIZ olarak dayanıklı olmalı: eski sunucu, vekil,
          kısmi yanıt — hiçbirinde ekran düşmemeli. `value` alanında aynı
          gerekçeyle `asInt`e geçilmişti.
       ⚠️ Metin alanlar da `String(...)` ile sabitlenir: sözlük bir `name`
          sunucuya sözlük olarak gider ve 422 üretirdi. */
    const existing = asRecords(row.family).map(d => ({
      id: typeof d.id === 'string' ? d.id : '',
      name: typeof d.name === 'string' ? d.name : '',
      game_name: (typeof d.game_name === 'string' && d.game_name)
                 ? d.game_name
                 : (typeof row.game_name === 'string' ? row.game_name : ''),
      icon_ids: asArray(d.icon_ids).filter(x => typeof x === 'string' || typeof x === 'number')
                                   .map(x => String(x)),
      no_icon: !!d.no_icon,
      /* ⚠️⚠️ `|| 1` KULLANILAMAZ — 0'I VE BOZUK DEĞERİ GİZLER.
         🔴 Kapatılan açık: hem `value` hem `value_seen` `d.value || 1` ile
         okunuyordu. DB'de değer 0 / eksik / "abc" / 1.5 olduğunda panel
         `value_seen: 1` UYDURUYORDU. Sunucu ise gerçek değeri
         `_mm2_safe_int` ile 0 okuyor → 0 ≠ 1 → koşullu yazma HER SEFERİNDE
         409 dönüyor ve panel `allow_value_change` göndermediği için bozuk
         kayıt kimlik ekranından ASLA düzeltilemiyordu. Üstelik 409 mesajı
         "ekran açıkken değişti" diyordu; hiçbir şey değişmemişti.
         İronisi: `_mm2_cas_filter` tam olarak bu kaydı düzeltilebilir
         tutmak için yazılmıştı — istek ona hiç ulaşmıyordu.

         ⚠️ Sunucu artık `value`yu normalize gönderiyor (bkz.
            mm2_identity_queue), ama panel de kendi başına dayanıklı olmalı:
            eski bir sunucuya karşı da doğru davransın. */
      value: asInt(d.value),
      /* ⚠️ EKRANI AÇARKEN GÖRÜLEN FİYAT. Sunucu bunu, operatörün bilerek
         yaptığı değişiklikten "ekran açıkken senkron değiştirdi"yi ayırmak
         için kullanıyor. Gönderilmezse mevcut eşyaların fiyatı hiç
         değiştirilemez (409).
         ⚠️ EKRANDA GÖSTERİLEN DEĞERLE BİREBİR AYNI OLMALI. Uydurulmuş bir
            sayı göndermek, yarış korumasını yanlış soruya cevap verdirir. */
      value_seen: asInt(d.value),
      image_url: typeof d.image_url === 'string' ? d.image_url : '',
      rarity: typeof d.rarity === 'string' && d.rarity ? d.rarity : 'common',
      category: (d.category === 'pets' || d.category === 'weapons') ? d.category
                : (row.category === 'pets' ? 'pets' : 'weapons'),
      kind: ['gun','knife','pet','other'].includes(d.kind) ? d.kind : 'other',
      active: d.active !== false,
      /* ⚠️ MEVCUT SLUG KORUNUR. Bu satır olmadan taslakta tanımsız kalır,
         kaydetmede boş gider ve fiyat eşlemesi SESSİZCE silinir. */
      price_slug: typeof d.price_slug === 'string' ? d.price_slug : '',
      __new: false,
    }));
    const alreadyHasAsset = row.asset &&
      existing.some(e => (e.icon_ids||[]).includes(String(row.asset)));
    const next = [...existing];
    if (row.asset && !alreadyHasAsset) {
      next.push({
        /* ⚠️ `row.game_name` sunucudan geliyor; metin değilse taslak
           bozuk kurulur ve doğrulama anlamsız bir hata verir. */
        id: '',
        name: typeof row.game_name === 'string' ? row.game_name : '',
        game_name: typeof row.game_name === 'string' ? row.game_name : '',
        icon_ids: [String(row.asset)], no_icon: false, value: 1,
        image_url: '', rarity: 'common',
        category: row.category === 'pets' ? 'pets' : 'weapons',
        kind: 'other', active: true,
        /* ⚠️ Yeni kayıtta slug BOŞ: Item ID zaten kaynağın slug'ıyla aynı
           anahtarı üretiyorsa gerekmiyor. Tutmazsa operatör doldurur. */
        price_slug: '',
        __new: true,
      });
    }
    setDraft(next);
    setReason('');
    setOpenKey(row.key);
  };

  const save = async () => {
    /* ⚠️ BOŞ TASLAK SUNUCUYA GİTMEZ. Sunucu modeli en az bir kayıt istiyor
       ve boş gönderim 422 döner — operatöre anlamsız bir hata gösterir.
       Bu durum gerçekten oluşuyor: assetId bildirilmemiş ve ailesi de olmayan
       bir kuyruk satırı açıldığında taslak boş başlar. */
    if (draft.length === 0) {
      toast.error('Add at least one variant before saving'); return; }
    if (reason.trim().length < 3) { toast.error('Write a short reason (audit trail)'); return; }
    for (const e of draft) {
      if (!MM2_ID_RE.test(e.id)) {
        toast.error(`Invalid item id: ${e.id || '(empty)'}`); return; }
      if (!e.name.trim() || !e.game_name.trim()) { toast.error('Name cannot be empty'); return; }
      /* ⚠️ UZUNLUK SINIRI. Eski/legacy bir katalog kaydının adı 64'ü aşabilir
         (model sonradan sınırlandı). Kontrol olmadan operatör satırı açıp
         kaydediyor ve sunucudan ham bir doğrulama hatası alıyordu; neyi
         kısaltması gerektiği hiçbir yerde yazmıyordu. */
      if (e.name.length > MM2_NAME_MAX || e.game_name.length > MM2_NAME_MAX) {
        toast.error(`${e.id}: ad en fazla ${MM2_NAME_MAX} karakter olabilir `
                    + `(şu an ${Math.max(e.name.length, e.game_name.length)}). Kısaltın.`);
        return; }
      /* ⚠️ Katalogdaki değer 0 / bozuk olan kayıtlar artık 0 olarak AÇILIR
         (eskiden `|| 1` ile 1 gösteriliyordu). Bu kasıtlı: bozuk bir fiyata
         "1" deyip kaydetmek, bilinmeyen bir değeri onaylamak olurdu.
         Operatöre ne yapması gerektiği açıkça söylenir. */
      if (!(e.value >= 1)) {
        /* ⚠️ İKİ DURUM AYRILIR. Operatör "fiyat neden geçersiz" diye
           katalogda arayıp boşuna vakit kaybetmesin: değer 0/eksikse sorun
           KATALOGDA, girilen metin sayı değilse sorun GİRDİDE. */
        const seen = asInt(e.value_seen);
        toast.error(seen >= 1
          ? `${e.id}: girilen fiyat geçersiz. Sayı yazın (katalogdaki değer: ${seen}).`
          : `${e.id}: katalogdaki fiyat geçersiz/eksik. Kaynaktan okuyup geçerli bir fiyat girin.`);
        return; }
      /* ⚠️ ÜST SINIR. "1e5" gibi bir girdi `asInt` ile 100000 olur ama "1e20"
         100000000000000000000 olur ve modelin tavanını aşar. Panel sadece
         alt sınıra bakıyordu. */
      if (e.value > MM2_VALUE_MAX) {
        toast.error(`${e.id}: fiyat en fazla ${MM2_VALUE_MAX.toLocaleString('tr-TR')} `
                    + `olabilir (girilen: ${e.value.toLocaleString('tr-TR')}).`);
        return; }
      const _sp = mm2SlugProblem(e.price_slug);
      if (_sp) { toast.error(`${e.id}: kaynak slug'ı geçersiz — ${_sp}.`); return; }
      if (e.no_icon && (e.icon_ids||[]).length) {
        toast.error(`${e.id}: no_icon and asset IDs cannot both be set`); return; }
    }
    /* ⚠️ İSTEMCİDE DE ASSET ÇAKIŞMASI KONTROLÜ. Sunucu zaten reddediyor;
       burada da bakmamızın sebebi operatörün hatayı KAYDET'e basmadan
       görmesi. İstemci kontrolü GÜVENLİK DEĞİL, ergonomidir. */
    const seen = {};
    for (const e of draft) for (const a of (e.icon_ids||[])) {
      if (seen[a] && seen[a] !== e.id) {
        toast.error(`Asset ${a} is assigned to both ${seen[a]} and ${e.id}`); return; }
      seen[a] = e.id;
    }
    setSaving(true);
    try {
      const payload = {
        reason: reason.trim(),
        entries: draft.map(({ __new, ...rest }) => rest),
      };
      //  auto_grant: kimlik kaydedilir kaydedilmez, BU assetId'lerle eslesen
      //  bekleyen kalemler sahiplerine verilir ve kayitlari kapanir.
      //  Sunucu yalnizca assetId ile eslestirir; ada gore ASLA — kuyrugun
      //  var olma sebebi zaten adin belirsiz olmasi.
      const _r = await axios.post(`${API}/admin/mm2/item-identity/save`,
                                  { ...payload, auto_grant: true }, auth);
      const _ag = _r?.data?.auto_grant;
      if (_ag && (_ag.granted > 0 || (_ag.errors || []).length)) {
        const _parts = [`${_ag.granted} kayit verildi (${_ag.items} esya)`];
        if ((_ag.skipped || []).length) _parts.push(`${_ag.skipped.length} atlandi`);
        if ((_ag.errors || []).length) _parts.push(`${_ag.errors.length} hata`);
        if (_ag.truncated) _parts.push('ust sinira takildi — tekrar kaydedin');
        ((_ag.errors || []).length ? toast.error : toast.success)(
          'Katalog guncellendi · ' + _parts.join(' · '));
      } else {
        toast.success('Katalog guncellendi. Eslesen bekleyen kalem yoktu.');
      }
      setOpenKey(null); setDraft([]);
      fetchQueue();
    } catch (e) {
      /* ⚠️ 409 = ekran açıkken fiyat değişti. Sunucu bilerek reddediyor ki
         operatör fiyata dokunmadığını sanırken eski değeri geri yazmasın.
         Doğru davranış: kuyruğu yeniden yükleyip güncel değerle devam etmek. */
      if (e?.response?.status === 409) {
        /* ⚠️ Çok satırlı mesaj: kısmi uygulama uyarısı ayrı satırda görünsün. */
        toast.error(toastDetail(e?.response?.data?.detail,
                                'Price changed — reloading'),
                    { duration: 8000 });
        setOpenKey(null); setDraft([]);
        fetchQueue();
      } else {
        toast.error(detailText(e?.response?.data?.detail, 'Save failed'));
      }
    } finally { setSaving(false); }
  };

  const collisionList = Object.entries(collisions || {});

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-fuchsia-400"/> MM2 Item Identity
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Deposits land here when the bot reports an asset ID the catalog doesn't know.
            Nothing is credited until you grant the items separately.
          </p>
        </div>
        <button onClick={fetchQueue} disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-white">
          <RefreshCw className={`w-4 h-4 ${loading && 'animate-spin'}`}/> Refresh
        </button>
      </div>

      {/* ⚠️ Katalogdaki asset çakışmaları. Okuma tarafı bunları düşürüyor ve
          o eşyalar SESSİZCE kredilenemez hale geliyor — en üstte gösterilir. */}
      {collisionList.length > 0 && (
        <div className="rounded-xl border border-red-800/60 bg-red-950/30 p-3">
          <div className="flex items-center gap-2 text-red-300 font-semibold text-sm">
            <AlertTriangle className="w-4 h-4"/> Asset ID collisions in the catalog
          </div>
          <p className="text-xs text-red-200/70 mt-1">
            Each asset ID must belong to exactly one item. While these clash, deposits
            of those items cannot be credited.
          </p>
          <div className="mt-2 space-y-1">
            {collisionList.map(([asset, ids]) => (
              <div key={asset} className="flex items-center gap-2 text-xs font-mono text-red-200">
                <AssetThumb assetId={asset} url={thumbs[asset]} size={28}/>
                <span>{asset}</span><ArrowRight className="w-3 h-3"/>
                <span>{(ids||[]).join(', ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ⚠️ Görsel çözümünün sonucu HER ZAMAN görünür. Sessiz başarısızlık,
             "kod çalışmıyor" ile "sunucu Roblox'a çıkamıyor"u ayırt
             edilemez hale getiriyordu. */}
      {thumbDiag && (
        <div className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${
          thumbDiag.ok ? 'border-slate-700 bg-slate-900/40 text-slate-400'
                       : 'border-amber-700/60 bg-amber-950/20 text-amber-200'}`}>
          {thumbDiag.ok ? <Info className="w-4 h-4 shrink-0 mt-0.5"/>
                        : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>}
          <span>Görsel: {thumbDiag.msg}</span>
        </div>
      )}

      {/* ⚠️ Kimlik çözüldü ama deposit hâlâ bekliyor: eşya kullanıcıya
             VERİLMEDİ. Bu satır olmasa operatör işi bitmiş sanırdı. */}
      {resolvedPending.length > 0 && (
        <div className="rounded-lg border border-sky-800/60 bg-sky-950/20 px-3 py-2 text-xs text-sky-200 flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5"/>
          <span>
            <b>{pendingTotal}</b> bekleyen kayıt artık tanınıyor ama
            eşyalar <b>henüz kullanıcıya verilmedi</b>. MM2 Bots sayfasından
            <span className="font-mono"> inventory/grant</span> ile verip
            kaydı kapatın.
          </span>
        </div>
      )}

      {/* ⚠️ Kırpılmış liste TAM sanılmasın: görünmeyen kayıtlar hiç
             düzeltilmez ve o kullanıcıların deposit'i bekler. */}
      {queueTruncated && (
        <div className="rounded-lg border border-amber-700/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
          <span>
            Bekleyen kayıt sayısı üst sınırı aştı — bu liste <b>eksik</b>.
            Görünenleri çözdükçe kalanlar gelir.
          </span>
        </div>
      )}

      {loading && queue.length === 0 && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin"/>
        </div>
      )}

      {!loading && queue.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-10 text-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2"/>
          <p className="text-slate-300 font-medium">No unidentified items</p>
          <p className="text-xs text-slate-500 mt-1">Every deposited asset ID is mapped to a catalog item.</p>
        </div>
      )}

      <div className="space-y-3">
        {queue.map(row => {
          const open = openKey === row.key;
            const ambiguous = asRecords(row.family).length > 1;
          return (
            <div key={row.key} className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden">
              <button onClick={()=>openRow(row)}
                className="w-full flex items-center gap-3 p-3 hover:bg-slate-800/40 text-left">
                <AssetThumb assetId={row.asset} url={thumbs[row.asset]} size={48}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white font-semibold">{row.game_name}</span>
                    {ambiguous && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-600/40">
                        {asRecords(row.family).length} variants share this name
                      </span>
                    )}
                    {!row.asset && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-700/40 text-slate-300 border border-slate-600">
                        no asset reported
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
                    {row.asset ? (
                      <>
                        {/* ⚠️ assetId SEÇİLEBİLİR ve Roblox'ta AÇILABİLİR olmalı.
                            Görsel yüklenmese bile operatör hangi eşya olduğunu
                            böyle doğrulayabilir — ekranda okunamayan bir numara
                            işe yaramıyor. */}
                        <span className="select-all">{row.asset}</span>
                        <a
                          href={`https://www.roblox.com/library/${encodeURIComponent(row.asset)}`}
                          target="_blank" rel="noreferrer noopener"
                          onClick={(e)=>e.stopPropagation()}
                          className="text-sky-400 hover:text-sky-300 inline-flex items-center gap-0.5"
                          title="Roblox'ta aç — hangi eşya olduğunu gör">
                          aç <ExternalLink className="w-3 h-3"/>
                        </a>
                      </>
                    ) : <span className="text-slate-600">assetId yok</span>}
                    {row.category ? <span>· {row.category}</span> : null}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm text-white font-semibold">{row.deposits}</div>
                  <div className="text-[10px] text-slate-500">deposits · {row.users} user(s)</div>
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-slate-500"/> : <ChevronDown className="w-4 h-4 text-slate-500"/>}
              </button>

              <AnimatePresence>
                {open && (
                  <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}}
                    exit={{height:0,opacity:0}} className="border-t border-slate-800">
                    <div className="p-3 space-y-3">
                      <p className="text-xs text-slate-400">
                        Every catalog item sharing the in-game name <span className="text-white font-mono">{row.game_name}</span> is
                        listed below, already filled in. Give each variant a distinct site
                        name (for example <span className="font-mono">{row.game_name} (Gun)</span>) and make
                        sure each asset ID appears exactly once.
                      </p>

                      {draft.map((e, i) => (
                        <IdentityRow key={i} entry={e} isNew={e.__new} thumbs={thumbs}
                          onChange={(v)=>setDraft(d => d.map((x,ix)=> ix===i ? { ...v, __new:x.__new } : x))}
                          onRemove={()=>setDraft(d => d.filter((_,ix)=>ix!==i))}/>
                      ))}

                      {draft.length < 8 && (
                        <button onClick={()=>setDraft(d=>[...d, {
                            id:'', name:row.game_name, game_name:row.game_name,
                            icon_ids:[], no_icon:false, value:1, image_url:'',
                            rarity:'common',
                            category: row.category==='pets' ? 'pets' : 'weapons',
                            kind:'other', active:true, price_slug:'', __new:true }])}
                          className="flex items-center gap-1.5 text-xs text-sky-400 hover:text-sky-300">
                          <Plus className="w-3.5 h-3.5"/> Add another variant
                        </button>
                      )}

                      <div>
                        <label className="text-[10px] uppercase tracking-wide text-slate-500">Reason (audit)</label>
                        <input value={reason} onChange={e=>setReason(e.target.value)}
                          placeholder="Split Starfish into gun and knife variants"
                          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white"/>
                      </div>

                      {/* ⚠️ Operatör bir sonraki adımı unutmasın: bu ekran
                          KREDİ YAZMAZ. Karantina kaydı ayrıca kapatılmalı. */}
                      <div className="rounded-lg border border-sky-800/50 bg-sky-950/20 p-2.5 text-xs text-sky-200/80 flex gap-2">
                        <Info className="w-4 h-4 shrink-0 mt-0.5"/>
                        <span>
                          Saving only updates the catalog. The {row.deposits} affected deposit(s)
                          stay pending — grant the items on the MM2 Bots page, then close the
                          pending record.
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button onClick={save} disabled={saving || viewerRank < 4}
                          title={viewerRank < 4 ? 'Owner rank required' : ''}
                          className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-sm text-white font-medium">
                          {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Check className="w-4 h-4"/>}
                          Save catalog
                        </button>
                        <button onClick={()=>{setOpenKey(null); setDraft([]);}}
                          className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-sm text-slate-300">
                          Cancel
                        </button>
                        {viewerRank < 4 && (
                          <span className="text-xs text-amber-400">Owner rank required to write the catalog</span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

/* ════════════════════════════════════════════════════════════
   DASHBOARD VIEW (existing + audit log stats widgets)
   ════════════════════════════════════════════════════════════ */
const DashboardView = ({ setPage }) => {
  const { token, user } = useAuth();
  const viewerRank = getUserRank(user);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    try {
      const reqs = [
        axios.get(`${API}/admin/logs/stats`, { headers:{Authorization:`Bearer ${token}`} }).catch(()=>null),
      ];
      if (viewerRank >= 3) {
        // ⚠️ `/admin/manual_review_queue` SUNUCUDA YOK. Çağrı `.catch(()=>null)`
        // ile yutulduğu için pano sessizce "0 bekleyen" gösteriyordu — admin
        // hiç iş olmadığını sanırken arkada tutulan yatırımlar birikiyordu.
        // Hatanın yutulması, hatanın kendisinden daha tehlikeliydi.
        reqs.push(axios.get(`${API}/admin/mm2/trades/review`, { headers:{Authorization:`Bearer ${token}`} }).catch(()=>null));
        reqs.push(axios.get(`${API}/admin/suspicious_events`,   { headers:{Authorization:`Bearer ${token}`} }).catch(()=>null));
      }
      const [sr, rr, susp] = await Promise.all(reqs);
      setStats(sr?.data || null);
      if (rr) {
        setData({
          // Tutulan yatırımlar VE bekleyen müsadereler birlikte sayılır:
          // ikisi de admin kararı bekliyor, ikisinde de para askıda.
          pending:   (rr.data.count??0) + (rr.data.forfeit_count??0),
          reviews:   (rr.data.reviews??[]).slice(0,5),
          suspicious:(susp?.data.items??[]).filter(e=>(Date.now()-new Date(e.created_at).getTime())<864e5).length,
          events:    (susp?.data.items??[]).filter(e=>!e.reviewed).slice(0,5),
        });
      }
    } catch { toast.error('Failed to load dashboard.'); }
    finally { setLoading(false); }
  }, [token, viewerRank]);

  useEffect(()=>{fetch();},[fetch]);

  const totals = stats?.totals || {};

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <button onClick={()=>{setLoading(true);fetch();}} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-zinc-400 hover:text-white transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      {loading ? <LoadingBlock/> : (
        <>
          {/* AUDIT LOG STATS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard
              icon={AlertTriangle}
              label="Critical (24h)"
              value={totals.critical_24h ?? 0}
              accent="red"
              onClick={()=>setPage('audit-logs')}/>
            <StatCard
              icon={Activity}
              label="Total (30d)"
              value={totals.total_30d ?? 0}
              accent="blue"
              onClick={()=>setPage('audit-logs')}/>
            <StatCard
              icon={LogIn}
              label="Failed Logins (24h)"
              value={totals.failed_logins_24h ?? 0}
              accent="amber"
              onClick={()=>setPage('audit-logs')}/>
            <StatCard
              icon={Ban}
              label="Banned Token Attempts (24h)"
              value={totals.banned_token_attempts_24h ?? 0}
              accent="red"
              onClick={()=>setPage('audit-logs')}/>
          </div>

          {/* TOP ACTIONS + CATEGORY BREAKDOWN */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-zinc-300">Top Actions (7d)</h2>
                <button onClick={()=>setPage('audit-logs')} className="text-xs text-zinc-600 hover:text-white flex items-center gap-1">Browse<ChevronRight className="w-3 h-3"/></button>
              </div>
              {!stats?.top_actions_7d?.length ? <EmptyState icon={Info} title="No events yet" sub="Top action stats appear after 7 days of activity."/> : (
                <div className="space-y-1">
                  {stats.top_actions_7d.slice(0,8).map((a,i)=>{
                    const maxCount = stats.top_actions_7d[0]?.count || 1;
                    const pct = (a.count / maxCount) * 100;
                    return (
                      <div key={i} className="flex items-center gap-3 py-1">
                        <span className="text-xs text-zinc-500 w-5 shrink-0">{i+1}</span>
                        <span className="text-xs text-zinc-300 truncate flex-1">{formatAction(a._id)}</span>
                        <div className="w-20 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500/50 rounded-full" style={{width:`${pct}%`}}/>
                        </div>
                        <span className="text-xs font-bold text-white w-10 text-right">{fmt(a.count)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-zinc-300">Categories (30d)</h2>
                <span className="text-xs text-zinc-600">{fmt(totals.total_30d||0)} total</span>
              </div>
              {!stats?.by_category_30d?.length ? <EmptyState icon={Info} title="No data" sub="Run a few actions to populate."/> : (
                <div className="space-y-1.5">
                  {(() => {
                    // Aggregate by category, summing severities
                    const agg = {};
                    stats.by_category_30d.forEach(({_id,count}) => {
                      const cat = _id.category;
                      if (!agg[cat]) agg[cat] = { total:0, info:0, warning:0, critical:0 };
                      agg[cat].total += count;
                      agg[cat][_id.severity] = (agg[cat][_id.severity]||0) + count;
                    });
                    const sorted = Object.entries(agg).sort(([,a],[,b])=>b.total-a.total);
                    const max = sorted[0]?.[1]?.total || 1;
                    return sorted.map(([cat,d]) => {
                      const cfg = CATEGORY_CFG[cat] || { icon:Hash, color:'text-zinc-500', label:cat };
                      const Icon = cfg.icon;
                      return (
                        <div key={cat} className="flex items-center gap-3 py-1">
                          <Icon className={`w-3.5 h-3.5 shrink-0 ${cfg.color}`}/>
                          <span className="text-xs text-zinc-300 truncate flex-1">{cfg.label}</span>
                          <div className="flex items-center gap-0.5 h-1.5 w-24 rounded-full overflow-hidden bg-white/5">
                            {d.critical>0 && <div className="h-full bg-red-500" style={{width:`${(d.critical/d.total)*100}%`}}/>}
                            {d.warning>0  && <div className="h-full bg-yellow-500" style={{width:`${(d.warning/d.total)*100}%`}}/>}
                            {d.info>0     && <div className="h-full bg-blue-500" style={{width:`${(d.info/d.total)*100}%`}}/>}
                          </div>
                          <span className="text-xs font-bold text-white w-12 text-right">{fmt(d.total)}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* ADMIN-ONLY SECTIONS */}
          {viewerRank >= 3 && data && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                  {label:'Pending Reviews',  val:data?.pending??0,    color:'text-yellow-400',  bg:'bg-yellow-500/10 border-yellow-500/20', page:'manual-review'},
                  {label:'Suspicious (24h)', val:data?.suspicious??0, color:'text-orange-400',  bg:'bg-orange-500/10 border-orange-500/20', page:'suspicious-events'},
                ].map(({label,val,color,page:p})=>(
                  <button key={label} onClick={()=>setPage(p)}
                    className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 text-left hover:border-white/20 transition-colors">
                    <div className="text-xs text-zinc-500 mb-2">{label}</div>
                    <div className={`text-3xl font-bold ${color}`}>{val}</div>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {[
                  {title:'Recent Reviews', items:data?.reviews, page:'manual-review', renderItem:item=>(
                    <div className="flex justify-between items-center py-2.5 border-b border-[#2A2D31]/50 last:border-0">
                      <div><div className="font-bold">{item.user_ign} → {item.matched_payee_ign}</div><div className="text-xs text-zinc-600">{relTime(item.created_at)}</div></div>
                      <div className="text-emerald-400 font-bold">{fmtD(item.amount)}</div>
                    </div>
                  )},
                  {title:'Unreviewed Anomalies', items:data?.events, page:'suspicious-events', renderItem:ev=>(
                    <div className="flex justify-between items-center py-2.5 border-b border-[#2A2D31]/50 last:border-0">
                      <div><div className="font-bold">{ev.user_ign||ev.user_id}</div><div className="text-xs text-zinc-600">{ev.type?.replace(/_/g,' ')} · {relTime(ev.created_at)}</div></div>
                      <div className="text-orange-400 font-bold">{fmtD(ev.amount)}</div>
                    </div>
                  )},
                ].map(({title,items,page:p,renderItem})=>(
                  <div key={title} className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5">
                    <div className="flex justify-between items-center mb-3">
                      <h2 className="font-bold text-zinc-300">{title}</h2>
                      <button onClick={()=>setPage(p)} className="text-xs text-zinc-600 hover:text-white flex items-center gap-1">View all<ChevronRight className="w-3 h-3"/></button>
                    </div>
                    {!items?.length ? <p className="text-zinc-600 text-sm text-center py-4">Nothing to show.</p> : items.map((item,i)=><div key={i}>{renderItem(item)}</div>)}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </motion.div>
  );
};

const StatCard = ({ icon:Icon, label, value, accent='blue', onClick }) => {
  const map = {
    red:     { c:'text-red-400',     b:'border-red-500/20 hover:border-red-500/40' },
    blue:    { c:'text-blue-400',    b:'border-blue-500/20 hover:border-blue-500/40' },
    amber:   { c:'text-amber-400',   b:'border-amber-500/20 hover:border-amber-500/40' },
    emerald: { c:'text-emerald-400', b:'border-emerald-500/20 hover:border-emerald-500/40' },
  };
  const m = map[accent] || map.blue;
  return (
    <button onClick={onClick}
      className={`bg-[#121417] border rounded-2xl p-4 text-left transition-colors ${m.b}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-500 truncate">{label}</span>
        <Icon className={`w-4 h-4 ${m.c}`}/>
      </div>
      <div className={`text-2xl font-bold ${m.c}`}>{fmt(value)}</div>
    </button>
  );
};

/* ════════════════════════════════════════════════════════════
   AUDIT LOGS VIEW (new)
   ════════════════════════════════════════════════════════════ */
const AuditLogsView = ({ goToUser, goToIdentity }) => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [meta, setMeta] = useState({ hidden:0 });
  const [showFilters, setShowFilters] = useState(false);
  const [categoriesMeta, setCategoriesMeta] = useState({ categories:[], severities:['info','warning','critical'], actions:[] });

  // Filters
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [category, setCategory] = useState('');
  const [severity, setSeverity] = useState('');
  const [action, setAction] = useState('');
  const [ip, setIp] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Load category/action dropdown data once
  useEffect(()=>{
    axios.get(`${API}/admin/logs/categories`, { headers:{Authorization:`Bearer ${token}`} })
      .then(r => setCategoriesMeta(r.data))
      .catch(()=>{});
  }, [token]);

  const buildParams = (cursor=null) => {
    const p = { page_size: 30 };
    if (cursor) p.before_cursor = cursor;
    if (q) p.q = q;
    if (category) p.category = category;
    if (severity) p.severity = severity;
    if (action) p.action = action;
    if (ip) p.ip = ip;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  };

  const load = useCallback(async (cursor=null, append=false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/logs`, {
        headers:{Authorization:`Bearer ${token}`},
        params: buildParams(cursor),
      });
      const newEvents = res.data.events || [];
      setEvents(prev => append ? [...prev, ...newEvents] : newEvents);
      setNextCursor(res.data.next_cursor);
      setMeta({ hidden: res.data.hidden_by_hierarchy || 0 });
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Failed to load logs.');
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, q, category, severity, action, ip, dateFrom, dateTo]);

  useEffect(()=>{ load(null, false); }, [load]);

  const onSearch = (e) => {
    e?.preventDefault();
    setQ(qDraft.trim());
  };

  const reset = () => {
    setQ(''); setQDraft('');
    setCategory(''); setSeverity(''); setAction(''); setIp('');
    setDateFrom(''); setDateTo('');
  };

  const hasActiveFilters = !!(q||category||severity||action||ip||dateFrom||dateTo);

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold">Audit Logs</h1>
          <p className="text-zinc-500 text-sm">Searchable, hierarchy-filtered event timeline.</p>
        </div>
        <button onClick={()=>load(null,false)} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-zinc-400 hover:text-white transition-colors">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/>
        </button>
      </div>

      {/* SEARCH BAR */}
      <form onSubmit={onSearch} className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600"/>
          <input
            value={qDraft} onChange={e=>setQDraft(e.target.value)}
            placeholder="Search by username, email, Roblox, user ID…"
            className="w-full pl-9 pr-3 py-2.5 bg-[#121417] border border-[#2A2D31] focus:border-blue-500 rounded-xl text-white text-sm outline-none transition-colors"/>
        </div>
        <button type="submit" className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-xl text-white font-bold text-sm">Search</button>
        <button type="button" onClick={()=>setShowFilters(v=>!v)}
          className={`px-3 py-2.5 rounded-xl text-sm font-bold flex items-center gap-1.5 transition-colors ${showFilters||hasActiveFilters?'bg-red-500/15 text-red-300 border border-red-500/30':'bg-white/5 text-zinc-400 hover:text-white border border-transparent'}`}>
          <Filter className="w-4 h-4"/>
          {hasActiveFilters && <span className="w-1.5 h-1.5 bg-red-400 rounded-full"/>}
        </button>
      </form>

      {/* FILTER PANEL */}
      <AnimatePresence>
        {showFilters && (
          <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}}
            className="overflow-hidden mb-3">
            <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <FilterField label="Category">
                <select value={category} onChange={e=>setCategory(e.target.value)} className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg text-white text-sm outline-none">
                  <option value="">All</option>
                  {categoriesMeta.categories.map(c=><option key={c} value={c}>{formatAction(c)}</option>)}
                </select>
              </FilterField>
              <FilterField label="Severity">
                <select value={severity} onChange={e=>setSeverity(e.target.value)} className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg text-white text-sm outline-none">
                  <option value="">All</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
              </FilterField>
              <FilterField label="Action">
                <select value={action} onChange={e=>setAction(e.target.value)} className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg text-white text-sm outline-none">
                  <option value="">All</option>
                  {categoriesMeta.actions.map(a=><option key={a} value={a}>{formatAction(a)}</option>)}
                </select>
              </FilterField>
              <FilterField label="IP">
                <input value={ip} onChange={e=>setIp(e.target.value)} placeholder="1.2.3.4"
                  className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg text-white text-sm outline-none mm-num"/>
              </FilterField>
              <FilterField label="From">
                <input type="datetime-local" value={dateFrom} onChange={e=>setDateFrom(e.target.value? new Date(e.target.value).toISOString() : '')}
                  className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg text-white text-sm outline-none"/>
              </FilterField>
              <FilterField label="To">
                <input type="datetime-local" value={dateTo} onChange={e=>setDateTo(e.target.value? new Date(e.target.value).toISOString() : '')}
                  className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] rounded-lg text-white text-sm outline-none"/>
              </FilterField>
              <div className="col-span-full flex justify-end">
                <button onClick={reset} className="px-3 py-1.5 text-xs text-zinc-500 hover:text-red-400 transition-colors">Clear filters</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* RESULTS */}
      {loading ? <LoadingBlock/> : events.length === 0 ? (
        <EmptyState icon={FileText} title="No events match your filters" sub={hasActiveFilters ? 'Try clearing filters.' : 'Logs will appear here as activity occurs.'}/>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-xs text-zinc-600">{events.length} event{events.length===1?'':'s'}</span>
            {meta.hidden > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
                <Shield className="w-3 h-3"/>{meta.hidden} hidden by rank
              </span>
            )}
          </div>
          <div className="space-y-2">
            {events.map((ev,i)=>(
              <AuditEventCard key={ev.id||`${ev.timestamp}-${i}`}
                event={ev} delay={Math.min(i*0.02, 0.3)}
                onUserClick={goToUser} onIdentityClick={goToIdentity}/>
            ))}
          </div>
          {nextCursor && (
            <div className="mt-4 flex justify-center">
              <button onClick={()=>load(nextCursor, true)} disabled={loadingMore}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 disabled:opacity-40 rounded-xl text-sm font-bold text-zinc-300 transition-colors flex items-center gap-2">
                {loadingMore ? <Loader2 className="w-4 h-4 animate-spin"/> : <ChevronDown className="w-4 h-4"/>}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

const FilterField = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-zinc-600 mb-1 block">{label}</label>
    {children}
  </div>
);

/* ════════════════════════════════════════════════════════════
   FILTER PILL — multi-select toggle button
   ════════════════════════════════════════════════════════════ */
const FilterPill = ({ active, onClick, color='zinc', icon:Icon, count, children, size='sm' }) => {
  const c = PILL_COLORS[color] || PILL_COLORS.zinc;
  const sizeCls = size === 'xs' ? 'px-2 py-1 text-[10px] gap-1' : 'px-2.5 py-1.5 text-xs gap-1.5';
  return (
    <button onClick={onClick}
      className={`inline-flex items-center font-bold border rounded-lg transition-colors ${sizeCls} ${
        active ? c.active : 'bg-white/5 text-zinc-400 border-transparent hover:bg-white/10 hover:text-white'
      }`}>
      {Icon && <Icon className="w-3 h-3"/>}
      <span>{children}</span>
      {count !== undefined && count !== null && (
        <span className={`text-[9px] opacity-60 ml-0.5`}>{count}</span>
      )}
    </button>
  );
};

/* ════════════════════════════════════════════════════════════
   USER LOOKUP VIEW — search, activity, action panel, granular filters
   ════════════════════════════════════════════════════════════ */
const ACTIVITY_FILTER_DEFAULTS = {
  // global
  minAmount: '',
  maxAmount: '',
  dateFrom: '',
  dateTo: '',
  // audit (multi-select)
  auditCategories: [],
  auditSeverities: [],
  auditActionQuery: '',
  // balance (multi-select semantic groups + direction)
  balanceCategories: [],
  balanceDirection: 'all', // 'all' | 'credit' | 'debit'
  // games (multi-select)
  games: [],
  gameOutcome: 'all', // 'all' | 'win' | 'loss'
  // transactions (multi-select)
  txTypes: [],
  txStatuses: [],
  // chat
  chatQuery: '',
};

const UserLookupView = ({ viewer, prefilled, onConsumed, goToIdentity }) => {
  const { token } = useAuth();
  const viewerRank = getUserRank(viewer);

  const [identifier, setIdentifier] = useState('');
  const [activeIdentifier, setActiveIdentifier] = useState('');
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [linked, setLinked] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(ACTIVITY_FILTER_DEFAULTS);

  const lookup = useCallback(async (id) => {
    if (!id || !id.trim()) return;
    setLoading(true);
    setData(null); setStats(null); setLinked(null);
    setActiveIdentifier(id.trim());
    const h = { headers:{Authorization:`Bearer ${token}`} };
    const enc = encodeURIComponent(id.trim());
    try {
      // Activity is required — others are best-effort (don't fail the page if they error)
      const [activityRes, statsRes, linkedRes] = await Promise.allSettled([
        axios.get(`${API}/admin/logs/user/${enc}`,         h),
        axios.get(`${API}/admin/logs/user/${enc}/stats`,   h),
        axios.get(`${API}/admin/logs/user/${enc}/linked`,  h),
      ]);
      if (activityRes.status === 'fulfilled') {
        setData(activityRes.value.data);
      } else {
        const err = activityRes.reason;
        if (err.response?.status === 404) toast.error('User not found.');
        else toast.error(err.response?.data?.detail || 'Lookup failed.');
        setLoading(false);
        return;
      }
      if (statsRes.status  === 'fulfilled') setStats(statsRes.value.data);
      if (linkedRes.status === 'fulfilled') setLinked(linkedRes.value.data);
    } finally { setLoading(false); }
  }, [token]);

  // Consume prefilled identifier from other section
  useEffect(()=>{
    if (prefilled) {
      setIdentifier(prefilled);
      lookup(prefilled);
      onConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilled]);

  const onSubmit = (e) => {
    e?.preventDefault();
    lookup(identifier);
  };

  const refreshAfterAction = () => lookup(activeIdentifier);

  const user = data?.user;
  const targetRank = user ? getUserRank(user) : 0;
  const canActOnTarget = user && (viewerRank > targetRank);
  const canBalanceAdjust = viewerRank === 4;

  // Per-source counts based on FILTERED data (so pill numbers update with filters)
  // For per-source counts, we compute filtered count per source for the pill
  // Discover audit categories present (for compact UI)
  const presentAuditCategories = useMemo(() => {
    const s = new Set();
    if (data?.activity) data.activity.forEach(it => {
      if (it.source === 'audit_log' && it.data?.category) s.add(it.data.category);
    });
    return [...s];
  }, [data]);

  // All distinct game names actually present in this user's data
  const presentGames = useMemo(() => {
    const s = new Set();
    if (data?.activity) data.activity.forEach(it => {
      if (it.source === 'game_history') {
        const g = getGameName(it.data);
        if (g) s.add(g);
      }
    });
    return [...s].sort();
  }, [data]);

  // Game names in data that DON'T match any known GAME_TYPES key — surface them as ad-hoc pills
  const unknownGames = useMemo(() => {
    return presentGames.filter(g =>
      !GAME_TYPES.some(t => matchGameType(g, t.key))
    );
  }, [presentGames]);

  // Per-game count in data (for showing on pills)
  const gameCountsInData = useMemo(() => {
    const counts = {};
    if (data?.activity) data.activity.forEach(it => {
      if (it.source === 'game_history') {
        const game = getGameName(it.data);
        if (!game) return;
        // Match to a known type if possible, else use raw name
        let key = GAME_TYPES.find(t => matchGameType(game, t.key))?.key || game;
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [data]);

  // Helper: check whether a single activity item passes all currently-active filters
  // EXCEPT the source filter (so we can compute per-source counts)
  const passesAllFiltersExceptSource = useCallback((item) => {
    const d = item.data || {};
    const min = filters.minAmount !== '' ? parseFloat(filters.minAmount) : null;
    const max = filters.maxAmount !== '' ? parseFloat(filters.maxAmount) : null;
    const dateFromMs = filters.dateFrom ? new Date(filters.dateFrom).getTime() : null;
    const dateToMs   = filters.dateTo   ? new Date(filters.dateTo).getTime()   : null;
    const chatQ      = filters.chatQuery ? filters.chatQuery.toLowerCase() : '';
    const auditQ     = filters.auditActionQuery ? filters.auditActionQuery.toLowerCase() : '';

    // Date — use multi-fallback timestamp resolver
    if (dateFromMs !== null || dateToMs !== null) {
      const ts = getItemTimestamp(item);
      const tsMs = ts ? new Date(ts).getTime() : 0;
      if (dateFromMs !== null && tsMs < dateFromMs) return false;
      if (dateToMs   !== null && tsMs > dateToMs)   return false;
    }

    // Amount
    if (min !== null || max !== null) {
      let amount = null;
      if (item.source === 'balance_log')  amount = getBalanceAmount(d);
      if (item.source === 'game_history') amount = getBetAmount(d);
      if (item.source === 'transaction')  amount = getTxAmount(d);
      if (amount !== null) {
        if (min !== null && amount < min) return false;
        if (max !== null && amount > max) return false;
      }
    }

    // Audit
    if (item.source === 'audit_log') {
      if (filters.auditCategories.length > 0 && !filters.auditCategories.includes(getAuditCategory(d))) return false;
      if (filters.auditSeverities.length > 0 && !filters.auditSeverities.includes(getAuditSeverity(d))) return false;
      if (auditQ && !((getAuditAction(d) || '').toLowerCase().includes(auditQ))) return false;
    }

    // Balance
    if (item.source === 'balance_log') {
      const action = getBalanceAction(d);
      if (filters.balanceCategories.length > 0) {
        const matched = filters.balanceCategories.some(catKey => matchBalanceCategory(action, catKey));
        if (!matched) return false;
      }
      if (filters.balanceDirection !== 'all') {
        const delta = getBalanceSignedDelta(d);
        if (filters.balanceDirection === 'credit' && delta <= 0) return false;
        if (filters.balanceDirection === 'debit'  && delta >= 0) return false;
      }
    }

    // Games — use getGameName + getGameWin (handles game_type/game and result/win)
    if (item.source === 'game_history') {
      const storedGame = getGameName(d);
      if (filters.games.length > 0) {
        const matched = filters.games.some(k => matchGameType(storedGame, k) || storedGame === k);
        if (!matched) return false;
      }
      if (filters.gameOutcome !== 'all') {
        const isWin = getGameWin(d);
        if (filters.gameOutcome === 'win'  && isWin !== true)  return false;
        if (filters.gameOutcome === 'loss' && isWin !== false) return false;
      }
    }

    // Transactions
    if (item.source === 'transaction') {
      if (filters.txTypes.length    > 0 && !filters.txTypes.includes(getTxType(d)))     return false;
      if (filters.txStatuses.length > 0 && !filters.txStatuses.includes(getTxStatus(d))) return false;
    }

    // Chat
    if (item.source === 'chat_message') {
      if (chatQ && !getChatMessage(d).toLowerCase().includes(chatQ)) return false;
    }

    return true;
  }, [filters]);

  // Filtered activity (source filter + all other filters)
  const filteredActivity = useMemo(() => {
    if (!data?.activity) return [];
    return data.activity.filter(item => {
      if (sourceFilter !== 'all' && item.source !== sourceFilter) return false;
      return passesAllFiltersExceptSource(item);
    });
  }, [data, sourceFilter, passesAllFiltersExceptSource]);

  // Filtered counts per source (for source pills)
  const filteredCounts = useMemo(() => {
    const counts = { all: 0, audit_log: 0, balance_log: 0, game_history: 0, transaction: 0, chat_message: 0 };
    if (!data?.activity) return counts;
    for (const it of data.activity) {
      if (!passesAllFiltersExceptSource(it)) continue;
      counts.all++;
      counts[it.source] = (counts[it.source] || 0) + 1;
    }
    return counts;
  }, [data, passesAllFiltersExceptSource]);

  // Any active filter?
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.minAmount !== '')           n++;
    if (filters.maxAmount !== '')           n++;
    if (filters.dateFrom)                   n++;
    if (filters.dateTo)                     n++;
    if (filters.auditCategories.length)     n++;
    if (filters.auditSeverities.length)     n++;
    if (filters.auditActionQuery)           n++;
    if (filters.balanceCategories.length)   n++;
    if (filters.balanceDirection !== 'all') n++;
    if (filters.games.length)               n++;
    if (filters.gameOutcome !== 'all')      n++;
    if (filters.txTypes.length)             n++;
    if (filters.txStatuses.length)          n++;
    if (filters.chatQuery)                  n++;
    return n;
  }, [filters]);

  const resetFilters = () => setFilters(ACTIVITY_FILTER_DEFAULTS);
  const setF = (patch) => setFilters(f => ({ ...f, ...patch }));

  // Generic toggle for array filters
  const toggleArr = (key, val) => setFilters(f => ({
    ...f,
    [key]: f[key].includes(val) ? f[key].filter(x => x !== val) : [...f[key], val],
  }));

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold">User Lookup</h1>
          <p className="text-zinc-500 text-sm">Search by user ID, email, username, or Roblox username.</p>
        </div>
        {activeIdentifier && <button onClick={refreshAfterAction} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-zinc-400 hover:text-white">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/>
        </button>}
      </div>

      <form onSubmit={onSubmit} className="flex gap-2 mb-5">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600"/>
          <input value={identifier} onChange={e=>setIdentifier(e.target.value)}
            placeholder="user-id / email / username / Roblox"
            className="w-full pl-9 pr-3 py-2.5 bg-[#121417] border border-[#2A2D31] focus:border-blue-500 rounded-xl text-white text-sm outline-none transition-colors"/>
        </div>
        <button type="submit" disabled={loading} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-xl text-white font-bold text-sm">
          {loading ? <Loader2 className="animate-spin w-4 h-4"/> : 'Lookup'}
        </button>
      </form>

      {loading && <LoadingBlock/>}

      {!loading && !data && !activeIdentifier && (
        <EmptyState icon={UserIcon} title="No user selected" sub="Enter an identifier above to investigate a user."/>
      )}

      {!loading && data && user && (
        <>
          {/* USER HEADER */}
          <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 mb-4">
            <div className="flex flex-wrap items-start gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h2 className="text-xl font-bold text-white truncate">{user.site_username || user.roblox_username || user.id}</h2>
                  <RankBadge rank={getRankLabel(user)}/>
                  {user.is_banned && <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30"><Ban className="w-3 h-3"/>BANNED</span>}
                  {user.muted_until && new Date(user.muted_until) > new Date() && <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30"><VolumeX className="w-3 h-3"/>MUTED</span>}
                  {user.is_vip && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/30">VIP</span>}
                  {user.is_youtuber && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">YouTuber</span>}
                  {user.is_streamer && <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/30">Streamer</span>}
                  {user.is_tiktoker && <span className="text-xs px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-400 border border-pink-500/30">TikToker</span>}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <InfoCell icon={Hash} label="User ID" value={user.id} mono/>
                  <InfoCell icon={AtSign} label="Email" value={user.email}/>
                  <InfoCell icon={UserIcon} label="Roblox" value={user.roblox_username}/>
                  {/* ⚠️ Backend `balance` alanı döndürüyor; eski arayüz
                      `site_balance` okuyordu. İkisi de destekleniyor. */}
                  <InfoCell icon={Coins} label="Robux" value={fmtD(user.site_balance ?? user.balance)} accent="emerald"/>
                  {/* 🍀 GERÇEK PARA bakiyesi — dondurma/iade kararı bunsuz verilmemeli. */}
                  <InfoCell icon={Coins} label={BB_NAME} value={user.lc_display || walletAmount(user.lc_balance_milli || 0, 'lc')} accent="amber"/>
                  <InfoCell icon={Globe} label="Last IP" value={user.last_ip} mono onClick={user.last_ip?()=>goToIdentity("ip", user.last_ip):null}/>
                  <InfoCell icon={Network} label="Subnet" value={user.last_ip_subnet} mono/>
                  <InfoCell icon={Fingerprint} label="Device" value={user.device_id} mono onClick={user.device_id?()=>goToIdentity("device", user.device_id):null}/>
                  <InfoCell icon={Fingerprint} label="Fingerprint" value={user.fingerprint || user.last_fingerprint} mono onClick={(user.fingerprint || user.last_fingerprint)?()=>goToIdentity("fingerprint", user.fingerprint || user.last_fingerprint):null}/>
                </div>
              </div>
            </div>

            {/* ACTION PANEL */}
            <div className="border-t border-[#2A2D31] pt-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Moderation Actions</div>
              <div className="flex flex-wrap gap-2">
                <ActionButton icon={Ban}    label={user.is_banned?'Banned':'Ban'} onClick={()=>setActiveModal('ban')}    disabled={!canActOnTarget||user.is_banned} accent="red"/>
                <ActionButton icon={CheckCircle2} label="Unban" onClick={()=>setActiveModal('unban')} disabled={!canActOnTarget||!user.is_banned} accent="emerald"/>
                <ActionButton icon={VolumeX} label="Mute"  onClick={()=>setActiveModal('mute')}  disabled={!canActOnTarget} accent="amber"/>
                <ActionButton icon={Volume2} label="Unmute"onClick={()=>setActiveModal('unmute')}disabled={!canActOnTarget||!user.muted_until} accent="emerald"/>
                <ActionButton icon={Crown}   label="Rank"  onClick={()=>setActiveModal('set-rank')} disabled={!canActOnTarget} accent="amber"/>
                <ActionButton icon={Sparkles}label="Badge" onClick={()=>setActiveModal('set-flag')} disabled={viewerRank<3} accent="purple"/>
                {/* ⚠️ Fiyat ihlali cezası: 2. ihlalde çekim engeli, 3.'te
                    dondurma. Destekle iletişime geçen kullanıcının yasağını
                    buradan kaldırıyoruz. Buton yalnızca GERÇEKTEN yasaklıysa
                    etkin — yoksa "kaldıracak bir şey yok"a basılırdı. */}
                <ActionButton icon={ShieldCheck} label="Lift W-Ban"
                  onClick={()=>setActiveModal('lift-wban')}
                  disabled={!canActOnTarget || (
                    !user.withdraw_blocked &&
                    !(user.is_frozen && user.frozen_reason === 'p2p_price_manipulation')
                  )}
                  accent="emerald"/>
                {canBalanceAdjust && (
                  <ActionButton icon={Coins} label="Adjust $" onClick={()=>setActiveModal('balance')} accent="amber" ownerOnly/>
                )}
              </div>
              {!canActOnTarget && user.id !== viewer.id && (
                <div className="mt-3 text-xs text-zinc-600 flex items-center gap-1.5">
                  <Shield className="w-3 h-3"/>You cannot act on equal or higher rank.
                </div>
              )}
            </div>
          </div>

          {/* STATS PANEL */}
          {stats && <UserStatsPanel stats={stats} activity={data?.activity}/>}

          {/* LINKED ACCOUNTS PANEL */}
          {linked && <LinkedAccountsPanel linked={linked} onUserClick={(uid)=>{ setIdentifier(uid); lookup(uid); }} onIdentityClick={goToIdentity}/>}

          {/* SOURCE PILLS + FILTER TOGGLE */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <SourceFilterPill source="all" label="All" count={filteredCounts.all} active={sourceFilter==='all'} onClick={()=>setSourceFilter('all')}/>
            {[
              ['audit_log',    'Audit'],
              ['balance_log',  'Balance'],
              ['game_history', 'Games'],
              ['transaction',  'TX'],
              ['chat_message', 'Chat'],
            ].map(([sourceKey, label]) => (
              <SourceFilterPill key={sourceKey} source={sourceKey} label={label}
                count={filteredCounts[sourceKey] || 0}
                active={sourceFilter===sourceKey}
                onClick={()=>setSourceFilter(sourceKey)}/>
            ))}
            <button onClick={()=>setShowFilters(v=>!v)}
              className={`ml-auto px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors ${
                showFilters||activeFilterCount>0
                  ? 'bg-red-500/15 text-red-300 border border-red-500/30'
                  : 'bg-white/5 text-zinc-400 hover:text-white border border-transparent'
              }`}>
              <Filter className="w-3.5 h-3.5"/>
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] leading-none">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {/* FILTER PANEL */}
          <AnimatePresence>
            {showFilters && (
              <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden mb-3">
                <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-4 space-y-4">

                  {/* GLOBAL */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2 flex items-center gap-2">
                      <Filter className="w-3 h-3"/>Global
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                      <FilterField label="Min $">
                        <input type="number" min="0" step="0.01" value={filters.minAmount}
                          onChange={e=>setF({minAmount: e.target.value})} placeholder="0"
                          className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] focus:border-blue-500 rounded-lg text-white text-sm outline-none transition-colors"/>
                      </FilterField>
                      <FilterField label="Max $">
                        <input type="number" min="0" step="0.01" value={filters.maxAmount}
                          onChange={e=>setF({maxAmount: e.target.value})} placeholder="∞"
                          className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] focus:border-blue-500 rounded-lg text-white text-sm outline-none transition-colors"/>
                      </FilterField>
                      <FilterField label="From">
                        <input type="datetime-local" value={filters.dateFrom}
                          onChange={e=>setF({dateFrom: e.target.value})}
                          className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] focus:border-blue-500 rounded-lg text-white text-sm outline-none transition-colors"/>
                      </FilterField>
                      <FilterField label="To">
                        <input type="datetime-local" value={filters.dateTo}
                          onChange={e=>setF({dateTo: e.target.value})}
                          className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] focus:border-blue-500 rounded-lg text-white text-sm outline-none transition-colors"/>
                      </FilterField>
                    </div>
                  </div>

                  {/* AUDIT */}
                  {sourceFilter === 'audit_log' && (
                    <div className="pt-3 border-t border-[#2A2D31]/50 space-y-3">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 flex items-center gap-2">
                        <Shield className="w-3 h-3 text-red-400"/>Audit
                      </div>
                      <FilterField label="Category">
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(CATEGORY_CFG).map(([catKey, cfg]) => {
                            const CIcon = cfg.icon;
                            const inUse = presentAuditCategories.includes(catKey);
                            return (
                              <FilterPill key={catKey}
                                active={filters.auditCategories.includes(catKey)}
                                onClick={()=>toggleArr('auditCategories', catKey)}
                                color={({
                                  auth:'blue', security:'red', admin_action:'red', permission:'purple',
                                  balance:'emerald', transaction:'emerald', game:'zinc', chat:'blue',
                                  system:'zinc', affiliate:'purple', tip:'pink',
                                })[catKey] || 'zinc'}
                                icon={CIcon}>
                                {cfg.label}{!inUse && <span className="opacity-40 ml-0.5">·0</span>}
                              </FilterPill>
                            );
                          })}
                        </div>
                      </FilterField>
                      <FilterField label="Severity">
                        <div className="flex flex-wrap gap-1.5">
                          {[['info','blue'],['warning','yellow'],['critical','red']].map(([sev,col])=>(
                            <FilterPill key={sev}
                              active={filters.auditSeverities.includes(sev)}
                              onClick={()=>toggleArr('auditSeverities', sev)}
                              color={col}>
                              {SEVERITY_CFG[sev].label}
                            </FilterPill>
                          ))}
                        </div>
                      </FilterField>
                      <FilterField label="Action name contains">
                        <input value={filters.auditActionQuery} onChange={e=>setF({auditActionQuery: e.target.value})}
                          placeholder="e.g. login_failed, banned, withdraw"
                          className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] focus:border-red-500 rounded-lg text-white text-sm outline-none transition-colors"/>
                      </FilterField>
                    </div>
                  )}

                  {/* BALANCE — semantic groups */}
                  {sourceFilter === 'balance_log' && (
                    <div className="pt-3 border-t border-[#2A2D31]/50 space-y-3">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 flex items-center gap-2">
                        <Coins className="w-3 h-3 text-emerald-400"/>Balance
                      </div>
                      <FilterField label="Direction">
                        <div className="flex gap-1.5">
                          {[
                            ['all','All','zinc'],
                            ['credit','+ Credit','emerald'],
                            ['debit','− Debit','red'],
                          ].map(([v,l,col])=>(
                            <button key={v} onClick={()=>setF({balanceDirection: v})}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                                filters.balanceDirection===v ? PILL_COLORS[col].active : 'bg-white/5 text-zinc-500 border-transparent hover:text-white'
                              }`}>{l}</button>
                          ))}
                        </div>
                      </FilterField>
                      <FilterField label={`Type — pick any (${BALANCE_CATEGORIES.length} categories)`}>
                        <div className="flex flex-wrap gap-1.5">
                          {BALANCE_CATEGORIES.map(cat => (
                            <FilterPill key={cat.key}
                              active={filters.balanceCategories.includes(cat.key)}
                              onClick={()=>toggleArr('balanceCategories', cat.key)}
                              color={cat.color}
                              icon={cat.icon}>
                              {cat.label}
                            </FilterPill>
                          ))}
                        </div>
                      </FilterField>
                    </div>
                  )}

                  {/* GAMES — full list */}
                  {sourceFilter === 'game_history' && (
                    <div className="pt-3 border-t border-[#2A2D31]/50 space-y-3">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 flex items-center gap-2">
                        <Activity className="w-3 h-3 text-zinc-300"/>Games
                      </div>
                      <FilterField label={`Game — pick any (${GAME_TYPES.length} known${unknownGames.length>0?` + ${unknownGames.length} detected`:''})`}>
                        <div className="flex flex-wrap gap-1.5">
                          {GAME_TYPES.map(g => {
                            const cnt = gameCountsInData[g.key] || 0;
                            return (
                              <FilterPill key={g.key}
                                active={filters.games.includes(g.key)}
                                onClick={()=>toggleArr('games', g.key)}
                                color={cnt > 0 ? 'blue' : 'zinc'}
                                icon={g.icon}
                                count={cnt > 0 ? cnt : null}>
                                {g.label}
                              </FilterPill>
                            );
                          })}
                        </div>
                        {unknownGames.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-[#2A2D31]/30">
                            <div className="flex items-center gap-1.5 text-[10px] text-amber-400 mb-2">
                              <AlertTriangle className="w-3 h-3"/>
                              <span className="font-bold uppercase tracking-wider">
                                {unknownGames.length} unmatched game name{unknownGames.length===1?'':'s'} detected in DB
                              </span>
                            </div>
                            <p className="text-[10px] text-zinc-600 mb-2 leading-relaxed">
                              These game names exist in this user's <code className="text-amber-300">game_history</code> but don't match any built-in pill above.
                              They're shown here as raw strings — filtering works, but consider letting Claude know so it can add proper matchers.
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {unknownGames.map(g => (
                                <FilterPill key={g}
                                  active={filters.games.includes(g)}
                                  onClick={()=>toggleArr('games', g)}
                                  color="amber"
                                  icon={Hash}
                                  count={gameCountsInData[g] || 0}>
                                  <span className="mm-num">{g}</span>
                                </FilterPill>
                              ))}
                            </div>
                          </div>
                        )}
                      </FilterField>
                      <FilterField label="Outcome">
                        <div className="flex gap-1.5">
                          {[
                            ['all','All','zinc'],
                            ['win','Wins','emerald'],
                            ['loss','Losses','red'],
                          ].map(([v,l,col])=>(
                            <button key={v} onClick={()=>setF({gameOutcome: v})}
                              className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-colors border ${
                                filters.gameOutcome===v ? PILL_COLORS[col].active : 'bg-white/5 text-zinc-500 border-transparent hover:text-white'
                              }`}>{l}</button>
                          ))}
                        </div>
                      </FilterField>
                    </div>
                  )}

                  {/* TRANSACTIONS */}
                  {sourceFilter === 'transaction' && (
                    <div className="pt-3 border-t border-[#2A2D31]/50 space-y-3">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 flex items-center gap-2">
                        <Wallet className="w-3 h-3 text-blue-400"/>Transactions
                      </div>
                      <FilterField label="Type">
                        <div className="flex flex-wrap gap-1.5">
                          {TX_TYPES.map(t => (
                            <FilterPill key={t.key}
                              active={filters.txTypes.includes(t.key)}
                              onClick={()=>toggleArr('txTypes', t.key)}
                              color={t.color}
                              icon={t.key==='deposit'?Plus:Minus}>
                              {t.label}
                            </FilterPill>
                          ))}
                        </div>
                      </FilterField>
                      <FilterField label="Status">
                        <div className="flex flex-wrap gap-1.5">
                          {TX_STATUSES.map(s => (
                            <FilterPill key={s.key}
                              active={filters.txStatuses.includes(s.key)}
                              onClick={()=>toggleArr('txStatuses', s.key)}
                              color={s.color}>
                              {s.label}
                            </FilterPill>
                          ))}
                        </div>
                      </FilterField>
                    </div>
                  )}

                  {/* CHAT */}
                  {sourceFilter === 'chat_message' && (
                    <div className="pt-3 border-t border-[#2A2D31]/50">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-600 mb-2 flex items-center gap-2">
                        <MessageCircle className="w-3 h-3 text-purple-400"/>Chat
                      </div>
                      <FilterField label="Message contains (case-insensitive)">
                        <input value={filters.chatQuery} onChange={e=>setF({chatQuery: e.target.value})}
                          placeholder="e.g. scam, deposit, slur"
                          className="w-full p-2 bg-[#0B0C0E] border border-[#2A2D31] focus:border-purple-500 rounded-lg text-white text-sm outline-none transition-colors"/>
                      </FilterField>
                    </div>
                  )}

                  {/* SOURCE=ALL HINT */}
                  {sourceFilter === 'all' && (
                    <div className="pt-3 border-t border-[#2A2D31]/50 text-xs text-zinc-600 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
                      <span>Pick a specific source (Audit / Balance / Games / TX / Chat) above to unlock source-specific filters. Global filters (amount, date) always apply.</span>
                    </div>
                  )}

                  {activeFilterCount > 0 && (
                    <div className="flex justify-between items-center pt-3 border-t border-[#2A2D31]/50">
                      <span className="text-xs text-zinc-600">{activeFilterCount} filter{activeFilterCount===1?'':'s'} active</span>
                      <button onClick={resetFilters} className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-red-500/10 text-xs text-zinc-400 hover:text-red-400 transition-colors flex items-center gap-1.5">
                        <X className="w-3 h-3"/>Clear all
                      </button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* RESULTS COUNT */}
          {(activeFilterCount > 0 || sourceFilter !== 'all') && (
            <div className="mb-2 px-1 text-xs text-zinc-600 flex items-center gap-2">
              <span>Showing <span className="text-white font-bold">{filteredActivity.length}</span> of {data.activity.length} events</span>
              {activeFilterCount > 0 && (
                <button onClick={resetFilters} className="text-red-400 hover:text-red-300 underline-offset-2 hover:underline">Clear filters</button>
              )}
            </div>
          )}

          {/* ACTIVITY FEED */}
          {filteredActivity.length === 0 ? (
            <EmptyState
              icon={Activity}
              title={activeFilterCount > 0 ? 'No events match your filters' : 'No activity'}
              sub={activeFilterCount > 0 ? 'Try clearing some filters.' : (sourceFilter==='all'?'No events recorded.':'No events in this category.')}
            />
          ) : (
            <div className="space-y-2">
              {filteredActivity.map((item, i) => (
                <UnifiedActivityRow key={`${item.source}-${item.timestamp}-${i}`} item={item} delay={Math.min(i*0.015,0.2)} onIdentityClick={goToIdentity}/>
              ))}
            </div>
          )}
        </>
      )}

      {/* ACTION MODALS */}
      <AnimatePresence>
        {activeModal === 'ban'      && <BanUserModal      targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'unban'    && <UnbanUserModal    targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'mute'     && <MuteUserModal     targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'unmute'   && <UnmuteUserModal   targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'set-rank' && <SetRankModal      targetUser={user} viewer={viewer} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'set-flag' && <SetFlagModal      targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'balance'  && <BalanceAdjustModal targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
        {activeModal === 'lift-wban' && <LiftWithdrawBanModal targetUser={user} onClose={()=>setActiveModal(null)} onDone={refreshAfterAction}/>}
      </AnimatePresence>
    </motion.div>
  );
};

const InfoCell = ({ icon:Icon, label, value, mono=false, accent, onClick }) => {
  const accentMap = { emerald:'text-emerald-400', red:'text-red-400', blue:'text-blue-400', amber:'text-amber-400' };
  const valueClass = `${mono?'mm-num text-[11px]':'text-sm'} font-bold truncate ${accent?accentMap[accent]:'text-white'} ${onClick?'hover:text-blue-400 cursor-pointer':''}`;
  return (
    <div className="bg-[#0B0C0E] border border-[#2A2D31] rounded-lg p-2.5">
      <div className="flex items-center gap-1 text-zinc-600 text-[10px] uppercase tracking-wider mb-0.5">
        <Icon className="w-3 h-3"/>{label}
      </div>
      {onClick
        ? <button onClick={onClick} className={valueClass + ' block w-full text-left'}>{value || '—'}</button>
        : <div className={valueClass}>{value || '—'}</div>}
    </div>
  );
};

const ActionButton = ({ icon:Icon, label, onClick, disabled, accent='blue', ownerOnly=false }) => {
  const map = {
    red:     'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20',
    amber:   'bg-amber-500/10 border-amber-500/30 text-amber-400 hover:bg-amber-500/20',
    blue:    'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20',
    purple:  'bg-purple-500/10 border-purple-500/30 text-purple-400 hover:bg-purple-500/20',
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`px-3 py-2 rounded-xl border text-sm font-bold flex items-center gap-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${map[accent]}`}>
      <Icon className="w-3.5 h-3.5"/>{label}
      {ownerOnly && <Crown className="w-3 h-3 opacity-80"/>}
    </button>
  );
};

const SourceFilterPill = ({ source, label, count, active, onClick }) => {
  const cfg = SOURCE_CFG[source] || { color:'text-zinc-400', bg:'bg-white/5' };
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors flex items-center gap-1.5 ${active?`${cfg.bg} ${cfg.color} border border-current/30`:'bg-white/5 text-zinc-500 hover:text-white border border-transparent'}`}>
      {label}
      <span className="opacity-70">{count}</span>
    </button>
  );
};

/* ════════════════════════════════════════════════════════════
   USER STATS PANEL (new) — lifetime aggregates
   ════════════════════════════════════════════════════════════ */
const StatTile = ({ icon:Icon, label, value, sub, accent='zinc' }) => {
  const colorMap = {
    emerald: 'text-emerald-400',
    red:     'text-red-400',
    blue:    'text-blue-400',
    amber:   'text-amber-400',
    purple:  'text-purple-400',
    zinc:    'text-zinc-300',
  };
  const c = colorMap[accent] || colorMap.zinc;
  return (
    <div className="bg-[#0B0C0E] border border-[#2A2D31] rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-zinc-600 text-[10px] uppercase tracking-wider mb-1">
        {Icon && <Icon className="w-3 h-3"/>}{label}
      </div>
      <div className={`text-lg font-bold ${c}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
};

const UserStatsPanel = ({ stats, activity }) => {
  const [expandGames, setExpandGames] = useState(false);
  const netDeposit = stats.net_deposit || 0;
  const pnl = stats.pnl || 0;

  // Rebuild per-game stats client-side from activity feed using normalized field names.
  // This is more accurate than backend's per_game because we read game_type/result correctly.
  // Falls back to backend's per_game if no activity loaded.
  const perGame = useMemo(() => {
    if (!activity || activity.length === 0) return stats.games?.per_game || [];
    const agg = {};
    for (const item of activity) {
      if (item.source !== 'game_history') continue;
      const d = item.data || {};
      const rawName = getGameName(d);
      if (!rawName) continue;
      // Normalize to known key when possible, else keep raw
      const key = GAME_TYPES.find(t => matchGameType(rawName, t.key))?.key || rawName;
      if (!agg[key]) agg[key] = { game: key, count: 0, wagered: 0, won: 0, wins: 0 };
      agg[key].count++;
      agg[key].wagered += getBetAmount(d) || 0;
      agg[key].won     += d.win_amount || 0;
      if (getGameWin(d) === true) agg[key].wins++;
    }
    return Object.values(agg).map(g => ({
      ...g,
      win_rate: g.count > 0 ? g.wins / g.count : 0,
      net: g.won - g.wagered,
    })).sort((a,b) => b.count - a.count);
  }, [activity, stats]);

  // Also recompute totals from rebuilt per-game (avoids stale backend numbers)
  const totals = useMemo(() => {
    if (!activity || activity.length === 0) {
      return {
        total_wagered: stats.wagered?.total || 0,
        total_won:     stats.won?.total || 0,
        total_games:   stats.games?.total || 0,
        total_wins:    stats.games?.wins || 0,
      };
    }
    return perGame.reduce((acc, g) => ({
      total_wagered: acc.total_wagered + (g.wagered || 0),
      total_won:     acc.total_won     + (g.won || 0),
      total_games:   acc.total_games   + (g.count || 0),
      total_wins:    acc.total_wins    + (g.wins || 0),
    }), { total_wagered: 0, total_won: 0, total_games: 0, total_wins: 0 });
  }, [perGame, activity, stats]);

  const overallWinRate = totals.total_games > 0 ? totals.total_wins / totals.total_games : 0;
  const rebuiltPnl = totals.total_won - totals.total_wagered;

  return (
    <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-400"/>
        <h3 className="font-bold text-zinc-300">Lifetime Stats</h3>
        {stats.account_age_days !== null && stats.account_age_days !== undefined && (
          <span className="ml-auto text-xs text-zinc-600">
            Account age: <span className="text-white font-bold">{stats.account_age_days}d</span>
          </span>
        )}
        {stats.last_active && (
          <span className="text-xs text-zinc-600">
            Last active: <span className="text-white">{relTime(stats.last_active)}</span>
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <StatTile icon={Plus}  label="Total Deposits"  value={fmtD(stats.deposits?.total)}     sub={`${stats.deposits?.count||0} txs · ${stats.deposits?.pending||0} pending`} accent="emerald"/>
        <StatTile icon={Minus} label="Total Withdrawals" value={fmtD(stats.withdrawals?.total)} sub={`${stats.withdrawals?.count||0} txs · ${stats.withdrawals?.pending||0} pending`} accent="blue"/>
        <StatTile icon={Coins} label="Net Deposit"     value={`${netDeposit>=0?'+':''}${fmtD(netDeposit)}`}  sub="deposits − withdrawals" accent={netDeposit>=0?'emerald':'red'}/>
        <StatTile icon={DollarSign} label="House P&L"  value={`${rebuiltPnl>=0?'+':''}${fmtD(rebuiltPnl)}`}                sub={rebuiltPnl>=0?'user is winning':'user is losing'} accent={rebuiltPnl>=0?'emerald':'red'}/>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <StatTile icon={Activity}   label="Total Wagered" value={fmtD(totals.total_wagered)}  sub={`${totals.total_games||0} games played`}/>
        <StatTile icon={TrendingUp} label="Total Won"     value={fmtD(totals.total_won)}      sub={`${totals.total_wins||0} wins`} accent="emerald"/>
        <StatTile icon={Trophy}     label="Win Rate"      value={`${(overallWinRate*100).toFixed(1)}%`} sub="overall" accent={overallWinRate>=0.5?'emerald':'zinc'}/>
        <StatTile icon={Users}      label="Referrals"     value={stats.referred_count||0}      sub={stats.affiliate_code?`code: ${stats.affiliate_code}`:'no affiliate code'} accent="purple"/>
      </div>

      {(stats.tips_sent?.count || stats.tips_received?.count || stats.ban_count) ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          {stats.tips_sent?.count > 0 && (
            <StatTile icon={Gift} label="Tips Sent" value={fmtD(stats.tips_sent.total)} sub={`${stats.tips_sent.count} tips`} accent="pink"/>
          )}
          {stats.tips_received?.count > 0 && (
            <StatTile icon={Gift} label="Tips Received" value={fmtD(stats.tips_received.total)} sub={`${stats.tips_received.count} tips`} accent="pink"/>
          )}
          {stats.ban_count > 0 && (
            <StatTile icon={Ban} label="Ban History" value={stats.ban_count} sub="lifetime bans" accent="red"/>
          )}
          {(stats.deposits?.failed > 0 || stats.withdrawals?.failed > 0) && (
            <StatTile icon={XCircle} label="Failed TXs" value={(stats.deposits?.failed||0)+(stats.withdrawals?.failed||0)} sub={`${stats.deposits?.failed||0} dep · ${stats.withdrawals?.failed||0} wd`} accent="red"/>
          )}
        </div>
      ) : null}

      {/* Per-game breakdown */}
      {(perGame.length > 0) && (
        <div className="bg-[#0B0C0E] border border-[#2A2D31] rounded-xl overflow-hidden">
          <button onClick={()=>setExpandGames(v=>!v)} className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-2 text-zinc-300 text-sm font-bold">
              <Activity className="w-4 h-4"/>Per-Game Breakdown
              <span className="text-zinc-600 text-xs font-normal">({perGame.length} games)</span>
            </div>
            {expandGames ? <ChevronUp className="w-4 h-4 text-zinc-500"/> : <ChevronDown className="w-4 h-4 text-zinc-500"/>}
          </button>
          <AnimatePresence>
            {expandGames && (
              <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
                <div className="px-3 pb-3 pt-1 space-y-1">
                  <div className="grid grid-cols-6 gap-2 text-[10px] uppercase tracking-wider text-zinc-600 px-2 pt-2 pb-1 border-b border-[#2A2D31]/30">
                    <span className="col-span-2">Game</span>
                    <span className="text-right">Count</span>
                    <span className="text-right">Wagered</span>
                    <span className="text-right">Won</span>
                    <span className="text-right">Net</span>
                  </div>
                  {perGame.map((g,i) => {
                    const gcfg = GAME_TYPES.find(t => matchGameType(g.game, t.key)) || GAME_TYPES.find(t => t.key === g.game);
                    const GIcon = gcfg?.icon || Activity;
                    const winRate = g.win_rate || 0;
                    return (
                      <div key={i} className="grid grid-cols-6 gap-2 items-center px-2 py-1.5 rounded-lg hover:bg-white/5">
                        <div className="col-span-2 flex items-center gap-1.5 text-xs">
                          <GIcon className="w-3 h-3 text-zinc-500"/>
                          <span className="font-bold text-white">{gcfg?.label || formatAction(g.game)}</span>
                          <span className="text-zinc-600 text-[10px]">({(winRate*100).toFixed(0)}% WR)</span>
                        </div>
                        <span className="text-right text-xs text-zinc-400">{fmt(g.count)}</span>
                        <span className="text-right text-xs text-zinc-400">{fmtD(g.wagered)}</span>
                        <span className="text-right text-xs text-emerald-400">{fmtD(g.won)}</span>
                        <span className={`text-right text-xs font-bold ${g.net>=0?'text-emerald-400':'text-red-400'}`}>
                          {g.net>=0?'+':''}{fmtD(g.net)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   LINKED ACCOUNTS PANEL (new) — multi-account detection
   ════════════════════════════════════════════════════════════ */
const LinkedAccountsPanel = ({ linked, onUserClick, onIdentityClick }) => {
  const [expanded, setExpanded] = useState({ fp:true, dev:true, ip:false, sub:false });

  const totals = linked.totals || {};
  const totalLinked = (totals.fingerprint||0) + (totals.device||0) + (totals.ip||0) + (totals.subnet||0);
  const ids = linked.identifiers || {};

  const sections = [
    { key:'fp',  label:'Same Fingerprint', icon:Fingerprint, color:'purple',
      users:linked.by_fingerprint||[], identifier:ids.fingerprint, identityType:'fingerprint',
      description:'These accounts share the same browser fingerprint hash — strongest multi-account signal.' },
    { key:'dev', label:'Same Device',      icon:Hash,        color:'blue',
      users:linked.by_device||[],      identifier:ids.device_id,  identityType:'device',
      description:'These accounts logged in from the same device ID.' },
    { key:'ip',  label:'Same IP',          icon:Globe,       color:'amber',
      users:linked.by_ip||[],          identifier:ids.ip,         identityType:'ip',
      description:'Currently shares last-seen IP address.' },
    { key:'sub', label:'Same Subnet',      icon:Network,     color:'zinc',
      users:linked.by_subnet||[],      identifier:ids.subnet,
      description:'Different IPs but within the same /24 subnet — possible shared network.' },
  ];

  const visibleSections = sections.filter(s => s.users.length > 0);

  return (
    <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Network className="w-4 h-4 text-purple-400"/>
        <h3 className="font-bold text-zinc-300">Linked Accounts</h3>
        {totalLinked > 0 ? (
          <span className={`ml-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
            totalLinked >= 5 ? 'bg-red-500/15 text-red-300 border border-red-500/30'
            : totalLinked >= 2 ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
            : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
          }`}>
            <AlertTriangle className="w-3 h-3"/>{totalLinked} linked
          </span>
        ) : (
          <span className="ml-2 text-xs text-zinc-600">No linked accounts found</span>
        )}
      </div>

      {visibleSections.length === 0 ? (
        <div className="text-center py-6 text-zinc-600 text-sm">
          <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-zinc-700"/>
          This account doesn't share its fingerprint, device, IP or subnet with any other visible accounts.
        </div>
      ) : (
        <div className="space-y-2">
          {visibleSections.map(sec => {
            const cfg = PILL_COLORS[sec.color] || PILL_COLORS.zinc;
            const Icon = sec.icon;
            const isOpen = expanded[sec.key];
            return (
              <div key={sec.key} className="bg-[#0B0C0E] border border-[#2A2D31] rounded-xl overflow-hidden">
                <button onClick={()=>setExpanded(e=>({...e, [sec.key]:!e[sec.key]}))}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/5 transition-colors">
                  <div className={`w-7 h-7 rounded-lg border ${cfg.active} flex items-center justify-center`}>
                    <Icon className="w-3.5 h-3.5"/>
                  </div>
                  <span className="font-bold text-white text-sm">{sec.label}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${cfg.active}`}>{sec.users.length} {sec.users.length===1?'account':'accounts'}</span>
                  {sec.identifier && (
                    <span className="text-[10px] mm-num text-zinc-600 truncate ml-2 hidden sm:block" title={sec.identifier}>
                      {sec.identifier.length > 30 ? sec.identifier.slice(0,30)+'…' : sec.identifier}
                    </span>
                  )}
                  {sec.identifier && sec.identityType && (
                    <button onClick={(e)=>{e.stopPropagation(); onIdentityClick?.(sec.identityType, sec.identifier);}}
                      className="ml-auto px-2 py-1 rounded-lg text-[10px] font-bold bg-white/5 hover:bg-blue-500/10 text-zinc-400 hover:text-blue-400 transition-colors">
                      Open in Identity Lookup
                    </button>
                  )}
                  {isOpen ? <ChevronUp className="w-4 h-4 text-zinc-500 ml-2"/> : <ChevronDown className="w-4 h-4 text-zinc-500 ml-2"/>}
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{height:0,opacity:0}} animate={{height:'auto',opacity:1}} exit={{height:0,opacity:0}} className="overflow-hidden">
                      <div className="px-3 pb-3 pt-1 border-t border-[#2A2D31]/50">
                        <p className="text-[10px] text-zinc-600 mb-2">{sec.description}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {sec.users.map(u => <UserMiniCard key={u.id} user={u} onClick={()=>onUserClick?.(u.id)}/>)}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   IDENTITY INVESTIGATION VIEW (replaces IP Investigation)
   ════════════════════════════════════════════════════════════ */
const IDENTITY_MODES = [
  { key:'ip',          label:'IP',          icon:Globe,       placeholder:'1.2.3.4 / 2001:db8::1', color:'blue'   },
  { key:'fingerprint', label:'Fingerprint', icon:Fingerprint, placeholder:'fp_xxx... hash',          color:'purple' },
  { key:'device',      label:'Device',      icon:Hash,        placeholder:'device_xxx ID',           color:'amber'  },
];

const IdentityInvestigationView = ({ prefilled, onConsumed, goToUser, goToIdentity }) => {
  const { token } = useAuth();
  const [mode, setMode] = useState('ip');
  const [draft, setDraft] = useState('');
  const [activeQuery, setActiveQuery] = useState(null); // {type, value}
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (type, value) => {
    if (!type || !value || !value.trim()) return;
    setLoading(true); setData(null);
    const v = value.trim();
    setActiveQuery({ type, value: v });
    setMode(type);
    setDraft(v);
    const endpoint = type === 'ip' ? `ip/${encodeURIComponent(v)}`
                   : type === 'fingerprint' ? `fingerprint/${encodeURIComponent(v)}`
                   : `device/${encodeURIComponent(v)}`;
    try {
      const res = await axios.get(`${API}/admin/logs/${endpoint}`, {
        headers:{Authorization:`Bearer ${token}`},
      });
      setData(res.data);
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Lookup failed.');
      setData(null);
    } finally { setLoading(false); }
  }, [token]);

  // Consume prefilled identity from other section
  useEffect(()=>{
    if (prefilled?.type && prefilled?.value) {
      lookup(prefilled.type, prefilled.value);
      onConsumed?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilled]);

  const onSubmit = e => { e?.preventDefault(); lookup(mode, draft); };

  const currentMode = IDENTITY_MODES.find(m => m.key === mode);
  const ModeIcon = currentMode?.icon || Globe;

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold">Identity Investigation</h1>
          <p className="text-zinc-500 text-sm">Search by IP, fingerprint, or device — find all accounts sharing the identifier.</p>
        </div>
        {activeQuery && <button onClick={()=>lookup(activeQuery.type, activeQuery.value)} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-zinc-400 hover:text-white">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/>
        </button>}
      </div>

      {/* MODE SELECTOR */}
      <div className="flex gap-2 mb-3">
        {IDENTITY_MODES.map(m => {
          const MIcon = m.icon;
          const cfg = PILL_COLORS[m.color] || PILL_COLORS.blue;
          const active = mode === m.key;
          return (
            <button key={m.key} onClick={()=>setMode(m.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-bold border transition-colors ${
                active ? cfg.active : 'bg-white/5 text-zinc-400 border-transparent hover:text-white hover:bg-white/10'
              }`}>
              <MIcon className="w-4 h-4"/>{m.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={onSubmit} className="flex gap-2 mb-5">
        <div className="flex-1 relative">
          <ModeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600"/>
          <input value={draft} onChange={e=>setDraft(e.target.value)}
            placeholder={currentMode?.placeholder}
            className="w-full pl-9 pr-3 py-2.5 bg-[#121417] border border-[#2A2D31] focus:border-blue-500 rounded-xl text-white text-sm outline-none mm-num transition-colors"/>
        </div>
        <button type="submit" disabled={loading} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-xl text-white font-bold text-sm">
          {loading ? <Loader2 className="animate-spin w-4 h-4"/> : 'Lookup'}
        </button>
      </form>

      {loading && <LoadingBlock/>}
      {!loading && !data && !activeQuery && (
        <EmptyState icon={Globe} title="No identity selected" sub="Choose a mode above and enter an IP, fingerprint, or device ID."/>
      )}

      {!loading && data && (
        <>
          {/* IDENTITY HEADER */}
          <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 mb-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <ModeIcon className={`w-4 h-4 ${PILL_COLORS[currentMode.color].active.split(' ')[1]}`}/>
                  <span className="text-xs uppercase tracking-wider text-zinc-600">{currentMode.label}</span>
                  {data.is_banned && <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30"><Ban className="w-3 h-3"/>BANNED</span>}
                </div>
                <h2 className="text-lg font-bold mm-num text-white break-all">{data.ip || data.fingerprint || data.device_id}</h2>
                {data.subnet && (
                  <div className="flex items-center gap-2 text-xs mt-1">
                    <Network className="w-3 h-3 text-zinc-600"/>
                    <span className="text-zinc-600">Subnet:</span>
                    <button onClick={()=>goToIdentity('ip', data.subnet)} className="mm-num text-zinc-400 hover:text-blue-400">{data.subnet}</button>
                    {data.is_subnet_banned && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30"><Ban className="w-2.5 h-2.5"/>SUBNET BANNED</span>}
                  </div>
                )}
              </div>
              <div className="text-xs text-zinc-600 text-right">
                <div>{data.events?.length ?? 0} event{data.events?.length===1?'':'s'}</div>
                {data.events_hidden_by_hierarchy > 0 && <div className="inline-flex items-center gap-1 mt-0.5"><Shield className="w-3 h-3"/>{data.events_hidden_by_hierarchy} hidden</div>}
              </div>
            </div>
          </div>

          {/* ACCOUNTS USING THIS IDENTITY */}
          {((data.users?.length || 0) + (data.users_with_this_ip?.length || 0)) > 0 && (
            <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-amber-400"/>
                <h3 className="font-bold text-zinc-300">
                  Accounts using this {currentMode.label.toLowerCase()}
                  &nbsp;<span className="text-zinc-600 font-normal">({(data.users?.length || data.users_with_this_ip?.length || 0)})</span>
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(data.users || data.users_with_this_ip || []).map(u => <UserMiniCard key={u.id} user={u} onClick={()=>goToUser(u.id)}/>)}
              </div>
            </div>
          )}

          {/* SUBNET PEERS (only for IP mode) */}
          {(data.other_users_in_subnet?.length > 0) && (
            <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Network className="w-4 h-4 text-zinc-400"/>
                <h3 className="font-bold text-zinc-300">Other accounts in subnet ({data.other_users_in_subnet.length})</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {data.other_users_in_subnet.map(u => <UserMiniCard key={u.id} user={u} onClick={()=>goToUser(u.id)}/>)}
              </div>
            </div>
          )}

          {/* EVENTS */}
          <div className="mb-2 text-xs text-zinc-600 px-1">Events from this {currentMode.label.toLowerCase()}</div>
          {!data.events?.length ? (
            <EmptyState icon={FileText} title={`No events from this ${currentMode.label.toLowerCase()}`}/>
          ) : (
            <div className="space-y-2">
              {data.events.map((ev,i)=>(
                <AuditEventCard key={ev.id||`${ev.timestamp}-${i}`} event={ev} delay={Math.min(i*0.02,0.3)} onUserClick={goToUser} onIdentityClick={goToIdentity}/>
              ))}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

const UserMiniCard = ({ user, onClick }) => (
  <button onClick={onClick}
    className="bg-[#0B0C0E] border border-[#2A2D31] hover:border-white/20 rounded-xl p-3 text-left transition-colors group w-full">
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="font-bold text-white text-sm truncate">{user.site_username||user.roblox_username||user.id}</span>
      <div className="flex items-center gap-1 shrink-0">
        {user.is_banned && <Ban className="w-3 h-3 text-red-400" title="Banned"/>}
        <RankBadge rank={getRankLabel(user)} size="xs"/>
      </div>
    </div>
    <div className="flex items-center justify-between text-xs">
      <span className="text-zinc-500 truncate">{user.email || '—'}</span>
      <span className="text-emerald-400 font-bold shrink-0 ml-2">{fmtD(user.site_balance)}</span>
    </div>
    {(user.last_ip || user.device_id || user.fingerprint || user.last_fingerprint) && (
      <div className="mt-2 pt-2 border-t border-[#2A2D31]/50 flex flex-wrap gap-1 text-[10px] text-zinc-600 mm-num">
        {user.last_ip && <span className="inline-flex items-center gap-0.5"><Globe className="w-2.5 h-2.5"/>{user.last_ip}</span>}
        {user.device_id && <span className="inline-flex items-center gap-0.5 truncate max-w-[120px]"><Hash className="w-2.5 h-2.5"/>{user.device_id.slice(0,10)}…</span>}
        {(user.fingerprint || user.last_fingerprint) && <span className="inline-flex items-center gap-0.5 truncate max-w-[120px]"><Fingerprint className="w-2.5 h-2.5"/>{(user.fingerprint || user.last_fingerprint).slice(0,10)}…</span>}
      </div>
    )}
  </button>
);

/* ════════════════════════════════════════════════════════════
   MANUAL REVIEW (existing — unchanged)
   ════════════════════════════════════════════════════════════ */
const ManualReviewView = () => {
  /* ══════════════════════════════════════════════════════════════════
     P2P MANUEL İNCELEME — YENİ SİSTEME GÖRE
     ══════════════════════════════════════════════════════════════════
     ⚠️ ESKİ HALİ ÖLÜ UÇLARA BAĞLIYDI: `/admin/manual_review_queue`,
     `/admin/match/{type}` ve `/admin/match_audit/{id}` artık sunucuda YOK.
     Yani bu ekran açılıyor, boş liste gösteriyor ve admin "inceleyecek bir
     şey yok" sanıyordu — oysa arkada `needs_review` yatırımlar ve süresi
     dolmak üzere olan müsadereler birikiyordu. Sessiz ve pahalı bir arıza.

     Yeni kaynaklar:
  */
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [note, setNote] = useState('');

  /* ⚠️ KAYNAK P2P'DEN MM2'YE ALINDI.
     Eski hâli `/admin/p2p/reviews` ve `/admin/p2p/resolve` çağırıyordu; o ray
     söküldü ve ikisi de artık 404 dönüyor. `catch` bloğu hatayı yutup BOŞ
     LİSTE gösterdiği için ekran "incelenecek bir şey yok" diyordu — oysa
     arkada `needs_review` çekimler birikiyor, eşyaları kilitli bekliyordu.
     Tam olarak bu yorumun anlattığı sessiz arızanın TEKRARI.

     Yeni kaynaklar:
       GET  /admin/mm2/trades/review   → insan kararı bekleyen çekimler
       POST /admin/mm2/trade/resolve   → delivered | not_delivered

     ⚠️ "forfeits" kavramı MM2'de YOK (satıcı müsaderesi P2P'ye özgüydü).
        Boş dizi veriliyor ki aşağıdaki JSX değişmeden çalışsın. */
  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/mm2/trades/review`,
        { headers:{Authorization:`Bearer ${token}`} });
      setData({ reviews: res.data?.trades || [], forfeits: [] });
    } catch {
      setData({ reviews: [], forfeits: [] });
    }
  }, [token]);

  useEffect(() => { if (token) fetchData(); }, [token, fetchData]);

  /* ⚠️ action ARTIK 'delivered' | 'not_delivered'.
     Eski değerler (credit_buyer / refund_seller / reverse_forfeit) sunucunun
     desenine UYMUYOR (`^(delivered|not_delivered)$`) ve 422 ile reddedilir. */
  const resolve = async (tradeId, action) => {
    setBusyId(tradeId);
    try {
      const res = await axios.post(`${API}/admin/mm2/trade/resolve`,
        { trade_id: tradeId, action, reason: note.trim() || 'admin review' },
        { headers:{Authorization:`Bearer ${token}`} });
      toast.success(res.data?.message || 'Resolved.');
      setConfirm(null); setNote('');
      await fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not resolve.');
    } finally { setBusyId(null); }
  };

  if (!data) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-[#B059E8] animate-spin"/>
    </div>
  );

  const reviews  = data.reviews  || [];
  const forfeits = data.forfeits || [];

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">MM2 Manual Review</h2>
          <p className="text-xs text-zinc-500">
            {reviews.length} withdrawal{reviews.length===1?'':'s'} awaiting a decision
          </p>
        </div>
        <button onClick={fetchData}
          className="p-2 rounded-lg border border-[#2A2D31] hover:border-[#B059E8]/50 text-zinc-400 hover:text-white transition-colors">
          <RefreshCw className="w-4 h-4"/>
        </button>
      </div>

      {forfeits.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Pending forfeits</h3>
          {forfeits.map(f => (
            <ForfeitCard key={f.withdraw.id} f={f}
              onAct={(id)=>{ setConfirm({id, action:'reverse_forfeit',
                label:'Reverse this forfeit',
                desc:'The seller keeps their balance and nothing is burned. Use this when the detection was a false positive.'}); setNote(''); }}/>
          ))}
        </div>
      )}

      {reviews.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Held deposits</h3>
          {reviews.map(r => (
            <HeldDepositCard key={r.deposit.id} r={r}
              onAct={(id, action, label, desc)=>{ setConfirm({id, action, label, desc}); setNote(''); }}/>
          ))}
        </div>
      )}

      {reviews.length === 0 && forfeits.length === 0 && (
        <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-10 text-center space-y-2">
          <Check className="w-6 h-6 text-[#86efac] mx-auto"/>
          <p className="text-white font-bold">Nothing waiting</p>
          <p className="text-sm text-zinc-500">Held deposits and pending forfeits appear here.</p>
        </div>
      )}

      <AnimatePresence>
        {confirm && (
          <ResolveConfirm confirm={confirm} note={note} setNote={setNote}
            busy={busyId===confirm.id}
            onCancel={()=>setConfirm(null)}
            onConfirm={()=>resolve(confirm.id, confirm.action)}/>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const RVFieldA = ({ label, children, mono }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</p>
    <p className={`text-sm text-zinc-200 truncate ${mono?'mm-num text-xs':''}`}>{children}</p>
  </div>
);

const rvNum = (n) => Number(n||0).toLocaleString();
const rvDate = (iso) => {
  if(!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleString(undefined,{dateStyle:'medium',timeStyle:'short'});
};

/* ⚠️ Ham anahtar göstermek, para kararı veren kişiyi TAHMİNE zorlar.
   Her sebebin ne olduğu VE ne anlama geldiği yazılı — hem şüpheli hem
   masum ihtimaliyle birlikte, çünkü ikisi de mümkün. */
const RV_REASON = {
  auto_gamepass_unlisted_before_verification: {
    title:'Gamepass was unlisted before we could verify the price',
    detail:'We created this pass and only the seller can unlist it. The buyer may '
      +'have paid correctly, or the seller may be hiding a lowered price.',
  },
  gamepass_modified_after_creation: {
    title:'Gamepass was modified after we created it',
    detail:'The change stamp differs from what we recorded at creation. A price '
      +'could have been lowered and restored. Benign edits are also possible.',
  },
};

const ForfeitCard = ({ f, onAct }) => (
  <div className={`rounded-2xl border p-5 space-y-4 ${
    f.expired ? 'bg-rose-500/5 border-rose-500/30' : 'bg-[#121417] border-[#2A2D31]'}`}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        <AlertTriangle className="w-4 h-4 text-rose-400"/>
        <span className="text-white font-bold">{rvNum(f.withdraw.forfeit_amount)} to be burned</span>
        {f.expired && (
          <span className="px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-500/40 text-[10px] font-bold text-rose-200">
            WINDOW CLOSED
          </span>
        )}
      </div>
      <span className="text-[11px] text-zinc-500">{rvDate(f.withdraw.forfeit_at)}</span>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <RVFieldA label="Seller">{f.seller?.site_username||'—'}</RVFieldA>
      <RVFieldA label="Reason">{f.withdraw.forfeit_reason||'—'}</RVFieldA>
      <RVFieldA label="Expected">{rvNum(f.withdraw.expected_price)}</RVFieldA>
      <RVFieldA label="Observed">{rvNum(f.withdraw.observed_price)}</RVFieldA>
    </div>
    <p className="text-[11px] text-zinc-400">
      Violations on record: {f.seller?.p2p_price_violations||0}
      {f.seller?.withdraw_blocked && ' · withdrawals blocked'}
      {f.seller?.is_frozen && ' · account frozen'}
    </p>
    <button onClick={()=>onAct(f.withdraw.id)}
      className="w-full py-2.5 rounded-xl bg-[#15171a] border border-[#2A2D31] hover:border-[#B059E8]/50 text-zinc-200 font-bold text-xs transition-colors">
      Reverse this forfeit
    </button>
  </div>
);

const HeldDepositCard = ({ r, onAct }) => {
  const d = r.deposit;
  const info = RV_REASON[d.review_reason] || {
    title: d.review_reason || 'Held for review',
    detail: 'Automatic verification could not decide.',
  };
  return (
    <div className="bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 space-y-4">
      <div>
        <p className="text-white font-bold text-sm">{info.title}</p>
        <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{info.detail}</p>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-zinc-200 font-bold">{r.buyer?.site_username||'—'}</span>
        <span className="text-zinc-600">→</span>
        <span className="text-zinc-200 font-bold">{r.seller?.site_username||'—'}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <RVFieldA label="Robux">{rvNum(d.amount)}</RVFieldA>
        <RVFieldA label="Credit at stake">{rvNum(d.site_credit_amount)}</RVFieldA>
        <RVFieldA label="Opened">{rvDate(d.created_at)}</RVFieldA>
        <RVFieldA label="Deposit" mono>{d.id}</RVFieldA>
      </div>
      {d.gamepass_id && (
        <a href={`https://www.roblox.com/game-pass/${d.gamepass_id}`}
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#c98bf0] hover:text-white transition-colors">
          Inspect Gamepass on Roblox <ExternalLink className="w-3 h-3"/>
        </a>
      )}
      <div className="flex gap-2 pt-1">
        <button
          onClick={()=>onAct(d.id,'credit_buyer','Credit buyer',
            "The buyer receives their site balance and the seller's payout is committed. Use this when you confirmed the buyer paid the correct amount.")}
          className="flex-1 py-2.5 rounded-xl bg-[#16A34A]/90 hover:bg-[#16A34A] text-white font-bold text-xs transition-colors">
          Credit buyer
        </button>
        <button
          onClick={()=>onAct(d.id,'refund_seller','Refund seller',
            "The deposit is cancelled and the seller's locked balance returns to them. Use this when the buyer did not actually pay.")}
          className="flex-1 py-2.5 rounded-xl bg-[#15171a] border border-[#2A2D31] hover:border-amber-500/50 text-zinc-200 font-bold text-xs transition-colors">
          Refund seller
        </button>
      </div>
    </div>
  );
};

/* ⚠️ Her karar GERİ ALINAMAZ bir para hareketi. Tek tıkla çalıştırmak,
   yanlış satıra basan bir adminin kararını sessizce uygulardı. */
const ResolveConfirm = ({ confirm, note, setNote, busy, onCancel, onConfirm }) => (
  <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
    className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
    onClick={onCancel}>
    <motion.div initial={{scale:0.96}} animate={{scale:1}} onClick={e=>e.stopPropagation()}
      className="w-full max-w-md bg-[#1A1D20] border border-[#2A2D31] rounded-2xl p-5 space-y-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5"/>
        <div>
          <h3 className="text-white font-bold">{confirm.label}</h3>
          <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{confirm.desc}</p>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
          Note (kept in the audit log)
        </label>
        <textarea value={note} onChange={e=>setNote(e.target.value)}
          maxLength={500} rows={3} placeholder="What did you check before deciding?"
          className="mt-1 w-full bg-[#15171a] border border-[#2A2D31] focus:border-[#B059E8] rounded-xl px-3 py-2 text-sm text-white outline-none transition-colors resize-none"/>
      </div>
      <div className="flex gap-2">
        <button onClick={onConfirm} disabled={busy}
          className="flex-1 py-3 rounded-xl bg-[#7b2fc4] hover:bg-[#B059E8] text-white font-bold text-sm transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin"/>}
          Confirm
        </button>
        <button onClick={onCancel}
          className="px-5 py-3 rounded-xl border border-[#2A2D31] hover:border-zinc-500 text-zinc-300 font-bold text-sm transition-colors">
          Cancel
        </button>
      </div>
    </motion.div>
  </motion.div>
);

/* ════════════════════════════════════════════════════════════
   SUSPICIOUS EVENTS (existing — unchanged)
   ════════════════════════════════════════════════════════════ */
const SuspiciousView = () => {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterReviewed, setFilterReviewed] = useState('unreviewed');

  const fetch = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/suspicious_events`,{headers:{Authorization:`Bearer ${token}`}});
      setEvents(res.data.items||[]);
    } catch { toast.error('Failed.'); } finally { setLoading(false); }
  },[token]);
  useEffect(()=>{fetch();},[fetch]);

  const displayed = events.filter(e=>{
    if(filterType!=='all'&&e.type!==filterType) return false;
    if(filterReviewed==='reviewed'&&!e.reviewed) return false;
    if(filterReviewed==='unreviewed'&&e.reviewed) return false;
    return true;
  });

  return (
    <motion.div initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0}}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold">Suspicious Events</h1>
          <p className="text-zinc-500 text-sm">Defense-in-depth anomaly detections.</p>
        </div>
        <button onClick={()=>{setLoading(true);fetch();}} className="p-2 bg-white/5 hover:bg-white/10 rounded-xl text-zinc-400 hover:text-white">
          <RefreshCw className={`w-4 h-4 ${loading?'animate-spin':''}`}/>
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {[['all','All'],['rapid_balance_gain','Rapid Gain'],['balance_cap_exceeded','Absurd Balance'],['reconciliation_mismatch_excess','Mismatch']].map(([v,l])=>(
          <button key={v} onClick={()=>setFilterType(v)}
            className={`px-3 py-1.5 rounded-xl text-sm font-bold transition-all ${filterType===v?'bg-orange-600 text-white':'bg-white/5 text-zinc-400 hover:text-white hover:bg-white/10'}`}>{l}</button>
        ))}
        <div className="ml-auto flex gap-1.5">
          {[['unreviewed','Unreviewed'],['reviewed','Reviewed'],['all','All']].map(([v,l])=>(
            <button key={v} onClick={()=>setFilterReviewed(v)}
              className={`px-3 py-1.5 rounded-xl text-sm font-bold transition-all ${filterReviewed===v?'bg-white/10 text-white border border-white/20':'bg-white/5 text-zinc-500 hover:text-white'}`}>{l}</button>
          ))}
        </div>
      </div>
      {loading?<LoadingBlock/>
        :displayed.length===0?<EmptyState icon={ShieldCheck} title="No events matching your filters"/>
        :(
          <div className="space-y-3">
            {displayed.map((ev,i)=>{
              const cfg = STYPE[ev.type]||STYPE.rapid_balance_gain;
              const Icon = cfg.icon;
              return (
                <motion.div key={ev.id} initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} transition={{delay:i*0.04}}
                  className={`bg-[#121417] border border-[#2A2D31] rounded-2xl p-5 ${ev.reviewed?'opacity-40':''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${cfg.color}`}/>
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`text-sm font-bold ${cfg.color}`}>{cfg.label}</span>
                          {ev.reviewed&&<span className="px-2 py-0.5 bg-white/5 rounded-full text-xs text-zinc-500">Reviewed</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-3 text-sm">
                          <span className="font-bold text-white">{ev.user_ign||ev.user_id}</span>
                          <span className="text-zinc-400">{fmtD(ev.amount)}</span>
                          {ev.threshold&&<span className="text-zinc-600">cap: {fmtD(ev.threshold)}</span>}
                        </div>
                        <div className="text-xs text-zinc-600 mt-0.5">{relTime(ev.created_at)}</div>
                      </div>
                    </div>
                    {!ev.reviewed&&<button onClick={()=>setReviewTarget(ev)} className="shrink-0 px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-xl text-sm text-zinc-400 hover:text-white transition-colors">Review</button>}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      <AnimatePresence>
        {reviewTarget&&(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={()=>setReviewTarget(null)}>
            <SuspiciousReviewModal event={reviewTarget} onClose={()=>setReviewTarget(null)} onReviewed={fetch}/>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const SuspiciousReviewModal = ({ event, onClose, onReviewed }) => {
  const { token } = useAuth();
  const [note, setNote] = useState('');
  const [action, setAction] = useState('dismissed');
  const [loading, setLoading] = useState(false);
  const cfg = STYPE[event.type]||STYPE.rapid_balance_gain;
  const Icon = cfg.icon;
  const submit = async () => {
    if(note.trim().length<5){toast.error('Enter a note (min 5 chars).');return;}
    setLoading(true);
    try {
      await axios.post(`${API}/admin/suspicious_events/${event.id}/review`,
        {action,note:note.trim()},{headers:{Authorization:`Bearer ${token}`}});
      toast.success('Marked as reviewed.'); onReviewed?.(); onClose();
    } catch(err){toast.error(err.response?.data?.detail||'Failed.');}
    finally{setLoading(false);}
  };
  return (
    <motion.div initial={{scale:0.95,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.95,opacity:0}}
      onClick={e=>e.stopPropagation()}
      className="w-full max-w-md bg-[#121417] rounded-2xl border border-[#2A2D31] shadow-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg text-white">Mark as Reviewed</h3>
        <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5"/></button>
      </div>
      <div className="flex items-center gap-2 mb-1"><Icon className={`w-5 h-5 ${cfg.color}`}/><span className={`font-bold ${cfg.color}`}>{cfg.label}</span></div>
      <p className="text-zinc-500 text-sm mb-4">{event.user_ign} · {fmtD(event.amount)}</p>
      <div className="flex gap-2 mb-4">
        {[['dismissed','False Positive'],['confirmed','Confirmed Threat']].map(([v,l])=>(
          <button key={v} onClick={()=>setAction(v)}
            className={`flex-1 py-2 rounded-xl text-sm font-bold border transition-all ${action===v?v==='dismissed'?'border-emerald-500/30 bg-emerald-500/10 text-emerald-400':'border-red-500/30 bg-red-500/10 text-red-400':'border-[#2A2D31] bg-[#0B0C0E] text-zinc-500 hover:text-white'}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-2 mb-4">
        <label className="text-sm text-zinc-400">Note</label>
        <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
          placeholder="e.g. False positive — legitimate big bet win"
          className="w-full p-3 bg-[#0B0C0E] border border-[#2A2D31] rounded-xl text-white text-sm outline-none resize-none focus:border-yellow-500 transition-colors"/>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-bold">Cancel</button>
        <button onClick={submit} disabled={loading} className="flex-1 py-3 bg-yellow-600 hover:bg-yellow-500 rounded-xl text-white font-bold disabled:opacity-40">
          {loading?<Loader2 className="animate-spin mx-auto w-5 h-5"/>:'Mark Reviewed'}
        </button>
      </div>
    </motion.div>
  );
};

export default AdminPanel;